import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BotaoImportacaoIW, InfoCadastroIW } from './iw/CaptacaoIW';
import { ArquivoLink } from './arquivos';
import { useSearchParams } from 'react-router-dom';
import { db, auth, getCollectionName } from './firebase';
import { supabase } from './supabase';
import WeeklyCalendarPicker from './components/WeeklyCalendarPicker';
import {
  speak,
  speakNovoPaciente,
  speakStatusChange,
  speakStatusChangeSLA,
  speakStatusChangeSLAVencido,
  speakVisitaAgendada,
  speakAvaliacaoConfirmada,
  speakSLAAlert,
  speakSLAVencido,
  speakImplantado,
} from './utils/speechUtils';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc, getDocs,
  query, orderBy, serverTimestamp, arrayUnion, where, resumo
} from './firestore-compat';
import {
  addDays, addWeeks, addMonths, subMonths, subWeeks, isBefore, isEqual, format,
  startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, parseISO
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ── Status flow ───────────────────────────────────────────────────────────────
// 2026-10-05: etapas Sessão Clínica/Relatório/Orçamento/Ag. Aprovação/Envio foram
// removidas — na prática eram preenchidas todas no mesmo minuto (a Direção não
// acessa o sistema pra aprovar/negar de verdade). Os dados que elas coletavam
// continuam existindo, só que juntos num popup único na transição Avaliação
// Concluída → Aguardando Retorno da Operadora (ver TransitionModal, case
// 'Aguardando Retorno da Operadora').
const STATUSES = [
  { key: 'Recebido',                        short: 'Recebido',       icon: '📥', color: '#6366f1' },
  { key: 'Aguardando Avaliação',            short: 'Ag. Avaliação',  icon: '📅', color: '#84cc16' },
  { key: 'Em Avaliação',                    short: 'Avaliação',      icon: '🔍', color: '#0ea5e9' },
  { key: 'Avaliação Concluída',             short: 'Aval. Concluída',icon: '✔️',  color: '#10b981' },
  { key: 'Aguardando Retorno da Operadora', short: 'Ag. Retorno',    icon: '🕐', color: '#64748b' },
  { key: 'Abordagem à Família',             short: 'Abord. Família', icon: '👨‍👩‍👧', color: '#f43f5e' },
  { key: 'Em Implantação',                  short: 'Em Implantação', icon: '🔧', color: '#14b8a6' },
  { key: 'Implantado',                      short: 'Implantado',     icon: '✅', color: '#22c55e' },
];

// ── Profissionais por tipo de paciente ────────────────────────────────────────
const PROF_BY_TYPE = {
  'Homebaby':                ['Médico','Enfermeiro'],
  'Homebaby VM':             ['Médico','Enfermeiro','Fisioterapeuta'],
  'Homebaby Petrobras':      ['Médico','Enfermeiro','Assistente Social'],
  'Homebaby Petrobras VM':   ['Médico','Enfermeiro','Fisioterapeuta','Assistente Social'],
  'Vitalcare':              ['Enfermeiro'],
  'Vitalcare VM':           ['Enfermeiro','Fisioterapeuta'],
  'Vitalcare Petrobras':    ['Enfermeiro','Assistente Social'],
  'Vitalcare Petrobras VM': ['Enfermeiro','Fisioterapeuta','Assistente Social'],
};

// Normaliza nomes legados do banco para o nome atual
const nf = v => v === 'Homesenior' ? 'Vitalcare' : (v || '');

const FUNCOES = ['Médico','Enfermeiro','Fisioterapeuta','Assistente Social'];
const DIAS_SEMANA = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
const CONVENIOS_DEFAULT = ['ASSIM SAÚDE','BRADESCO SAÚDE','CASSI','GEAP','GOLDEN CROSS','HAPVIDA','NOTREDAME','OMINT','PETROBRAS','PREVENT SENIOR','SULAMERICA','UNIMED','UNIMED LESTE FLUMINE','PARTICULAR','OUTRO'];

// ── PAD (Plano de Atenção Domiciliar) ─────────────────────────────────────────
const CAP_PAD_DISCIPLINES = [
  { key: 'fisioterapia',        label: 'Fisioterapia',             short: 'Fisio' },
  { key: 'fonoaudiologia',      label: 'Fonoaudiologia',           short: 'Fono' },
  { key: 'nutricao',            label: 'Nutrição',                 short: 'Nutri' },
  { key: 'tecEnfermagem',       label: 'Técnico de Enfermagem',    short: 'Téc. Enf.' },
  { key: 'enfermeiro',          label: 'Enfermeiro',               short: 'Enfermeiro' },
  { key: 'medico',              label: 'Médico',                   short: 'Médico' },
  { key: 'medicoEspecialista',  label: 'Médico Especialista',      short: 'Méd. Esp.' },
  { key: 'psicologo',           label: 'Psicólogo',                short: 'Psicólogo' },
  { key: 'terapeutaOcupacional',label: 'Terapeuta Ocupacional',    short: 'TO' },
];

const CAP_PAD_SUBSPECIALTIES = {
  fisioterapia: [
    'Fisioterapeuta','Fisioterapeuta Bobath','Fisioterapeuta Cuevas Medke',
    'Fisioterapeuta Método AME','Fisioterapeuta Método TR3','Fisioterapeuta Neurológica',
    'Fisioterapeuta Pediasuit','Fisioterapeuta RPG','Fisioterapeuta RTA',
    'Fisioterapeuta Urologia','Fisioterapia Motora','Fisioterapia Respiratória',
  ],
  fonoaudiologia: [
    'Fonoaudiologo(a)','Fonoaudiologo(a) Bobath','Fonoaudiologo(a) Laserterapia',
  ],
  psicologo: [
    'Psicologo(a)','Psicomotricidade','Psicopedagoga','Psicoterapia',
  ],
  terapeutaOcupacional: [
    'Terapeuta Ocupacional','Terapia Ocup. Integra Sensorial','Terapia Ocupacional Bobath',
  ],
  medicoEspecialista: [
    'Neurologista','Cardiologista','Pneumologista',
  ],
};

const buildPadInit = (dadosSalvos) => {
  const init = {};
  CAP_PAD_DISCIPLINES.forEach(d => {
    const saved = dadosSalvos?.[d.key];
    const subs = CAP_PAD_SUBSPECIALTIES[d.key];
    if (subs) {
      const subSaved = saved?.subspecialidades || {};
      const subspecialidades = {};
      subs.forEach(s => { subspecialidades[s] = subSaved[s] || ''; });
      init[d.key] = { enabled: saved ? !!saved.enabled : false, subspecialidades };
    } else {
      init[d.key] = saved ? { enabled: !!saved.enabled, frequencia: saved.frequencia || '' } : { enabled: false, frequencia: '' };
    }
  });
  return init;
};

const CAP_FREQUENCIA_OPTIONS = [
  'Anual','Avulsa','Bimestral','Diária','Dias alternados','Mensal','Quinzenal',
  'Trimestral','Semestral','1x semana','2x dia','2x semana','3x dia','3x semana',
  '3x semana (-SD)','4x semana','5x semana','6x semana',
];

// Opções do seletor + o valor que o paciente já tem, se ele saiu da lista (ex.: "Aux. Enfermagem…",
// retiradas em 21/10) — assim a ficha antiga continua mostrando o que foi escolhido.
const opcoesComAtual = (opcoes, atual) => (atual && atual !== 'N/A' && !opcoes.includes(atual) ? [atual, ...opcoes] : opcoes);

const CURINGA_DISPONIBILIDADE = [0,1,2,3,4,5,6].map(dia => ({ dia, horaInicio:'08:00', horaFim:'18:00' }));

const CAP_AREA_SUBITEMS = {
  'Exames':              ['Hemograma','Bioquímica','Urocultura','Hemocultura','Raio-X Tórax','ECG','Gasometria','Glicemia'],
  'Remoções':            ['Alta Médica Assinada','Laudo de Remoção','Ambulância Solicitada','Acompanhante Confirmado','Relatório de Transferência'],
  'Mobiliário':          ['Cama Hospitalar','Colchão Piramidal','Mesa de Refeição','Poltrona Reclinável','Grade Lateral','Suporte de Soro','Cadeira de Rodas'],
  'Gases Medicinais':    ['Cilindro de O₂','Regulador de Pressão','Máscara de O₂','Cânula Nasal','Umidificador','Oxímetro de Pulso'],
  'Equipamentos':        ['Bomba de Infusão','Monitor Multiparamétrico','Aspirador','Concentrador de O₂','CPAP/BIPAP','Nebulizador','Balança'],
  'Prontuário':          ['Pasta Fixa','Prontuário Rotativo','Prescrição de Enfermagem','Prescrição Médica','Lista de Verificação','Folha de Evolução','Registro de Sinais Vitais'],
  'Farmácia/Suprimentos':['Medicamentos','Material de Curativo','Fraldas','Dieta Enteral','Sondas/Cateteres','Luvas e EPIs'],
  'Logística':           ['Equipe Alocada','Transporte Confirmado','Kit de Alta Preparado','Família Orientada'],
};

const buscarCepEndereco = async (cep) => {
  const c = cep.replace(/\D/g, '');
  if (c.length !== 8) return null;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
    const d = await r.json();
    if (d.erro) return null;
    return [d.logradouro, d.bairro, d.localidade + ' - ' + d.uf].filter(Boolean).join(', ');
  } catch { return null; }
};

const CAP_IMPLANTACAO_AREAS = [
  'Exames','Remoções','Mobiliário','Gases Medicinais',
  'Equipamentos','Prontuário','Farmácia/Suprimentos','Logística',
];

// ── SLA config (carregado do Firestore, atualizável em Configurações → Captação) ─
let _slaConfig = { sla_padrao: 24, sla_vm: 48 };
export function setSlaConfig(cfg) { _slaConfig = { ..._slaConfig, ...cfg }; }

// ── Helpers ───────────────────────────────────────────────────────────────────
const getSLAHours = (p) => p.vm ? _slaConfig.sla_vm : _slaConfig.sla_padrao;

const getTipoKey = (p) => {
  const base  = p.filial || 'Homebaby';
  const petro = p.convenioEspecial === 'Petrobras' ? ' Petrobras' : '';
  const vm    = p.vm ? ' VM' : '';
  return `${base}${petro}${vm}`;
};

const getSLAInfo = (dataReferencia, slaHours) => {
  const _empty = { pct:0, status:'ok', label:'—', remainingLabel:'', tempoVoz:'', expired:false, remaining:slaHours, color:'#22c55e', bg:'#f0fdf4', border:'#bbf7d0' };
  if (!dataReferencia) return _empty;
  const ts = typeof dataReferencia.toDate === 'function' ? dataReferencia.toDate() : new Date(dataReferencia);
  if (isNaN(ts.getTime())) return _empty;
  const elapsed = (Date.now() - ts.getTime()) / 3600000;
  // Data no futuro = campo inválido (erro de cadastro ou migração)
  if (elapsed < -1) return { ..._empty, label:'⚠️ Data inválida', color:'#f59e0b', bg:'#fefce8', border:'#fde68a', status:'warning' };
  const remaining = slaHours - elapsed;
  const expired = elapsed >= slaHours;
  const pct = (elapsed / slaHours) * 100;
  const h = Math.floor(elapsed), m = Math.floor((elapsed - h) * 60);
  const label = elapsed >= 24 ? `${Math.floor(elapsed/24)}d ${h%24}h` : `${h}h ${m}m`;
  const rh = Math.floor(Math.abs(remaining)), rm = Math.floor((Math.abs(remaining) - rh) * 60);
  const remainingLabel = expired
    ? `Vencido há ${rh}h${rm > 0 ? ` ${rm}m` : ''}`
    : `Faltam ${rh}h${rm > 0 ? ` ${rm}m` : ''}`;
  // tempoVoz: versão por extenso para leitura em voz alta
  const partes = [];
  if (rh > 0) partes.push(`${rh} hora${rh !== 1 ? 's' : ''}`);
  if (rm > 0) partes.push(`${rm} minuto${rm !== 1 ? 's' : ''}`);
  const tempoVoz = partes.join(' e ') || 'menos de um minuto';
  // ok = < 75% do tempo; warning = >= 75% mas não vencido; critical = vencido
  const status = expired ? 'critical' : pct >= 75 ? 'warning' : 'ok';
  return { pct: Math.min(100, pct), elapsed, remaining, expired, label, remainingLabel, tempoVoz, status,
    color:  status==='ok'?'#22c55e':status==='warning'?'#f59e0b':'#ef4444',
    bg:     status==='ok'?'#f0fdf4':status==='warning'?'#fefce8':'#fff1f2',
    border: status==='ok'?'#bbf7d0':status==='warning'?'#fde68a':'#fecaca' };
};

const statusIdx  = (key) => STATUSES.findIndex(s => s.key === key);
const nextStatus = (key) => { const i = statusIdx(key); return i >= 0 && i < STATUSES.length-1 ? STATUSES[i+1].key : null; };
const prevStatus = (key) => { const i = statusIdx(key); return i > 0 ? STATUSES[i-1].key : null; };

// SLA só se aplica até (exclusive) "Aguardando Retorno da Operadora"
const SLA_CUTOFF_IDX = statusIdx('Aguardando Retorno da Operadora');
const isSLAActive = (paciente) => statusIdx(paciente.status) < SLA_CUTOFF_IDX;

const fmtTs = (ts) => {
  if (!ts) return '—';
  try { const d = typeof ts.toDate==='function'?ts.toDate():new Date(ts); return d.toLocaleDateString('pt-BR')+' '+d.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}); }
  catch { return '—'; }
};
const uid = () => Math.random().toString(36).slice(2,10);

const waLink = (contato, nomeProf, nomePaciente, data, hora, hospital) => {
  if (!contato) return null;
  const numero = contato.replace(/\D/g,'');
  const full = numero.startsWith('55') ? numero : `55${numero}`;
  const msg = encodeURIComponent(`Olá ${nomeProf}! Você foi agendada para avaliação do paciente *${nomePaciente}* em *${data}* às *${hora}* no hospital *${hospital}*. Por favor confirme o recebimento. — Quality System`);
  return `https://wa.me/${full}?text=${msg}`;
};

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = { border:'#e2e8f0', bg:'#f8fafc', white:'#fff', text:'#1e293b', muted:'#64748b', primary:'#6366f1' };
const inp = { border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', fontSize:'0.85rem', width:'100%', boxSizing:'border-box', outline:'none', color:C.text, background:C.white };
const lbl = { display:'block', fontSize:'0.72rem', fontWeight:700, color:C.muted, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.05em' };
const btnS = (bg, col='#fff', extra={}) => ({ background:bg, color:col, border:'none', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontWeight:700, fontSize:'0.84rem', ...extra });
const toggleBtn = (active, activeColor, label, onClick) => (
  <button onClick={onClick} type="button" style={{ flex:1, padding:'8px', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:'0.84rem', border:`2px solid ${active?activeColor:C.border}`, background:active?`${activeColor}15`:C.white, color:active?activeColor:C.muted }}>
    {label}
  </button>
);

// ── Pequenos badges ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = STATUSES.find(x => x.key === status); if (!s) return null;
  return <span style={{ background:`${s.color}18`, color:s.color, border:`1px solid ${s.color}40`, fontSize:'0.72rem', fontWeight:700, padding:'2px 10px', borderRadius:20, whiteSpace:'nowrap' }}>{s.icon} {s.short}</span>;
}
function TipoBadge({ paciente }) {
  const parts = [];
  if (paciente.filial)  parts.push({ label:nf(paciente.filial), color:paciente.filial==='Homebaby'?'#6366f1':'#0ea5e9' });
  if (paciente.tipoImplantacao === 'Reimplantação') parts.push({ label:'Reimplant.', color:'#be185d' });
  if (paciente.vm)      parts.push({ label:'VM', color:'#ef4444' });
  if (paciente.convenioEspecial==='Petrobras') parts.push({ label:'Petrobras', color:'#f59e0b' });
  if (paciente.classificacao) parts.push({ label:paciente.classificacao, color:'#3b82f6' });
  if (paciente.categoria) parts.push({ label:paciente.categoria, color:'#10b981' });
  return <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>{parts.map(p=><span key={p.label} style={{ background:`${p.color}18`, color:p.color, fontSize:'0.68rem', fontWeight:700, padding:'1px 7px', borderRadius:20, border:`1px solid ${p.color}30` }}>{p.label}</span>)}</div>;
}
function SLABadge({ paciente }) {
  if (!isSLAActive(paciente)) return null;
  const sla = getSLAInfo(paciente.dataSolicitacao || paciente.dataAbertura, getSLAHours(paciente));
  const displayLabel = (sla.expired || sla.status === 'warning') ? sla.remainingLabel : sla.label;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <span style={{ fontSize:'0.7rem' }}>{sla.status==='ok'?'🟢':sla.status==='warning'?'🟡':'🔴'}</span>
      <div style={{ flex:1, minWidth:50 }}>
        <div style={{ height:4, borderRadius:2, background:'#e2e8f0' }}>
          <div style={{ height:'100%', width:`${sla.pct}%`, borderRadius:2, background:sla.color }} />
        </div>
        <span style={{ fontSize:'0.65rem', color:sla.color, fontWeight:600 }}>{displayLabel} / {getSLAHours(paciente)}h</span>
      </div>
    </div>
  );
}

// ── Modal base ─────────────────────────────────────────────────────────────────
function Modal({ children, maxWidth=560, onClose }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={e=>e.target===e.currentTarget&&onClose&&onClose()}>
      <div style={{ background:C.white, borderRadius:16, width:'100%', maxWidth, maxHeight:'92vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,0.22)', display:'flex', flexDirection:'column' }}>
        {children}
      </div>
    </div>
  );
}
function ModalHeader({ title, subtitle, icon, onClose, color }) {
  return (
    <div style={{ padding:'18px 22px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'flex-start', background:color?`${color}08`:'transparent', position:'sticky', top:0, zIndex:2, borderRadius:'16px 16px 0 0' }}>
      <div>
        <h3 style={{ margin:0, fontSize:'1rem', fontWeight:800, color:color||C.text }}>{icon} {title}</h3>
        {subtitle && <p style={{ margin:0, fontSize:'0.78rem', color:C.muted, marginTop:2 }}>{subtitle}</p>}
      </div>
      {onClose && <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:C.muted, lineHeight:1 }}>✕</button>}
    </div>
  );
}

// ── Config Modal (Convênios + Profissionais) ──────────────────────────────────
function ConfigModal({ onClose, convenios, profissionais, onSaveConvenios, onSaveProfissionais }) {
  const [tab, setTab]                 = useState('convenios');
  const [convList, setConvList]       = useState([...convenios]);
  const [novoConv, setNovoConv]       = useState('');
  const [profList, setProfList]       = useState(profissionais.map(p=>({...p, disponibilidade:p.disponibilidade||[]})));
  const [editProf, setEditProf]       = useState(null); // { idx, data }
  const [saving, setSaving]           = useState(false);

  const addConv = () => {
    if (!novoConv.trim()) return;
    const n = novoConv.trim().toUpperCase();
    if (!convList.includes(n)) setConvList(p=>[...p, n].sort());
    setNovoConv('');
  };

  const saveAll = async () => {
    setSaving(true);
    await onSaveConvenios(convList);
    await onSaveProfissionais(profList);
    setSaving(false);
    onClose();
  };

  const emptyProf = () => ({ id:uid(), nome:'', funcao:'Médico', contato:'', email:'', status:'Ativo', disponibilidade:[] });
  const startNewProf = () => setEditProf({ idx:-1, data:emptyProf() });

  const saveProf = (data) => {
    if (editProf.idx === -1) setProfList(p=>[...p, data]);
    else setProfList(p=>p.map((x,i)=>i===editProf.idx?data:x));
    setEditProf(null);
  };

  const delProf = (idx) => { if(window.confirm('Remover profissional?')) setProfList(p=>p.filter((_,i)=>i!==idx)); };

  const toggleDia = (data, dia) => {
    const exists = data.disponibilidade.find(d=>d.dia===dia);
    const disp = exists ? data.disponibilidade.filter(d=>d.dia!==dia) : [...data.disponibilidade, { dia, horaInicio:'08:00', horaFim:'17:00' }];
    return { ...data, disponibilidade:disp };
  };

  return (
    <Modal maxWidth={700} onClose={onClose}>
      <ModalHeader title="Configurações de Captação" icon="⚙️" onClose={onClose} />

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, padding:'0 22px' }}>
        {[{ k:'convenios', l:'🏥 Convênios' }, { k:'profissionais', l:'👥 Profissionais' }].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)}
            style={{ border:'none', background:'none', padding:'10px 18px', cursor:'pointer', fontWeight:tab===t.k?700:500, fontSize:'0.85rem', color:tab===t.k?C.primary:C.muted, borderBottom:tab===t.k?`2px solid ${C.primary}`:'2px solid transparent', marginBottom:-1 }}>
            {t.l}
          </button>
        ))}
      </div>

      <div style={{ padding:'20px 22px', flex:1 }}>
        {/* ── Convênios ── */}
        {tab==='convenios' && (
          <>
            <div style={{ display:'flex', gap:8, marginBottom:16 }}>
              <input style={{ ...inp, flex:1 }} value={novoConv} onChange={e=>setNovoConv(e.target.value)} placeholder="Nome do convênio..." onKeyDown={e=>e.key==='Enter'&&addConv()} />
              <button onClick={addConv} style={btnS(C.primary)}>+ Adicionar</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, maxHeight:340, overflowY:'auto' }}>
              {convList.map((c,i)=>(
                <div key={c} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}` }}>
                  <span style={{ fontSize:'0.84rem', color:C.text }}>{c}</span>
                  <button onClick={()=>setConvList(p=>p.filter((_,idx)=>idx!==i))} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', fontSize:'0.9rem', lineHeight:1 }}>✕</button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Profissionais ── */}
        {tab==='profissionais' && !editProf && (
          <>
            <button onClick={startNewProf} style={{ ...btnS(C.primary), marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>+ Novo Profissional</button>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {profList.length===0 && <p style={{ color:C.muted, textAlign:'center', padding:'24px 0' }}>Nenhum profissional cadastrado</p>}
              {profList.map((p,i)=>(
                <div key={p.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:C.bg, borderRadius:10, border:`1px solid ${C.border}` }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background:`${C.primary}20`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'0.85rem', color:C.primary, flexShrink:0 }}>
                    {p.nome.split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase()||'?'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:'0.88rem', color:C.text }}>{p.nome}</div>
                    <div style={{ fontSize:'0.72rem', color:C.muted }}>{p.funcao} · {p.contato||'sem contato'} · {p.disponibilidade?.length||0} dia(s) disponível</div>
                  </div>
                  <span style={{ background:p.status==='Ativo'?'#f0fdf4':'#f1f5f9', color:p.status==='Ativo'?'#15803d':C.muted, fontSize:'0.7rem', fontWeight:700, padding:'2px 8px', borderRadius:20 }}>{p.status}</span>
                  <button onClick={()=>setEditProf({idx:i,data:{...p}})} style={{ background:'#f1f5f9', border:'none', borderRadius:6, padding:'5px 10px', cursor:'pointer', fontSize:'0.75rem', fontWeight:600, color:C.muted }}>✏️ Editar</button>
                  <button onClick={()=>delProf(i)} style={{ background:'#fee2e2', border:'none', borderRadius:6, padding:'5px 10px', cursor:'pointer', fontSize:'0.75rem', fontWeight:600, color:'#ef4444' }}>🗑️</button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Form de profissional ── */}
        {tab==='profissionais' && editProf && (
          <ProfForm data={editProf.data} isNew={editProf.idx===-1} onSave={saveProf} onCancel={()=>setEditProf(null)} />
        )}
      </div>

      {!editProf && (
        <div style={{ padding:'14px 22px', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'flex-end', gap:8 }}>
          <button onClick={onClose} style={{ ...btnS(C.white,C.muted), border:`1px solid ${C.border}` }}>Cancelar</button>
          <button onClick={saveAll} disabled={saving} style={{ ...btnS(C.primary), opacity:saving?0.7:1 }}>{saving?'⏳ Salvando...':'💾 Salvar Configurações'}</button>
        </div>
      )}
    </Modal>
  );
}

function ProfForm({ data, isNew, onSave, onCancel }) {
  const [form, setForm] = useState({ ...data });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const toggleDia = (dia) => {
    const exists = form.disponibilidade.find(d=>d.dia===dia);
    const disp = exists ? form.disponibilidade.filter(d=>d.dia!==dia) : [...form.disponibilidade, { dia, horaInicio:'08:00', horaFim:'17:00' }];
    set('disponibilidade', disp);
  };
  const updateHorario = (dia, campo, valor) => {
    set('disponibilidade', form.disponibilidade.map(d=>d.dia===dia?{...d,[campo]:valor}:d));
  };

  return (
    <div>
      <div style={{ fontWeight:700, fontSize:'0.95rem', color:C.text, marginBottom:16 }}>{isNew?'Novo Profissional':'Editar Profissional'}</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
        <div style={{ gridColumn:'1/-1' }}>
          <label style={lbl}>Nome Completo *</label>
          <input style={inp} value={form.nome} onChange={e=>set('nome',e.target.value)} placeholder="Nome do profissional" />
        </div>
        <div>
          <label style={lbl}>Função *</label>
          <select style={inp} value={form.funcao} onChange={e=>set('funcao',e.target.value)}>
            {FUNCOES.map(f=><option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>WhatsApp / Contato</label>
          <input style={inp} value={form.contato} onChange={e=>set('contato',e.target.value)} placeholder="(21) 99999-9999" />
        </div>
        <div style={{ gridColumn:'1/-1' }}>
          <label style={lbl}>E-mail de acesso ao Quality (para agenda)</label>
          <input style={inp} type="email" value={form.email||''} onChange={e=>set('email',e.target.value)} placeholder="nome@homebaby.com.br" />
          <span style={{ fontSize:'0.72rem', color:C.muted }}>Usado para que a profissional veja seus agendamentos em /agenda-captacao</span>
        </div>
        <div>
          <label style={lbl}>Status</label>
          <div style={{ display:'flex', gap:8 }}>
            {['Ativo','Inativo'].map(s=>toggleBtn(form.status===s,s==='Ativo'?'#22c55e':'#ef4444',s,()=>set('status',s)))}
          </div>
        </div>
      </div>

      <div style={{ background:C.bg, borderRadius:10, padding:14, border:`1px solid ${C.border}`, marginBottom:14 }}>
        <label style={{ ...lbl, marginBottom:10 }}>📅 Disponibilidade Semanal</label>
        <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
          {DIAS_SEMANA.map((d,i)=>{
            const ativo = form.disponibilidade.find(x=>x.dia===i);
            return (
              <button key={d} onClick={()=>toggleDia(i)} type="button"
                style={{ padding:'5px 11px', borderRadius:20, border:`2px solid ${ativo?C.primary:C.border}`, background:ativo?'#eef2ff':C.white, color:ativo?C.primary:C.muted, cursor:'pointer', fontWeight:700, fontSize:'0.78rem' }}>
                {d}
              </button>
            );
          })}
        </div>
        {form.disponibilidade.map(d=>(
          <div key={d.dia} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
            <span style={{ fontSize:'0.78rem', fontWeight:700, color:C.primary, minWidth:30 }}>{DIAS_SEMANA[d.dia]}</span>
            <input type="time" value={d.horaInicio} onChange={e=>updateHorario(d.dia,'horaInicio',e.target.value)} style={{ ...inp, width:'auto', flex:1 }} />
            <span style={{ color:C.muted }}>—</span>
            <input type="time" value={d.horaFim}    onChange={e=>updateHorario(d.dia,'horaFim',e.target.value)}    style={{ ...inp, width:'auto', flex:1 }} />
          </div>
        ))}
        {form.disponibilidade.length===0 && <p style={{ fontSize:'0.78rem', color:C.muted, margin:0 }}>Clique nos dias para definir a disponibilidade</p>}
      </div>

      <div style={{ display:'flex', gap:8 }}>
        <button onClick={onCancel} style={{ ...btnS(C.white,C.muted), border:`1px solid ${C.border}` }}>← Voltar</button>
        <button onClick={()=>onSave(form)} disabled={!form.nome.trim()} style={{ ...btnS(C.primary), opacity:form.nome.trim()?1:0.5 }}>
          {isNew?'✅ Adicionar':'💾 Salvar'}
        </button>
      </div>
    </div>
  );
}

// ── Novo Paciente Modal ───────────────────────────────────────────────────────
function NovoPacienteModal({ onClose, onSave, convenios, initialData, onUpdate }) {
  const isEdit = !!initialData;
  const [form, setForm] = useState(initialData ? {
    nome: initialData.nome||'', convenio: initialData.convenio||'', liminar: initialData.liminar||false,
    hospital: initialData.hospital||'', regiaoHospital: initialData.regiaoHospital||'',
    dataSolicitacao: initialData.dataSolicitacao||'', filial: initialData.filial||'Homebaby',
    vm: initialData.vm||false, convenioEspecial: initialData.convenioEspecial||'Nenhum',
    tipoImplantacao: initialData.tipoImplantacao||'Novo', observacoes: initialData.observacoes||'',
    dataNascimento: initialData.dataNascimento||'', cep: initialData.cep||'',
    numIW: initialData.numIW||'',
    classificacao: initialData.classificacao||'',
    categoria: initialData.categoria||''
  } : { nome:'', convenio:'', liminar:false, hospital:'', regiaoHospital:'', dataSolicitacao:'', filial:'Homebaby', vm:false, convenioEspecial:'Nenhum', tipoImplantacao:'Novo', observacoes:'', dataNascimento:'', cep:'', numIW:'', classificacao:'', categoria:'' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const buscarCep = async (cep) => {
    const c = cep.replace(/\D/g, '');
    if (c.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
      const d = await r.json();
      if (!d.erro) set('regiaoHospital', `${d.logradouro}, ${d.bairro}, ${d.localidade} - ${d.uf}`);
    } catch {}
  };

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = true;
    if (!form.convenio) e.convenio = true;
    if (!form.hospital.trim()) e.hospital = true;
    if (!form.dataSolicitacao) e.dataSolicitacao = true;
    if (!form.numIW.trim()) e.numIW = true;
    setErrors(e);
    return Object.keys(e).length===0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    if (isEdit) {
      await onUpdate(form);
    } else {
      await onSave(form);
    }
    setSaving(false);
  };

  const fi = (k) => ({ ...inp, borderColor:errors[k]?'#ef4444':C.border });

  return (
    <Modal maxWidth={640} onClose={onClose}>
      <ModalHeader title={isEdit ? 'Editar Paciente' : 'Novo Paciente'} subtitle={isEdit ? 'Atualize os dados do paciente' : 'Preencha os dados para iniciar a captação'} icon={isEdit ? '✏️' : '📥'} onClose={onClose} />

      <div style={{ padding:'20px 22px', flex:1 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={lbl}>Nome do Paciente *</label>
            <input style={fi('nome')} value={form.nome} onChange={e=>set('nome',e.target.value)} placeholder="Nome completo" autoFocus />
            {errors.nome && <span style={{ fontSize:'0.72rem', color:'#ef4444' }}>Obrigatório</span>}
          </div>

          <div>
            <label style={lbl}>Nº IW (Nº Atendimento) *</label>
            <input
              style={fi('numIW')}
              value={form.numIW}
              onChange={e=>set('numIW',e.target.value)}
              placeholder="Número de atendimento no sistema IW..."
            />
            {errors.numIW && <span style={{ fontSize:'0.72rem', color:'#ef4444' }}>Obrigatório</span>}
          </div>

          <div>
            <label style={lbl}>Convênio *</label>
            <select style={fi('convenio')} value={form.convenio} onChange={e=>set('convenio',e.target.value)}>
              <option value="">Selecione...</option>
              {convenios.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            {errors.convenio && <span style={{ fontSize:'0.72rem', color:'#ef4444' }}>Obrigatório</span>}
          </div>

          <div>
            <label style={lbl}>Hospital *</label>
            <input style={fi('hospital')} value={form.hospital} onChange={e=>set('hospital',e.target.value)} placeholder="Nome do hospital..." />
            {errors.hospital && <span style={{ fontSize:'0.72rem', color:'#ef4444' }}>Obrigatório</span>}
          </div>

          <div>
            <label style={lbl}>Data de Nascimento</label>
            <input type="date" style={inp} value={form.dataNascimento} onChange={e=>set('dataNascimento',e.target.value)} />
          </div>

          <div>
            <label style={lbl}>CEP</label>
            <input style={inp} value={form.cep} onChange={e=>{ set('cep',e.target.value); buscarCep(e.target.value); }} placeholder="00000-000" maxLength={9} />
          </div>

          <div style={{ gridColumn:'1/-1' }}>
            <label style={lbl}>Endereço / Localização</label>
            <input style={inp} value={form.regiaoHospital} onChange={e=>set('regiaoHospital',e.target.value)} placeholder="Preenchido automaticamente pelo CEP ou manualmente..." />
          </div>

          <div>
            <label style={lbl}>Data/Hora da Solicitação *</label>
            <input type="datetime-local" style={fi('dataSolicitacao')} value={form.dataSolicitacao} onChange={e=>set('dataSolicitacao',e.target.value)} />
            {errors.dataSolicitacao && <span style={{ fontSize:'0.72rem', color:'#ef4444' }}>Obrigatório</span>}
          </div>

          <div>
            <label style={lbl}>Filial *</label>
            <div style={{ display:'flex', gap:8 }}>
              {toggleBtn(form.filial==='Homebaby', '#6366f1', '🏠 Homebaby',  ()=>set('filial','Homebaby'))}
              {toggleBtn(form.filial==='Vitalcare','#0ea5e9', '🏡 Vitalcare',()=>set('filial','Vitalcare'))}
            </div>
          </div>

          <div>
            <label style={lbl}>Tipo de Implantação</label>
            <div style={{ display:'flex', gap:8 }}>
              {toggleBtn(form.tipoImplantacao==='Novo','#10b981','Novo',()=>set('tipoImplantacao','Novo'))}
              {toggleBtn(form.tipoImplantacao==='Reimplantação','#10b981','Reimplantação',()=>set('tipoImplantacao','Reimplantação'))}
            </div>
          </div>

          <div>
            <label style={lbl}>VM (Ventilação Mecânica)</label>
            <div style={{ display:'flex', gap:8 }}>
              {toggleBtn(form.vm===true,'#ef4444','⚠️ Sim',()=>set('vm',true))}
              {toggleBtn(form.vm===false,'#22c55e','✓ Não',()=>set('vm',false))}
            </div>
          </div>

          <div>
            <label style={lbl}>Convênio Especial</label>
            <div style={{ display:'flex', gap:8 }}>
              {toggleBtn(form.convenioEspecial==='Nenhum','#94a3b8','— Nenhum',()=>set('convenioEspecial','Nenhum'))}
              {toggleBtn(form.convenioEspecial==='Petrobras','#f59e0b','🏭 Petrobras',()=>set('convenioEspecial','Petrobras'))}
            </div>
          </div>

          <div>
            <label style={lbl}>Possui Liminar?</label>
            <div style={{ display:'flex', gap:8 }}>
              {toggleBtn(form.liminar===true, C.primary,'Sim',()=>set('liminar',true))}
              {toggleBtn(form.liminar===false,C.primary,'Não',()=>set('liminar',false))}
            </div>
          </div>

          {isEdit && (
            <>
              <div>
                <label style={lbl}>Classificação</label>
                <select style={inp} value={form.classificacao} onChange={e=>set('classificacao',e.target.value)}>
                  <option value="">— Selecione —</option>
                  {['ID', 'AD', 'Pontual'].map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={lbl}>Categoria</label>
                <select style={inp} value={form.categoria} onChange={e=>set('categoria',e.target.value)}>
                  <option value="">— Selecione —</option>
                  {[
                    'ID 6h', 'ID 12h', 'ID 24h', 'ID 24h com VM',
                    'Pontual Curativo', 'Pontual Medicamento', 'Pontual Curativo/Medicamento',
                    'Pontual Procedimento', 'Pontual Antibioticoterapia',
                    'Terapias e Curativo', 'Terapias e Medicamentos', 'Terapias/ Curativo/ Medicamento',
                    'Terapias e Procedimentos', 'Terapias e Antibioticoterapia', 'Terapias e Equipamentos',
                    'Terapias', 'Equipamentos',
                    'Enfermagem Exclusiva 6h', 'Enfermagem Exclusiva 12h', 'Enfermagem Exclusiva 24h'
                  ].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div style={{ gridColumn:'1/-1' }}>
            <label style={lbl}>Observações</label>
            <textarea style={{ ...inp, minHeight:64, resize:'vertical' }} value={form.observacoes} onChange={e=>set('observacoes',e.target.value)} placeholder="Informações adicionais..." />
          </div>

          {/* Profissionais preview */}
          <div style={{ gridColumn:'1/-1', background:C.bg, borderRadius:10, padding:'12px 16px', border:`1px solid ${C.border}` }}>
            <label style={{ ...lbl, marginBottom:8 }}>👥 Profissionais convocados (automático)</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {(PROF_BY_TYPE[getTipoKey(form)]||['Médico','Enfermeiro']).map(role=>(
                <span key={role} style={{ background:`${C.primary}15`, color:C.primary, border:`1px solid ${C.primary}30`, fontSize:'0.78rem', fontWeight:700, padding:'3px 12px', borderRadius:20 }}>{role}</span>
              ))}
            </div>
            <p style={{ margin:'6px 0 0', fontSize:'0.72rem', color:C.muted }}>⏱ SLA: {form.vm?'48h (VM)':'24h'} · Alertas: 6h / 24h / 30h</p>
          </div>
        </div>
      </div>

      <div style={{ padding:'14px 22px', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'flex-end', gap:8 }}>
        <button onClick={onClose} style={{ ...btnS(C.white,C.muted), border:`1px solid ${C.border}` }}>Cancelar</button>
        <button onClick={handleSave} disabled={saving} style={{ ...btnS(C.primary), opacity:saving?0.7:1 }}>
          {saving?'⏳ Salvando...':isEdit?'💾 Salvar Alterações':'✅ Cadastrar e Agendar →'}
        </button>
      </div>
    </Modal>
  );
}

// ── Transition Modal ──────────────────────────────────────────────────────────
function TransitionModal({ paciente, targetStatus, profissionais, agendamentos = [], dadosPADSalvo, areaSubitems = CAP_AREA_SUBITEMS, freqOptions = CAP_FREQUENCIA_OPTIONS, initialData = null, onClose, onConfirm }) {
  const modoEdicao = !!initialData;
  // Pré-popula origem da remoção com o hospital/local de avaliação do paciente
  const origemPadrao = paciente?.hospital || paciente?.regiaoHospital || '';
  const [form, setForm] = useState({ obs:'', orcamentoObs:'', justificativa:'', data:'', hora:'', classificacao: paciente?.classificacao || '', categoria: paciente?.categoria || '', profsSelecionados:[], remocaoOrigem: origemPadrao, remocaoDestino:'', cepOrigem: paciente?.cep || '', cepDestino:'', tipoRemocao:'Simples', suprimentos:false, medicamentos:false, material:false, mobiliario:false, agendamentosPorProf:{}, ...(initialData || {}) });

  // Se não há nome de hospital, resolve endereço de origem a partir do CEP cadastrado
  useEffect(() => {
    if (!paciente?.cep) return;
    buscarCepEndereco(paciente.cep).then(end => {
      if (end) setForm(f => ({ ...f, remocaoOrigem: f.remocaoOrigem || end }));
    });
  }, [paciente?.cep]); // eslint-disable-line react-hooks/exhaustive-deps
  const [saving, setSaving] = useState(false);
  const [calendarOpenFor, setCalendarOpenFor] = useState(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  // Carrega prévia existente quando abrindo "Em Implantação"
  const [previaExistente, setPreviaExistente] = useState(null);
  const [loadingPrevia, setLoadingPrevia] = useState(false);
  useEffect(() => {
    if (targetStatus !== 'Em Implantação') return;
    setLoadingPrevia(true);
    getDoc(doc(db, getCollectionName('captacao_previas'), paciente.id))
      .then(snap => {
        if (!snap.exists() || snap.data().status !== 'ativa') return;
        const pd = snap.data();
        setPreviaExistente(pd);
        // 20/10: o formulário completo aparece já preenchido com a prévia, para conferir e ajustar
        // (antes só mostrava um resumo e usava a prévia sem dar chance de mudar)
        if (initialData) return;
        setForm(p => ({
          ...p,
          remocaoOrigem: pd.remocaoOrigem || p.remocaoOrigem, remocaoDestino: pd.remocaoDestino || p.remocaoDestino,
          cepOrigem: pd.cepOrigem || p.cepOrigem, cepDestino: pd.cepDestino || p.cepDestino,
          tipoRemocao: pd.tipoRemocao || p.tipoRemocao,
          dataImplantacao: pd.dataImplantacao || p.dataImplantacao || '', horaImplantacao: pd.horaImplantacao || p.horaImplantacao || '',
        }));
        if (pd.pad) setPad(buildPadInit(pd.pad));
        if (pd.areas) setAreas(prev => {
          const out = { ...prev };
          for (const a of Object.keys(prev)) {
            const v = pd.areas[a];
            if (v === undefined) continue;
            out[a] = typeof v === 'object'
              ? { enabled: !!v.enabled, items: { ...prev[a].items, ...(v.items || {}) } }
              : { ...prev[a], enabled: !!v };
          }
          return out;
        });
      })
      .catch(() => {})
      .finally(() => setLoadingPrevia(false));
  }, [paciente.id, targetStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = STATUSES.find(x=>x.key===targetStatus);
  const isBackward = !modoEdicao && statusIdx(targetStatus) < statusIdx(paciente.status);

  const [pad, setPad] = useState(() => buildPadInit(initialData?.pad || dadosPADSalvo));
  const updatePad = (key, field, value) => setPad(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  const updateSubspe = (discKey, subName, value) => setPad(prev => ({
    ...prev,
    [discKey]: { ...prev[discKey], subspecialidades: { ...prev[discKey].subspecialidades, [subName]: value } },
  }));
  const padPreenchido = Object.values(pad).some(d => d.enabled);

  // Seleção de áreas/setores para Em Implantação (default: todos desmarcados,
  // ou os valores salvos em initialData quando reabrindo pra edição)
  const [areas, setAreas] = useState(() => {
    if (initialData?.areas) return initialData.areas;
    const init = {};
    CAP_IMPLANTACAO_AREAS.forEach(a => {
      const items = {};
      (areaSubitems[a] || []).forEach(s => { items[s] = false; });
      init[a] = { enabled: false, items };
    });
    return init;
  });
  const toggleArea = (a) => setAreas(prev => ({ ...prev, [a]: { ...prev[a], enabled: !prev[a].enabled } }));
  const toggleAreaItem = (area, item) => setAreas(prev => ({
    ...prev,
    [area]: { ...prev[area], items: { ...prev[area].items, [item]: !prev[area].items[item] } },
  }));

  const profsNecessariosRoles = PROF_BY_TYPE[getTipoKey(paciente)] || [];
  const profsDisponiveis = profissionais.filter(p => profsNecessariosRoles.includes(p.funcao) && p.status==='Ativo');

  const renderBody = () => {
    switch (targetStatus) {
      case 'Aguardando Avaliação': return (
        <>
          <p style={{ margin:'0 0 14px', color:C.muted, fontSize:'0.85rem' }}>Agende a visita de avaliação. Após confirmar, os profissionais receberão notificação via WhatsApp.</p>
          {profsDisponiveis.length > 0 ? (
            <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:14 }}>
              {profsDisponiveis.map(p=>{
                const ag = form.agendamentosPorProf[p.id] || {};
                const link = ag.data && (ag.hora||ag.horaLivre) ? waLink(p.contato, p.nome, paciente.nome, ag.data, ag.hora==='livre'?ag.horaLivre:ag.hora, paciente.hospital) : null;
                return (
                  <div key={p.id} style={{ background:C.bg, borderRadius:10, padding:14, border:`1px solid ${C.border}` }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                      <div style={{ width:34, height:34, borderRadius:'50%', background:`${C.primary}20`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'0.8rem', color:C.primary }}>
                        {p.nome.split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:'0.88rem', color:C.text }}>{p.nome}</div>
                        <div style={{ fontSize:'0.72rem', color:C.muted }}>{p.funcao}{p.contato?` · 📱 ${p.contato}`:''}</div>
                      </div>
                      {link && (
                        <a href={link} target="_blank" rel="noopener noreferrer"
                          style={{ background:'#dcfce7', color:'#15803d', border:'1px solid #bbf7d0', borderRadius:8, padding:'4px 12px', fontSize:'0.72rem', fontWeight:700, textDecoration:'none', display:'flex', alignItems:'center', gap:4 }}>
                          📲 Notificar
                        </a>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: 12, borderRadius: 8, border: `1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 2 }}>Data e Horário</div>
                        {ag.data && (ag.hora || ag.horaLivre) ? (
                          <div style={{ fontWeight: 800, color: C.primary }}>
                            {ag.data.split('-').reverse().join('/')} às {ag.hora === 'livre' ? ag.horaLivre : ag.hora}
                          </div>
                        ) : (
                          <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Não selecionado</div>
                        )}
                      </div>
                      <button 
                        onClick={() => setCalendarOpenFor(calendarOpenFor === p.id ? null : p.id)}
                        style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 700, color: C.primary, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                        📅 {calendarOpenFor === p.id ? 'Fechar Calendário' : 'Selecionar Horário'}
                      </button>
                    </div>
                    {calendarOpenFor === p.id && (
                      <Modal maxWidth={900} onClose={() => setCalendarOpenFor(null)}>
                        <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.text }}>📅 Agendar Horário</h3>
                            <p style={{ margin: 0, fontSize: '0.82rem', color: C.muted, marginTop: 2 }}>Profissional: <strong>{p.nome}</strong></p>
                          </div>
                          <button onClick={() => setCalendarOpenFor(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: C.muted, lineHeight: 1 }}>✕</button>
                        </div>
                        <div style={{ padding: 20, background: C.bg }}>
                          <WeeklyCalendarPicker 
                            disponibilidade={p.disponibilidade}
                            agendamentos={agendamentos.filter(a => a.profissionalId === p.id && a.status !== 'cancelado')}
                            selectedDate={ag.data}
                            selectedTime={ag.hora === 'livre' ? ag.horaLivre : ag.hora}
                            onSelect={(d, h) => {
                              setForm(prev => ({
                                ...prev,
                                agendamentosPorProf: {
                                  ...prev.agendamentosPorProf,
                                  [p.id]: {
                                    ...(prev.agendamentosPorProf[p.id] || {}),
                                    data: d,
                                    hora: h
                                  }
                                }
                              }));
                              setCalendarOpenFor(null);
                            }}
                          />
                        </div>
                      </Modal>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div style={{ background:'#fffbeb', borderRadius:10, padding:14, marginBottom:14, border:'1px solid #fde68a' }}>
                <p style={{ margin:'0 0 6px', fontWeight:700, color:'#92400e' }}>⚠️ Nenhum profissional cadastrado para esse tipo de paciente</p>
                <p style={{ margin:0, fontSize:'0.78rem', color:'#a16207' }}>Funções necessárias: {profsNecessariosRoles.join(', ')}</p>
                <p style={{ margin:'4px 0 0', fontSize:'0.78rem', color:'#a16207' }}>Cadastre em ⚙️ Config → Profissionais</p>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><label style={lbl}>Data da visita</label><input type="date" style={inp} value={form.data} onChange={e=>set('data',e.target.value)} /></div>
                <div><label style={lbl}>Horário</label><input type="time" style={inp} value={form.hora} onChange={e=>set('hora',e.target.value)} /></div>
              </div>
            </>
          )}
          {/* Card Curinga */}
          {(() => {
            const ag = form.agendamentosPorProf['curinga'] || {};
            return (
              <div style={{ background:'#fffbeb', borderRadius:10, padding:14, border:'2px dashed #fbbf24', marginBottom:12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ width:34, height:34, borderRadius:'50%', background:'#fef3c7', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'1rem' }}>⭐</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#92400e' }}>Profissional Curinga</div>
                    <div style={{ fontSize:'0.72rem', color:'#a16207' }}>Profissional avulso / não cadastrado</div>
                  </div>
                </div>
                <div style={{ marginBottom:10 }}>
                  <label style={{ ...lbl, color:'#92400e' }}>Nome do Profissional</label>
                  <input style={{ ...inp, borderColor:'#fbbf24' }} value={ag.nomeCustom||''} onChange={e=>setForm(prev=>({ ...prev, agendamentosPorProf:{ ...prev.agendamentosPorProf, curinga:{ ...ag, nomeCustom:e.target.value } } }))} placeholder="Digite o nome do profissional..." />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'#fef9c3', padding:12, borderRadius:8, border:'1px solid #fde68a' }}>
                  <div>
                    <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#a16207', textTransform:'uppercase', marginBottom:2 }}>Data e Horário</div>
                    {ag.data && ag.hora ? (
                      <div style={{ fontWeight:800, color:'#92400e' }}>{ag.data.split('-').reverse().join('/')} às {ag.hora}</div>
                    ) : (
                      <div style={{ color:'#94a3b8', fontSize:'0.85rem' }}>Não selecionado</div>
                    )}
                  </div>
                  <button onClick={()=>setCalendarOpenFor(calendarOpenFor==='curinga'?null:'curinga')} style={{ background:'#fff', border:'1px solid #fbbf24', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontWeight:700, color:'#92400e', fontSize:'0.8rem', display:'flex', alignItems:'center', gap:6 }}>
                    📅 {calendarOpenFor==='curinga'?'Fechar Calendário':'Selecionar Horário'}
                  </button>
                </div>
                {calendarOpenFor==='curinga' && (
                  <Modal maxWidth={900} onClose={()=>setCalendarOpenFor(null)}>
                    <div style={{ padding:'18px 22px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <h3 style={{ margin:0, fontSize:'1.1rem', fontWeight:800, color:C.text }}>📅 Agendar Horário — Curinga</h3>
                      <button onClick={()=>setCalendarOpenFor(null)} style={{ background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:C.muted }}>✕</button>
                    </div>
                    <div style={{ padding:20, background:C.bg }}>
                      <WeeklyCalendarPicker disponibilidade={CURINGA_DISPONIBILIDADE} agendamentos={[]} selectedDate={ag.data} selectedTime={ag.hora}
                        onSelect={(d,h)=>{ setForm(prev=>({ ...prev, agendamentosPorProf:{ ...prev.agendamentosPorProf, curinga:{ ...ag, data:d, hora:h } } })); setCalendarOpenFor(null); }} />
                    </div>
                  </Modal>
                )}
              </div>
            );
          })()}
          <div style={{ background:'#f0f9ff', borderRadius:8, padding:'10px 14px', border:'1px solid #bfdbfe', fontSize:'0.78rem', color:'#1d4ed8' }}>
            📱 Clique em "📲 Notificar" ao lado de cada profissional para abrir o WhatsApp com a mensagem pronta
          </div>
        </>
      );

      case 'Em Avaliação': return (
        <div style={{ textAlign:'center', padding:'8px 0' }}>
          <div style={{ fontSize:'2rem', marginBottom:10 }}>🔍</div>
          <p style={{ margin:0, fontWeight:700, color:C.text }}>Confirmar início da avaliação</p>
          <p style={{ margin:'4px 0 16px', fontSize:'0.82rem', color:C.muted }}>O profissional realizou a visita e a avaliação está em andamento.</p>
        </div>
      );

      case 'Aguardando Retorno da Operadora': return (
        <>
          <div style={{ background:'#eef2ff', border:'1px solid #c7d2fe', borderRadius:8, padding:'10px 14px', marginBottom:16 }}>
            <p style={{ margin:0, fontSize:'0.8rem', color:C.primary, fontWeight:600 }}>
              Essa etapa reúne sessão clínica, PAD, orçamento e envio à operadora num passo só. Preencha tudo antes de confirmar.
            </p>
          </div>

          {/* Sessão Clínica */}
          <p style={{ margin:'0 0 8px', fontWeight:800, fontSize:'0.85rem', color:C.text }}>🩺 Sessão Clínica</p>
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Classificação do Atendimento *</label>
            <div style={{ display:'flex', gap:8, marginTop:6 }}>
              {['ID', 'AD', 'Pontual'].map(cls => (
                toggleBtn(form.classificacao === cls, C.primary, cls, () => set('classificacao', cls))
              ))}
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Categoria do Paciente *</label>
            <select style={inp} value={form.categoria} onChange={e=>set('categoria',e.target.value)}>
              <option value="">— Selecione a categoria —</option>
              {[
                'ID 6h', 'ID 12h', 'ID 24h', 'ID 24h com VM',
                'Pontual Curativo', 'Pontual Medicamento', 'Pontual Curativo/Medicamento',
                'Pontual Procedimento', 'Pontual Antibioticoterapia',
                'Terapias e Curativo', 'Terapias e Medicamentos', 'Terapias/ Curativo/ Medicamento',
                'Terapias e Procedimentos', 'Terapias e Antibioticoterapia', 'Terapias e Equipamentos',
                'Terapias', 'Equipamentos',
                'Enfermagem Exclusiva 6h', 'Enfermagem Exclusiva 12h', 'Enfermagem Exclusiva 24h'
              ].map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lbl}>Profissionais para a Sessão</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
              {FUNCOES.map(f=>(
                <button key={f} onClick={()=>set('profsSelecionados',form.profsSelecionados.includes(f)?form.profsSelecionados.filter(x=>x!==f):[...form.profsSelecionados,f])} type="button"
                  style={{ padding:'5px 14px', borderRadius:20, border:`2px solid ${form.profsSelecionados.includes(f)?C.primary:C.border}`, background:form.profsSelecionados.includes(f)?'#eef2ff':C.white, color:form.profsSelecionados.includes(f)?C.primary:C.muted, cursor:'pointer', fontWeight:700, fontSize:'0.78rem' }}>
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
            <div><label style={lbl}>Data</label><input type="date" style={inp} value={form.data} onChange={e=>set('data',e.target.value)} /></div>
            <div><label style={lbl}>Horário</label><input type="time" style={inp} value={form.hora} onChange={e=>set('hora',e.target.value)} /></div>
          </div>

          {/* Relatório / PAD */}
          <div style={{ background:'#f0fdf4', borderRadius:10, padding:14, marginBottom:16 }}>
            <p style={{ margin:'0 0 4px', fontWeight:800, color:'#15803d', fontSize:'0.92rem' }}>📋 PAD — Plano de Atenção Domiciliar</p>
            <p style={{ margin:0, fontSize:'0.78rem', color:'#166534' }}>
              Selecione as disciplinas que farão parte do PAD deste paciente. <strong>Ao menos uma é obrigatória</strong> para avançar.
            </p>
          </div>

          {CAP_PAD_DISCIPLINES.map(disc => {
            const d = pad[disc.key];
            const subs = CAP_PAD_SUBSPECIALTIES[disc.key];
            const hasPendingSubs = d.enabled && subs && subs.some(s => !d.subspecialidades?.[s]);
            return (
              <div key={disc.key} style={{ background:C.bg, borderRadius:10, padding:'12px 14px', marginBottom:10, border:`1.5px solid ${hasPendingSubs ? '#f59e0b' : d.enabled ? '#10b981' : C.border}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom: d.enabled ? 8 : 0 }}>
                  <div onClick={() => updatePad(disc.key, 'enabled', !d.enabled)}
                    style={{ width:44, height:24, borderRadius:12, cursor:'pointer', position:'relative', flexShrink:0, background: d.enabled ? '#10b981' : '#cbd5e1', transition:'background 0.2s' }}>
                    <div style={{ width:18, height:18, borderRadius:'50%', background:'#fff', position:'absolute', top:3, left: d.enabled ? 23 : 3, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                  <span style={{ fontWeight:700, fontSize:'0.88rem', color: d.enabled ? '#065f46' : C.muted, flex:1 }}>{disc.label}</span>
                  {hasPendingSubs && <span style={{ fontSize:'0.68rem', color:'#b45309', fontWeight:700 }}>⚠ preencher tudo</span>}
                </div>
                {d.enabled && subs && (
                  <div style={{ display:'flex', flexDirection:'column', gap:5, marginTop:4, paddingLeft:8, borderLeft:'2px solid #10b98140' }}>
                    <div style={{ fontSize:'0.7rem', color:'#166534', fontWeight:600, marginBottom:2 }}>Marque cada sub-especialidade (obrigatório — use N/A se não se aplicar):</div>
                    {subs.map(sub => (
                      <div key={sub} style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:'0.77rem', color:C.text, flex:1, minWidth:0 }}>{sub}</span>
                        <select value={d.subspecialidades?.[sub] || ''} onChange={e => updateSubspe(disc.key, sub, e.target.value)}
                          style={{ ...inp, fontSize:'0.73rem', flex:'0 0 auto', width:168, borderColor: d.subspecialidades?.[sub] ? (d.subspecialidades[sub]==='N/A'?'#94a3b8':'#10b981') : '#f59e0b' }}>
                          <option value="">— Selecione —</option>
                          <option value="N/A">N/A</option>
                          {opcoesComAtual(freqOptions, d.subspecialidades?.[sub]).map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
                {d.enabled && !subs && (
                  <div>
                    <label style={{ ...lbl, marginBottom:4 }}>Frequência</label>
                    <select value={d.frequencia} onChange={e => updatePad(disc.key, 'frequencia', e.target.value)} style={{ ...inp, fontSize:'0.82rem' }}>
                      <option value="">— Selecione a frequência —</option>
                      {opcoesComAtual(freqOptions, d.frequencia).map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                )}
              </div>
            );
          })}

          {!padPreenchido && (
            <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:8, padding:'10px 14px', fontSize:'0.78rem', color:'#92400e', marginBottom:20 }}>
              ⚠️ Selecione ao menos uma disciplina para continuar.
            </div>
          )}

          {/* Orçamento */}
          <p style={{ margin:'20px 0 8px', fontWeight:800, fontSize:'0.85rem', color:C.text }}>💰 Orçamento</p>
          <div style={{ marginBottom:20 }}>
            <label style={lbl}>Detalhes do Orçamento</label>
            <textarea style={{ ...inp, minHeight:80 }} value={form.orcamentoObs} onChange={e=>set('orcamentoObs',e.target.value)} placeholder="Descreva os itens e valores..." />
          </div>

          {/* Envio para Operadora — Prévia de Implantação */}
          <p style={{ margin:'0 0 8px', fontWeight:800, fontSize:'0.85rem', color:C.text }}>📤 Envio para Operadora</p>
          <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
            <p style={{ margin:0, fontSize:'0.82rem', color:'#15803d', fontWeight:700 }}>
              📋 Prévia de Implantação — preencha as informações para envio à operadora. Os setores envolvidos terão acesso e confirmarão as datas de chegada de cada item.
            </p>
          </div>

          {/* Remoção */}
          <div style={{ border:`2px solid ${(form.remocaoOrigem||form.remocaoDestino)?C.primary:C.border}`, borderRadius:10, marginBottom:14, overflow:'hidden' }}>
            <div style={{ background:(form.remocaoOrigem||form.remocaoDestino)?'#eef2ff':C.bg, padding:'10px 14px', borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontWeight:800, fontSize:'0.9rem', color:(form.remocaoOrigem||form.remocaoDestino)?C.primary:C.muted }}>🚗 Remoção</span>
              <span style={{ fontSize:'0.72rem', color:C.muted, marginLeft:8 }}>Preencha origem e destino para incluir na prévia</span>
            </div>
            <div style={{ padding:'14px 14px 0' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:10 }}>
                <div><label style={lbl}>Local de Origem</label><input style={inp} value={form.remocaoOrigem} onChange={e=>set('remocaoOrigem',e.target.value)} placeholder="Hospital atual" /></div>
                <div><label style={lbl}>Local de Destino</label><input style={inp} value={form.remocaoDestino} onChange={e=>set('remocaoDestino',e.target.value)} placeholder="Endereço do paciente" /></div>
                <div><label style={lbl}>CEP de Origem</label><input style={inp} value={form.cepOrigem} maxLength={9} placeholder="00000-000" onChange={async e=>{ set('cepOrigem',e.target.value); const end=await buscarCepEndereco(e.target.value); if(end) set('remocaoOrigem',end); }} /></div>
                <div><label style={lbl}>CEP de Destino</label><input style={inp} value={form.cepDestino} maxLength={9} placeholder="00000-000" onChange={async e=>{ set('cepDestino',e.target.value); const end=await buscarCepEndereco(e.target.value); if(end) set('remocaoDestino',end); }} /></div>
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ ...lbl, marginBottom:6 }}>Tipo</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {toggleBtn(form.tipoRemocao==='Simples',C.primary,'Simples',()=>set('tipoRemocao','Simples'))}
                  {toggleBtn(form.tipoRemocao==='UTI com O₂',C.primary,'UTI com O₂',()=>set('tipoRemocao','UTI com O₂'))}
                  {toggleBtn(form.tipoRemocao==='Meios Próprios',C.primary,'🚗 Meios Próprios',()=>set('tipoRemocao','Meios Próprios'))}
                </div>
              </div>
            </div>
            {(form.remocaoOrigem||form.remocaoDestino) && (areaSubitems['Remoções']||[]).length>0 && (
              <div style={{ padding:'8px 14px 12px', background:'#f5f7ff', borderTop:'1px solid #c7d2fe' }}>
                <p style={{ margin:'0 0 8px', fontSize:'0.7rem', color:C.primary, fontWeight:700 }}>Itens pendentes para a remoção:</p>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:6 }}>
                  {(areaSubitems['Remoções']||[]).map(item => {
                    const checked=!!areas['Remoções']?.items?.[item];
                    return (<label key={item} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'5px 8px', borderRadius:6, background:checked?'#e0e7ff':C.white, border:`1px solid ${checked?'#a5b4fc':C.border}` }}>
                      <input type="checkbox" checked={checked} onChange={()=>toggleAreaItem('Remoções',item)} style={{ accentColor:C.primary, width:15, height:15, flexShrink:0 }} />
                      <span style={{ fontSize:'0.78rem', color:checked?C.primary:C.text, fontWeight:checked?700:400 }}>{item}</span>
                    </label>);
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Setores envolvidos */}
          <div style={{ background:C.bg, borderRadius:10, padding:14, border:`1px solid ${C.border}` }}>
            <label style={{ ...lbl, marginBottom:4 }}>Setores envolvidos nesta implantação</label>
            <p style={{ margin:'0 0 10px', fontSize:'0.72rem', color:C.muted }}>Ative o setor e marque os itens com pendência para este paciente.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {CAP_IMPLANTACAO_AREAS.filter(a=>a!=='Remoções').map(area => {
                const areaState=areas[area]||{enabled:false,items:{}};
                const subitems=areaSubitems[area]||[];
                const checkedCount=Object.values(areaState.items).filter(Boolean).length;
                return (
                  <div key={area} style={{ border:`2px solid ${areaState.enabled?C.primary:C.border}`, borderRadius:10, overflow:'hidden', transition:'all 0.15s' }}>
                    <button type="button" onClick={()=>toggleArea(area)} style={{ width:'100%', padding:'10px 14px', background:areaState.enabled?'#eef2ff':C.white, border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:10, textAlign:'left' }}>
                      <span style={{ fontSize:'1rem' }}>{areaState.enabled?'✅':'⬜'}</span>
                      <span style={{ fontWeight:700, fontSize:'0.85rem', color:areaState.enabled?C.primary:C.muted, flex:1 }}>{area}</span>
                      {areaState.enabled && checkedCount>0 && (<span style={{ background:C.primary, color:'#fff', fontSize:'0.68rem', fontWeight:800, borderRadius:20, padding:'1px 8px' }}>{checkedCount}/{subitems.length}</span>)}
                    </button>
                    {areaState.enabled && subitems.length>0 && (
                      <div style={{ padding:'8px 14px 12px', background:'#f5f7ff', borderTop:'1px solid #c7d2fe' }}>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(190px,1fr))', gap:6 }}>
                          {subitems.map(item => (
                            <label key={item} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'5px 8px', borderRadius:6, background:areaState.items[item]?'#e0e7ff':C.white, border:`1px solid ${areaState.items[item]?'#a5b4fc':C.border}` }}>
                              <input type="checkbox" checked={!!areaState.items[item]} onChange={()=>toggleAreaItem(area,item)} style={{ accentColor:C.primary, width:15, height:15, flexShrink:0 }} />
                              <span style={{ fontSize:'0.78rem', color:areaState.items[item]?C.primary:C.text, fontWeight:areaState.items[item]?700:400 }}>{item}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      );

      case 'Em Implantação': return loadingPrevia ? (
        <div style={{ textAlign:'center', padding:'24px 0', color:C.muted }}>⏳ Verificando prévia...</div>
      ) : (
        <>
          {previaExistente && (
            <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:14, marginBottom:14 }}>
              <p style={{ margin:'0 0 6px', fontWeight:800, color:'#15803d', fontSize:'0.9rem' }}>✅ Prévia encontrada</p>
              <p style={{ margin:0, fontSize:'0.82rem', color:'#166534' }}>Os campos abaixo já vieram preenchidos com o que foi informado no envio à operadora. Confira e ajuste o que mudou antes de confirmar.</p>
            </div>
          )}
          {/* 📅 Data e Hora prevista de implantação */}
          <div style={{ border:`2px solid ${form.dataImplantacao ? '#0891b2' : C.border}`, borderRadius:10, marginBottom:14, overflow:'hidden' }}>
            <div style={{ background: form.dataImplantacao ? '#ecfeff' : C.bg, padding:'10px 14px', borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontWeight:800, fontSize:'0.9rem', color: form.dataImplantacao ? '#0891b2' : C.muted }}>📅 Data e Hora Prevista da Implantação</span>
              <span style={{ fontSize:'0.72rem', color:C.muted, marginLeft:8 }}>Esta informação será encaminhada ao setor de Educação Continuada</span>
            </div>
            <div style={{ padding:'14px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={lbl}>Data prevista <span style={{ color:'#ef4444' }}>*</span></label>
                <input type="date" style={{ ...inp, borderColor: form.dataImplantacao ? '#0891b2' : C.border }} value={form.dataImplantacao||''} onChange={e=>set('dataImplantacao',e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Horário previsto</label>
                <input type="time" style={inp} value={form.horaImplantacao||''} onChange={e=>set('horaImplantacao',e.target.value)} />
              </div>
            </div>
          </div>

          {/* 🚗 Remoção — bloco único: origem, destino, tipo e itens pendentes */}
          <div style={{ border:`2px solid ${(form.remocaoOrigem||form.remocaoDestino)?C.primary:C.border}`, borderRadius:10, marginBottom:14, overflow:'hidden' }}>
            <div style={{ background:(form.remocaoOrigem||form.remocaoDestino)?'#eef2ff':C.bg, padding:'10px 14px', borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontWeight:800, fontSize:'0.9rem', color:(form.remocaoOrigem||form.remocaoDestino)?C.primary:C.muted }}>🚗 Remoção</span>
              <span style={{ fontSize:'0.72rem', color:C.muted, marginLeft:8 }}>Preencha origem e destino para gerar a ocorrência de remoção automaticamente</span>
            </div>
            <div style={{ padding:'14px 14px 0' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:10 }}>
                <div>
                  <label style={lbl}>Local de Origem</label>
                  <input style={inp} value={form.remocaoOrigem} onChange={e=>set('remocaoOrigem',e.target.value)} placeholder="Hospital atual" />
                </div>
                <div>
                  <label style={lbl}>Local de Destino</label>
                  <input style={inp} value={form.remocaoDestino} onChange={e=>set('remocaoDestino',e.target.value)} placeholder="Endereço do paciente" />
                </div>
                <div>
                  <label style={lbl}>CEP de Origem</label>
                  <input style={inp} value={form.cepOrigem} maxLength={9} placeholder="00000-000"
                    onChange={async e => { set('cepOrigem', e.target.value); const end = await buscarCepEndereco(e.target.value); if (end) set('remocaoOrigem', end); }} />
                </div>
                <div>
                  <label style={lbl}>CEP de Destino</label>
                  <input style={inp} value={form.cepDestino} maxLength={9} placeholder="00000-000"
                    onChange={async e => { set('cepDestino', e.target.value); const end = await buscarCepEndereco(e.target.value); if (end) set('remocaoDestino', end); }} />
                </div>
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ ...lbl, marginBottom:6 }}>Tipo</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {toggleBtn(form.tipoRemocao==='Simples',C.primary,'Simples',()=>set('tipoRemocao','Simples'))}
                  {toggleBtn(form.tipoRemocao==='UTI com O₂',C.primary,'UTI com O₂',()=>set('tipoRemocao','UTI com O₂'))}
                  {toggleBtn(form.tipoRemocao==='Meios Próprios',C.primary,'🚗 Meios Próprios',()=>set('tipoRemocao','Meios Próprios'))}
                </div>
              </div>
            </div>
            {/* Itens de remoção — só mostra se tiver origem ou destino */}
            {(form.remocaoOrigem || form.remocaoDestino) && (areaSubitems['Remoções'] || []).length > 0 && (
              <div style={{ padding:'8px 14px 12px', background:'#f5f7ff', borderTop:`1px solid #c7d2fe` }}>
                <p style={{ margin:'0 0 8px', fontSize:'0.7rem', color:C.primary, fontWeight:700 }}>Itens pendentes para a remoção:</p>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:6 }}>
                  {(areaSubitems['Remoções'] || []).map(item => {
                    const checked = !!areas['Remoções']?.items?.[item];
                    return (
                      <label key={item} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'5px 8px', borderRadius:6, background:checked?'#e0e7ff':C.white, border:`1px solid ${checked?'#a5b4fc':C.border}`, transition:'all 0.1s' }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleAreaItem('Remoções', item)} style={{ accentColor:C.primary, width:15, height:15, flexShrink:0 }} />
                        <span style={{ fontSize:'0.78rem', color:checked?C.primary:C.text, fontWeight:checked?700:400 }}>{item}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Demais setores — Remoções já está acima, não aparece aqui */}
          <div style={{ background:C.bg, borderRadius:10, padding:14, border:`1px solid ${C.border}`, marginBottom:14 }}>
            <label style={{ ...lbl, marginBottom:4 }}>Setores envolvidos nesta implantação</label>
            <p style={{ margin:'0 0 10px', fontSize:'0.72rem', color:C.muted }}>Ative o setor e marque os itens com pendência para este paciente.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {CAP_IMPLANTACAO_AREAS.filter(a => a !== 'Remoções').map(area => {
                const areaState = areas[area] || { enabled:false, items:{} };
                const subitems = areaSubitems[area] || [];
                const checkedCount = Object.values(areaState.items).filter(Boolean).length;
                return (
                  <div key={area} style={{ border:`2px solid ${areaState.enabled?C.primary:C.border}`, borderRadius:10, overflow:'hidden', transition:'all 0.15s' }}>
                    <button type="button" onClick={() => toggleArea(area)}
                      style={{ width:'100%', padding:'10px 14px', background:areaState.enabled?'#eef2ff':C.white, border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:10, textAlign:'left' }}>
                      <span style={{ fontSize:'1rem' }}>{areaState.enabled?'✅':'⬜'}</span>
                      <span style={{ fontWeight:700, fontSize:'0.85rem', color:areaState.enabled?C.primary:C.muted, flex:1 }}>{area}</span>
                      {areaState.enabled && checkedCount > 0 && (
                        <span style={{ background:C.primary, color:'#fff', fontSize:'0.68rem', fontWeight:800, borderRadius:20, padding:'1px 8px' }}>{checkedCount}/{subitems.length}</span>
                      )}
                    </button>
                    {areaState.enabled && subitems.length > 0 && (
                      <div style={{ padding:'8px 14px 12px', background:'#f5f7ff', borderTop:`1px solid #c7d2fe` }}>
                        <p style={{ margin:'0 0 8px', fontSize:'0.7rem', color:C.primary, fontWeight:700 }}>Selecione os itens com pendência:</p>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(190px,1fr))', gap:6 }}>
                          {subitems.map(item => (
                            <label key={item} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', padding:'5px 8px', borderRadius:6, background: areaState.items[item]?'#e0e7ff':C.white, border:`1px solid ${areaState.items[item]?'#a5b4fc':C.border}`, transition:'all 0.1s' }}>
                              <input type="checkbox" checked={!!areaState.items[item]} onChange={() => toggleAreaItem(area, item)}
                                style={{ accentColor:C.primary, width:15, height:15, flexShrink:0 }} />
                              <span style={{ fontSize:'0.78rem', color:areaState.items[item]?C.primary:C.text, fontWeight:areaState.items[item]?700:400 }}>{item}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* PAD — pré-populado com o que foi preenchido na etapa Relatório */}
          <div style={{ background:'#f0fdf4', borderRadius:10, padding:14, border:'1px solid #bbf7d0' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
              <p style={{ margin:0, fontWeight:800, fontSize:'0.85rem', color:'#15803d' }}>🩺 PAD — Plano de Atenção Domiciliar</p>
              {dadosPADSalvo && (
                <button type="button" onClick={() => setPad(buildPadInit(dadosPADSalvo))}
                  style={{ background:'#dcfce7', color:'#15803d', border:'1px solid #bbf7d0', borderRadius:8, padding:'4px 10px', cursor:'pointer', fontWeight:700, fontSize:'0.72rem' }}>
                  ↩ Importar PAD da Captação
                </button>
              )}
            </div>
            <p style={{ margin:'0 0 10px', fontSize:'0.75rem', color:'#166534' }}>
              {padPreenchido
                ? 'PAD carregado da etapa Relatório. Revise e ajuste se necessário.'
                : 'Nenhuma disciplina selecionada ainda. Ative as que se aplicam.'}
            </p>
            {CAP_PAD_DISCIPLINES.map(disc => {
              const d = pad[disc.key];
              const subs = CAP_PAD_SUBSPECIALTIES[disc.key];
              return (
                <div key={disc.key} style={{ background:C.white, borderRadius:8, padding:'8px 12px', marginBottom:6, border:`1.5px solid ${d.enabled ? '#10b981' : C.border}` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: d.enabled ? 8 : 0 }}>
                    <div onClick={() => updatePad(disc.key, 'enabled', !d.enabled)}
                      style={{ width:38, height:20, borderRadius:10, cursor:'pointer', position:'relative', flexShrink:0, background: d.enabled ? '#10b981' : '#cbd5e1', transition:'background 0.2s' }}>
                      <div style={{ width:14, height:14, borderRadius:'50%', background:'#fff', position:'absolute', top:3, left: d.enabled ? 21 : 3, transition:'left 0.2s' }} />
                    </div>
                    <span style={{ fontWeight:600, fontSize:'0.82rem', color: d.enabled ? '#065f46' : C.muted }}>{disc.label}</span>
                  </div>
                  {d.enabled && subs && (
                    <div style={{ display:'flex', flexDirection:'column', gap:4, paddingLeft:6, borderLeft:'2px solid #10b98140' }}>
                      {subs.map(sub => (
                        <div key={sub} style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ fontSize:'0.72rem', color:C.text, flex:1 }}>{sub}</span>
                          <select value={d.subspecialidades?.[sub] || ''} onChange={e => updateSubspe(disc.key, sub, e.target.value)}
                            style={{ ...inp, fontSize:'0.7rem', flex:'0 0 auto', width:156 }}>
                            <option value="">— Selecione —</option>
                            <option value="N/A">N/A</option>
                            {opcoesComAtual(freqOptions, d.subspecialidades?.[sub]).map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                  {d.enabled && !subs && (
                    <select value={d.frequencia} onChange={e => updatePad(disc.key, 'frequencia', e.target.value)} style={{ ...inp, fontSize:'0.8rem' }}>
                      <option value="">— Frequência —</option>
                      {opcoesComAtual(freqOptions, d.frequencia).map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  )}
                </div>
              );
            })}
          </div>

          {/* 👩‍⚕️ Agendar enfermeiro de implantação */}
          {profsDisponiveis.length > 0 && (
            <div style={{ background:'#f0f9ff', borderRadius:10, padding:14, border:'1px solid #bae6fd', marginTop:14 }}>
              <p style={{ margin:'0 0 10px', fontWeight:800, fontSize:'0.85rem', color:'#0369a1' }}>👩‍⚕️ Agendar Enfermeiro de Implantação</p>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {profsDisponiveis.map(p => {
                  const ag = form.agendamentosPorProf[p.id] || {};
                  const calKey = `impl_${p.id}`;
                  return (
                    <div key={p.id} style={{ background:C.white, borderRadius:10, padding:14, border:`1px solid ${ag.data ? '#0ea5e9' : C.border}` }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                        <div style={{ width:34, height:34, borderRadius:'50%', background:'#ecfeff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'0.8rem', color:'#0891b2' }}>
                          {p.nome.split(' ').map(w=>w[0]).join('').substring(0,2).toUpperCase()}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:'0.88rem', color:C.text }}>{p.nome}</div>
                          <div style={{ fontSize:'0.72rem', color:C.muted }}>{p.funcao}</div>
                        </div>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'#f8fafc', padding:12, borderRadius:8, border:`1px solid ${C.border}` }}>
                        <div>
                          <div style={{ fontSize:'0.72rem', fontWeight:700, color:C.muted, textTransform:'uppercase', marginBottom:2 }}>Data e Horário</div>
                          {ag.data && ag.hora ? (
                            <div style={{ fontWeight:800, color:'#0891b2' }}>{ag.data.split('-').reverse().join('/')} às {ag.hora}</div>
                          ) : (
                            <div style={{ color:'#94a3b8', fontSize:'0.85rem' }}>Não selecionado</div>
                          )}
                        </div>
                        <button onClick={() => setCalendarOpenFor(calendarOpenFor === calKey ? null : calKey)}
                          style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 14px', cursor:'pointer', fontWeight:700, color:'#0891b2', fontSize:'0.8rem', display:'flex', alignItems:'center', gap:6 }}>
                          📅 {calendarOpenFor === calKey ? 'Fechar Calendário' : 'Selecionar Horário'}
                        </button>
                      </div>
                      {calendarOpenFor === calKey && (
                        <Modal maxWidth={900} onClose={() => setCalendarOpenFor(null)}>
                          <div style={{ padding:'18px 22px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <div>
                              <h3 style={{ margin:0, fontSize:'1.1rem', fontWeight:800, color:C.text }}>📅 Agendar para Implantação</h3>
                              <p style={{ margin:0, fontSize:'0.82rem', color:C.muted, marginTop:2 }}>Profissional: <strong>{p.nome}</strong></p>
                            </div>
                            <button onClick={() => setCalendarOpenFor(null)} style={{ background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:C.muted, lineHeight:1 }}>✕</button>
                          </div>
                          <div style={{ padding:20, background:C.bg }}>
                            <WeeklyCalendarPicker
                              disponibilidade={p.disponibilidade}
                              agendamentos={agendamentos.filter(a => a.profissionalId === p.id && a.status !== 'cancelado')}
                              selectedDate={ag.data}
                              selectedTime={ag.hora}
                              onSelect={(d, h) => {
                                setForm(prev => ({ ...prev, agendamentosPorProf: { ...prev.agendamentosPorProf, [p.id]: { ...(prev.agendamentosPorProf[p.id] || {}), data: d, hora: h } } }));
                                setCalendarOpenFor(null);
                              }}
                            />
                          </div>
                        </Modal>
                      )}
                    </div>
                  );
                })}

                {/* Curinga de implantação */}
                {(() => {
                  const ag = form.agendamentosPorProf['impl_curinga'] || {};
                  return (
                    <div style={{ background:'#fffbeb', borderRadius:10, padding:14, border:'2px dashed #fbbf24' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                        <div style={{ width:34, height:34, borderRadius:'50%', background:'#fef3c7', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'1rem' }}>⭐</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#92400e' }}>Profissional Curinga</div>
                          <div style={{ fontSize:'0.72rem', color:'#a16207' }}>Profissional avulso / não cadastrado</div>
                        </div>
                      </div>
                      <div style={{ marginBottom:10 }}>
                        <label style={{ ...lbl, color:'#92400e' }}>Nome do Profissional</label>
                        <input style={{ ...inp, borderColor:'#fbbf24' }} value={ag.nomeCustom||''} onChange={e=>setForm(prev=>({ ...prev, agendamentosPorProf:{ ...prev.agendamentosPorProf, impl_curinga:{ ...ag, nomeCustom:e.target.value } } }))} placeholder="Digite o nome do profissional..." />
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'#fef9c3', padding:12, borderRadius:8, border:'1px solid #fde68a' }}>
                        <div>
                          <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#a16207', textTransform:'uppercase', marginBottom:2 }}>Data e Horário</div>
                          {ag.data && ag.hora ? (
                            <div style={{ fontWeight:800, color:'#92400e' }}>{ag.data.split('-').reverse().join('/')} às {ag.hora}</div>
                          ) : (
                            <div style={{ color:'#94a3b8', fontSize:'0.85rem' }}>Não selecionado</div>
                          )}
                        </div>
                        <button onClick={()=>setCalendarOpenFor(calendarOpenFor==='impl_curinga'?null:'impl_curinga')} style={{ background:'#fff', border:'1px solid #fbbf24', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontWeight:700, color:'#92400e', fontSize:'0.8rem', display:'flex', alignItems:'center', gap:6 }}>
                          📅 {calendarOpenFor==='impl_curinga'?'Fechar Calendário':'Selecionar Horário'}
                        </button>
                      </div>
                      {calendarOpenFor==='impl_curinga' && (
                        <Modal maxWidth={900} onClose={()=>setCalendarOpenFor(null)}>
                          <div style={{ padding:'18px 22px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <h3 style={{ margin:0, fontSize:'1.1rem', fontWeight:800, color:C.text }}>📅 Agendar para Implantação — Curinga</h3>
                            <button onClick={()=>setCalendarOpenFor(null)} style={{ background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:C.muted }}>✕</button>
                          </div>
                          <div style={{ padding:20, background:C.bg }}>
                            <WeeklyCalendarPicker disponibilidade={CURINGA_DISPONIBILIDADE} agendamentos={[]} selectedDate={ag.data} selectedTime={ag.hora}
                              onSelect={(d,h)=>{ setForm(prev=>({ ...prev, agendamentosPorProf:{ ...prev.agendamentosPorProf, impl_curinga:{ ...ag, data:d, hora:h } } })); setCalendarOpenFor(null); }} />
                          </div>
                        </Modal>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </>
      ); // fim do bloco else (sem prévia)

      case 'Implantado': return (
        <div style={{ textAlign:'center', padding:'16px 0' }}>
          <div style={{ fontSize:'3rem', marginBottom:12 }}>🎉</div>
          <h3 style={{ margin:'0 0 8px', color:'#15803d', fontWeight:800 }}>Paciente Implantado!</h3>
        </div>
      );

      default: return <p>Confirmar mudança para <strong>{targetStatus}</strong>?</p>;
    }
  };

  return (
    <Modal maxWidth={targetStatus === 'Em Implantação' ? 680 : 540} onClose={onClose}>
      <ModalHeader title={modoEdicao ? `✏️ Editar — ${targetStatus}` : targetStatus} subtitle={`Paciente: ${paciente.nome}`} icon={s?.icon} onClose={onClose} color={s?.color} />
      {modoEdicao && (
        <div style={{ margin:'14px 22px 0', padding:'8px 12px', borderRadius:8, background:'#fef3c7', border:'1px solid #fde68a', color:'#92400e', fontSize:'0.78rem', fontWeight:600 }}>
          ✏️ Você está corrigindo os dados desta etapa já concluída. O status atual do paciente não muda — fica registrado no histórico que foi uma edição.
        </div>
      )}
      <div style={{ padding:'20px 22px', flex:1 }}>
        {isBackward ? (
            <div style={{ marginBottom:16 }}>
              <label style={lbl}>Justificativa para o retorno <span style={{color:'#ef4444'}}>*</span></label>
              <textarea style={{...inp,minHeight:80}} value={form.justificativa} onChange={e=>set('justificativa',e.target.value)} placeholder="Por que o paciente está voltando de etapa?" />
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {renderBody()}
              <div>
                <label style={lbl}>Observações extras (opcional)</label>
                <textarea style={{...inp,minHeight:60}} value={form.obs} onChange={e=>set('obs',e.target.value)} placeholder="Alguma informação adicional..." />
              </div>
            </div>
          )}
      </div>
      <div style={{ padding:'14px 22px', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ ...btnS(C.white,C.muted), border:`1px solid ${C.border}` }} disabled={saving}>Cancelar</button>
          <button onClick={async ()=>{
            if (isBackward && !form.justificativa.trim()) return alert('A justificativa é obrigatória para voltar etapa.');
            if (targetStatus === 'Aguardando Retorno da Operadora') {
              if (!form.classificacao) return alert('Por favor, selecione a Classificação do atendimento (ID, AD ou Pontual).');
              if (!form.categoria) return alert('Por favor, selecione a Categoria do paciente.');
            }
            if (targetStatus === 'Aguardando Retorno da Operadora' || targetStatus === 'Em Implantação') {
              if (!padPreenchido) return alert('Selecione ao menos uma disciplina do PAD antes de continuar.');
              for (const disc of CAP_PAD_DISCIPLINES) {
                const d = pad[disc.key];
                if (!d.enabled) continue;
                const subs = CAP_PAD_SUBSPECIALTIES[disc.key];
                if (subs) {
                  const faltando = subs.filter(s => !d.subspecialidades?.[s]);
                  if (faltando.length > 0) return alert(`Preencha todas as sub-especialidades de "${disc.label}" (use N/A para as que não se aplicam). Faltam: ${faltando.join(', ')}`);
                }
              }
            }
            setSaving(true); await onConfirm({ ...form, pad, areas }); setSaving(false); onClose();
          }} style={{ ...btnS(modoEdicao ? '#f59e0b' : isBackward ? '#ef4444' : s?.color), display:'flex', alignItems:'center', gap:6, opacity:saving?0.7:1 }} disabled={saving}>
            {saving ? 'Salvando...' : modoEdicao ? '💾 Salvar edição' : isBackward ? `⏪ Confirmar Retorno` : `✅ Confirmar ➔ ${s?.short}`}
          </button>
        </div>
    </Modal>
  );
}

// ── Ficha do Paciente ─────────────────────────────────────────────────────────
function getStepSummary(status, dados) {
  if (!dados) return null;
  switch (status) {
    case 'Aguardando Avaliação': {
      const ags = Object.entries(dados.agendamentosPorProf || {}).filter(([, ag]) => ag.data);
      if (!ags.length) return null;
      return ags.map(([, ag]) => `📅 ${ag.data.split('-').reverse().join('/')} às ${ag.hora === 'livre' ? (ag.horaLivre || '?') : (ag.hora || '?')}`).join(' · ');
    }
    case 'Em Implantação': {
      const parts = [];
      if (dados.dataImplantacao) parts.push(`📅 ${dados.dataImplantacao.split('-').reverse().join('/')}${dados.horaImplantacao ? ' às ' + dados.horaImplantacao : ''}`);
      if (dados.tipoRemocao) parts.push(`🚗 ${dados.tipoRemocao}`);
      return parts.join(' · ') || null;
    }
    case 'Avaliação Concluída':
      return dados.confirmadoPor ? `✅ Confirmado por ${dados.confirmadoPor}` : null;
    default:
      return null;
  }
}

// Tabela PAD — Disciplina | Frequência / Sub-especialidades
function PadTabela({ pad, titulo, cor = '#065f46', bg = '#f0fdf4', border = '#bbf7d0' }) {
  const linhas = CAP_PAD_DISCIPLINES
    .map(disc => {
      const d = pad?.[disc.key];
      if (!d?.enabled) return null;
      const subs = CAP_PAD_SUBSPECIALTIES[disc.key];
      let freq = '';
      if (subs) {
        const ativos = Object.entries(d.subspecialidades || {})
          .filter(([, v]) => v && v !== 'N/A')
          .map(([sub, f]) => `${sub}: ${f}`);
        freq = ativos.length > 0 ? ativos.join('\n') : '—';
      } else {
        freq = d.frequencia || '—';
      }
      return { disciplina: disc.label, freq };
    })
    .filter(Boolean);

  if (linhas.length === 0) return null;

  return (
    <div style={{ marginBottom:18, borderRadius:10, overflow:'hidden', border:`1px solid ${border}` }}>
      <div style={{ padding:'8px 14px', background:bg, borderBottom:`1px solid ${border}` }}>
        <span style={{ fontWeight:700, fontSize:'0.82rem', color:cor }}>📋 {titulo}</span>
      </div>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
        <thead>
          <tr style={{ background:`${bg}` }}>
            <th style={{ padding:'6px 14px', textAlign:'left', fontSize:'0.68rem', fontWeight:700, color:cor, textTransform:'uppercase', letterSpacing:'0.04em', width:'38%', borderBottom:`1px solid ${border}` }}>Disciplina</th>
            <th style={{ padding:'6px 14px', textAlign:'left', fontSize:'0.68rem', fontWeight:700, color:cor, textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:`1px solid ${border}` }}>Frequência / Sub-especialidade</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : bg }}>
              <td style={{ padding:'7px 14px', fontWeight:600, color:'#1e293b', verticalAlign:'top', borderBottom:`1px solid ${border}` }}>{l.disciplina}</td>
              <td style={{ padding:'7px 14px', color:'#334155', whiteSpace:'pre-wrap', lineHeight:1.5, borderBottom:`1px solid ${border}` }}>{l.freq}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FichaPaciente({ paciente, profissionais = [] }) {
  const basicos = [
    { l:'Convênio',         v: paciente.convenio },
    { l:'Matrícula',        v: paciente.matricula },
    { l:'Contato',          v: paciente.telefone },
    { l:'Filial',           v: nf(paciente.filial) },
    { l:'Hospital',         v: paciente.hospital },
    { l:'Região Hospital',  v: paciente.regiaoHospital },
    { l:'Data Solicitação', v: (() => { const d = new Date(paciente.dataSolicitacao); return paciente.dataSolicitacao && !isNaN(d) ? d.toLocaleString('pt-BR',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}) : null; })() },
    { l:'Data Nascimento',  v: (() => { const d = new Date(paciente.dataNascimento + 'T00:00'); return paciente.dataNascimento && !isNaN(d) ? d.toLocaleDateString('pt-BR') : null; })() },
    { l:'CEP',              v: paciente.cep },
    { l:'VM',               v: paciente.vm ? 'Sim' : null },
    { l:'Convênio Especial',v: paciente.convenioEspecial },
    { l:'Liminar',          v: paciente.liminar ? 'Sim' : null },
    { l:'Tipo Implantação', v: paciente.tipoImplantacao },
    { l:'Observações',      v: paciente.observacoes },
  ].filter(c => c.v);

  const padRelatorio   = paciente.dadosPAD;
  const padImplantacao = paciente.dadosImplantacao?.pad;
  const padIgual       = JSON.stringify(padImplantacao) === JSON.stringify(padRelatorio);
  const historico      = (paciente.historico || []).slice().reverse();

  return (
    <div style={{ padding:'16px 22px', overflowY:'auto', maxHeight:420 }}>
      {paciente.desconsideradoPelaOperadora && (
        <div style={{ background:'#dc2626', color:'#fff', borderRadius:10, padding:'10px 14px', marginBottom:14, fontSize:'0.82rem' }}>
          <div style={{ fontWeight:800, marginBottom:4 }}>⛔ A operadora pediu para DESCONSIDERAR este pedido</div>
          {paciente.desconsideradoMotivo && <div style={{ whiteSpace:'pre-wrap' }}>"{paciente.desconsideradoMotivo}"</div>}
          <div style={{ fontSize:'0.74rem', opacity:.9, marginTop:4 }}>
            E-mail de {paciente.desconsideradoEmail || '—'}{paciente.desconsideradoEm ? ` · ${new Date(paciente.desconsideradoEm).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}` : ''}
          </div>
          <button type="button" onClick={async () => {
              if (!window.confirm('Remover o aviso de "desconsiderar" deste card? (o card volta à cor normal)')) return;
              await updateDoc(doc(db, getCollectionName('captacao_pacientes'), paciente.id), { desconsideradoPelaOperadora: false, desconsideradoRemovidoEm: new Date().toISOString(), desconsideradoRemovidoPor: auth.currentUser?.email || '' });
            }}
            style={{ marginTop:8, background:'#fff', color:'#991b1b', border:0, borderRadius:6, padding:'4px 10px', fontWeight:800, fontSize:'0.75rem', cursor:'pointer' }}>
            Remover aviso
          </button>
        </div>
      )}
      {/* Robô do e-mail com a operadora (20/10): proposta enviada / autorização / negativa */}
      {paciente.autorizacaoOperadora?.em && (
        <div style={{ background:'#16a34a', color:'#fff', borderRadius:10, padding:'10px 14px', marginBottom:14, fontSize:'0.82rem' }}>
          {(() => {
            const a = paciente.autorizacaoOperadora;
            const temItens = a.itens?.length > 0;
            return (
              <>
                <div style={{ fontWeight:800, fontSize:'0.9rem', marginBottom:8 }}>✅ {rotuloAutorizacao(a)}</div>
                {a.tipo === 'parcial' && (
                  <div style={{ background:'#fef9c3', color:'#713f12', borderRadius:6, padding:'4px 8px', fontWeight:700, fontSize:'0.76rem', marginBottom:8 }}>⚠ Autorização parcial: confira o que ficou de fora da proposta</div>
                )}
                {temItens ? (
                  <>
                    <div style={{ fontSize:'0.74rem', opacity:.9, marginBottom:6 }}>{a.cabecalho || 'Itens autorizados'}:</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom: a.obs ? 8 : 0 }}>
                      {a.itens.map((it, i) => (
                        <span key={i} style={{ background:'rgba(255,255,255,.2)', border:'1px solid rgba(255,255,255,.35)', borderRadius:20, padding:'3px 10px', fontWeight:700, fontSize:'0.78rem' }}>{it}</span>
                      ))}
                    </div>
                    {a.obs && <div style={{ fontSize:'0.8rem' }}><strong>Obs.:</strong> {a.obs}</div>}
                  </>
                ) : (
                  <div style={{ whiteSpace:'pre-wrap' }}>{a.cabecalho ? <strong>{a.cabecalho}: </strong> : null}{a.cabecalho ? a.obs : a.texto}</div>
                )}
                <div style={{ fontSize:'0.72rem', opacity:.85, marginTop:8, borderTop:'1px solid rgba(255,255,255,.25)', paddingTop:6 }}>
                  📧 {a.de}{a.assunto ? ` · ${a.assunto}` : ''}
                </div>
              </>
            );
          })()}
        </div>
      )}
      {paciente.negativaOperadora?.em && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', color:'#991b1b', borderRadius:10, padding:'10px 14px', marginBottom:14, fontSize:'0.82rem' }}>
          <div style={{ fontWeight:800, marginBottom:4 }}>🚫 A operadora respondeu NEGANDO ({fmtDiaHora(paciente.negativaOperadora.em)})</div>
          <div style={{ whiteSpace:'pre-wrap' }}>"{paciente.negativaOperadora.texto}"</div>
        </div>
      )}
      {paciente.propostaEnviada?.em && (
        <div style={{ background:'#fef9c3', border:'1px solid #facc15', color:'#713f12', borderRadius:10, padding:'10px 14px', marginBottom:14, fontSize:'0.82rem' }}>
          <div style={{ fontWeight:800, marginBottom:4 }}>📤 Proposta enviada à operadora em {fmtDiaHora(paciente.propostaEnviada.em)}</div>
          <div>Para: {paciente.propostaEnviada.para}{paciente.propostaEnviada.arquivos?.length ? ` · Anexos: ${paciente.propostaEnviada.arquivos.join(', ')}` : ''}</div>
          {paciente.envioOperadoraPendente && (
            <div style={{ marginTop:6, fontWeight:700 }}>⚠ O card andou sozinho pelo e-mail: complete classificação, categoria e PAD editando a etapa "Aguardando Retorno da Operadora" no histórico abaixo (✏️).</div>
          )}
        </div>
      )}
      {/* Card criado pelo e-mail da operadora: o que conferir e os PDFs que vieram junto */}
      {paciente.origem === 'Email' && (paciente.revisar?.length > 0 || paciente.anexos?.length > 0) && (
        <div style={{ marginBottom:14, display:'flex', flexDirection:'column', gap:8 }}>
          {paciente.revisar?.length > 0 && paciente.status === 'Recebido' && (
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', color:'#991b1b', borderRadius:8, padding:'8px 12px', fontSize:'0.8rem' }}>
              <strong>⚠ Conferir (card criado pelo e-mail):</strong> {paciente.revisar.join(' · ')}
            </div>
          )}
          {paciente.anexos?.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, alignItems:'center' }}>
              <span style={{ fontSize:'0.72rem', color:C.muted, fontWeight:700 }}>📎 Anexos do e-mail:</span>
              {paciente.anexos.map((a, i) => (
                <ArquivoLink key={i} href={a.url} target="_blank" rel="noreferrer"
                  style={{ fontSize:'0.78rem', color:'#1e40af', background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'3px 8px', textDecoration:'none' }}>
                  {a.nome}
                </ArquivoLink>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Dados cadastrais */}
      {basicos.length > 0 && (
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:'0.7rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Dados do Paciente</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:7 }}>
            {basicos.map(c => (
              <div key={c.l} style={{ background:C.bg, borderRadius:8, padding:'8px 12px', border:`1px solid ${C.border}`, gridColumn: c.l==='Observações' ? '1 / -1' : undefined }}>
                <div style={{ fontSize:'0.65rem', color:C.muted, fontWeight:700, textTransform:'uppercase', marginBottom:2 }}>{c.l}</div>
                <div style={{ fontSize:'0.85rem', color:C.text, fontWeight:600, whiteSpace:'pre-wrap' }}>{c.v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PAD — Plano acordado (Relatório/Sessão Clínica) */}
      {padRelatorio && <PadTabela pad={padRelatorio} titulo="PAD — Plano de Atenção Domiciliar (Sessão Clínica)" />}
      {padImplantacao && !padIgual && <PadTabela pad={padImplantacao} titulo="PAD — Abertura da Ocorrência (Implantação)" cor="#1e40af" bg="#eff6ff" border="#bfdbfe" />}

      {/* Linha do tempo das etapas */}
      {historico.length > 0 && (
        <div>
          <div style={{ fontSize:'0.7rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Linha do Tempo</div>
          <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
            {historico.map((h, i) => {
              const st      = STATUSES.find(x => x.key === h.status);
              const obs     = h.dados?.obs?.trim();
              const just    = h.dados?.justificativa?.trim();
              const summary = getStepSummary(h.status, h.dados);
              const hasBody = !!(obs || just || summary);
              return (
                <div key={i} style={{ borderRadius:9, border:`1px solid ${hasBody ? C.border : '#eef1f5'}`, overflow:'hidden' }}>
                  <div style={{ padding:'7px 12px', background:`${st?.color || '#94a3b8'}14`, display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:'0.85rem' }}>{st?.icon || '•'}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <span style={{ fontWeight:700, fontSize:'0.82rem', color:st?.color || C.text }}>{h.status}</span>
                      {h.responsavel && <span style={{ fontSize:'0.68rem', color:C.muted, marginLeft:6 }}>por {h.responsavel.split('@')[0]}</span>}
                    </div>
                    <span style={{ fontSize:'0.68rem', color:C.muted, whiteSpace:'nowrap' }}>{fmtTs(h.timestamp)}</span>
                  </div>
                  {hasBody && (
                    <div style={{ padding:'8px 12px', display:'flex', flexDirection:'column', gap:5, background:'#fff' }}>
                      {summary && <div style={{ fontSize:'0.8rem', color:'#475569' }}>{summary}</div>}
                      {obs && (
                        <div style={{ fontSize:'0.82rem', color:C.text, lineHeight:1.5, padding:'5px 10px', background:'#f0fdf4', borderRadius:6, borderLeft:'3px solid #86efac' }}>
                          <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#166534', textTransform:'uppercase', display:'block', marginBottom:2 }}>Observação</span>
                          {obs}
                        </div>
                      )}
                      {just && (
                        <div style={{ fontSize:'0.82rem', color:'#92400e', lineHeight:1.5, padding:'5px 10px', background:'#fffbeb', borderRadius:6, borderLeft:'3px solid #fbbf24' }}>
                          <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#a16207', textTransform:'uppercase', display:'block', marginBottom:2 }}>Justificativa</span>
                          {just}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {historico.length === 0 && basicos.length === 0 && (
        <div style={{ textAlign:'center', padding:'30px 0', color:C.muted, fontSize:'0.85rem' }}>Nenhuma informação preenchida ainda.</div>
      )}
    </div>
  );
}

// ── Cancel Modal ─────────────────────────────────────────────────────────────
function CancelModal({ paciente, onClose, onConfirm }) {
  const [justificativa, setJustificativa] = useState('');
  const [senha, setSenha] = useState('');
  const [saving, setSaving] = useState(false);
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' };
  const handleSubmit = async () => {
    if (!justificativa.trim()) { alert('Justificativa é obrigatória.'); return; }
    if (!senha.trim()) { alert('Senha é obrigatória.'); return; }
    setSaving(true);
    await onConfirm({ paciente, justificativa: justificativa.trim(), senha });
    setSaving(false);
  };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, padding:28, width:420, maxWidth:'95vw', boxShadow:'0 8px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize:'1.1rem', fontWeight:700, color:'#dc2626', marginBottom:6 }}>⚠️ Cancelar pela Operadora</div>
        <div style={{ fontSize:'0.84rem', color:'#475569', marginBottom:18 }}>
          Paciente: <strong>{paciente.nome}</strong><br />
          Status atual: <strong>{paciente.status}</strong><br />
          O paciente sairá do fluxo ativo mas não será excluído.
        </div>
        <label style={{ display:'block', fontSize:'0.8rem', fontWeight:600, color:'#374151', marginBottom:4 }}>Justificativa <span style={{ color:'#dc2626' }}>*</span></label>
        <textarea
          value={justificativa}
          onChange={e => setJustificativa(e.target.value)}
          rows={4}
          placeholder="Descreva o motivo do cancelamento pela operadora..."
          style={{ ...inp, resize:'vertical', marginBottom:14 }}
        />
        <label style={{ display:'block', fontSize:'0.8rem', fontWeight:600, color:'#374151', marginBottom:4 }}>Senha para confirmar <span style={{ color:'#dc2626' }}>*</span></label>
        <input
          type="password"
          value={senha}
          onChange={e => setSenha(e.target.value)}
          placeholder="Sua senha de acesso"
          style={{ ...inp, marginBottom:20 }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        />
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose} disabled={saving} style={{ padding:'8px 18px', borderRadius:6, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer', fontSize:'0.87rem' }}>Cancelar</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding:'8px 18px', borderRadius:6, border:'none', background:'#dc2626', color:'#fff', cursor:'pointer', fontWeight:600, fontSize:'0.87rem' }}>
            {saving ? 'Processando...' : 'Confirmar Cancelamento'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Patient Timeline ──────────────────────────────────────────────────────────
function PatientTimeline({ paciente, onClose, onAdvance, onEdit, onEditHistorico, onCancelar, profissionais = [], agendamentos = [] }) {
  const [tab, setTab]  = useState('historico');
  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const curIdx    = statusIdx(paciente.status);
  const agPaciente = agendamentos.filter(a => a.pacienteId === paciente.id && a.tipoAgenda === 'avaliacao' && a.status !== 'cancelado');
  const next      = nextStatus(paciente.status);
  const prev      = prevStatus(paciente.status);
  const slaActive = isSLAActive(paciente);
  const sla       = slaActive ? getSLAInfo(paciente.dataSolicitacao || paciente.dataAbertura, getSLAHours(paciente)) : null;

  return (
    <>
    <Modal maxWidth={760} onClose={onClose}>
      <div style={{ padding:'18px 22px 14px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontSize:'0.7rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:2 }}>{paciente.numero}</div>
          <h3 style={{ margin:'0 0 6px', fontSize:'1.1rem', fontWeight:800, color:C.text }}>{paciente.nome}</h3>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ fontSize:'0.78rem', color:C.muted }}>{paciente.convenio}</span>
            <TipoBadge paciente={paciente} />
            <StatusBadge status={paciente.status} />
            {paciente.dataImplantacaoPrevista && (
              <span style={{ fontSize:'0.75rem', color:'#0891b2', fontWeight:700, background:'#ecfeff', border:'1px solid #a5f3fc', borderRadius:6, padding:'2px 8px' }}>
                📅 Implantação: {paciente.dataImplantacaoPrevista.split('-').reverse().join('/')}{paciente.horaImplantacaoPrevista ? ` às ${paciente.horaImplantacaoPrevista}` : ''}
              </span>
            )}
          </div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:C.muted }}>✕</button>
      </div>

      {slaActive && sla && (
        <div style={{ padding:'12px 22px', background:sla.bg, borderBottom:`1px solid ${sla.border}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:'0.8rem', fontWeight:700, color:sla.color }}>
              ⏱ {sla.expired || sla.status === 'warning' ? sla.remainingLabel : sla.label} / {getSLAHours(paciente)}h
            </span>
            <div style={{ flex:1, height:6, borderRadius:3, background:'#e2e8f0' }}>
              <div style={{ height:'100%', width:`${sla.pct}%`, borderRadius:3, background:sla.color }} />
            </div>
            <span style={{ fontSize:'0.72rem', color:sla.color, fontWeight:700 }}>{Math.round(sla.pct)}%</span>
          </div>
        </div>
      )}

      <div style={{ padding:'20px 22px 8px', overflowX:'auto' }}>
        <div style={{ display:'flex', alignItems:'flex-start', minWidth:600 }}>
          {STATUSES.map((s,i) => {
            const done=i<curIdx, current=i===curIdx;
            const isAgAval = s.key === 'Aguardando Avaliação';
            const temAg = isAgAval && agPaciente.length > 0;
            return (
              <React.Fragment key={s.key}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:1, minWidth:56 }}>
                  <div
                    onClick={isAgAval ? () => setShowAgendaModal(true) : undefined}
                    title={isAgAval ? (temAg ? 'Ver / alterar agendamento de avaliação' : 'Agendar avaliação') : undefined}
                    style={{ position:'relative', width:38, height:38, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', background:done?s.color:current?C.white:'#f1f5f9', border:`3px solid ${done||current?s.color:'#e2e8f0'}`, boxShadow:current?`0 0 0 5px ${s.color}25`: isAgAval?`0 0 0 3px #84cc1640`:'none', fontSize:'1rem', transition:'all 0.3s', cursor:isAgAval?'pointer':'default' }}>
                    {done?'✓':s.icon}
                    {temAg && <div style={{ position:'absolute', top:-2, right:-2, width:11, height:11, borderRadius:'50%', background:'#22c55e', border:'2px solid #fff' }} />}
                    {isAgAval && !temAg && <div style={{ position:'absolute', top:-2, right:-2, width:11, height:11, borderRadius:'50%', background:'#f59e0b', border:'2px solid #fff' }} title="Sem agendamento" />}
                  </div>
                  <span style={{ fontSize:'0.6rem', fontWeight:current?700:500, color:done?s.color:current?s.color:C.muted, marginTop:6, textAlign:'center', lineHeight:1.2, maxWidth:58 }}>{s.short}</span>
                </div>
                {i<STATUSES.length-1 && <div style={{ flex:'0 0 10px', height:3, marginTop:17, background:done?STATUSES[i].color:'#e2e8f0', borderRadius:2 }} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Abas */}
      <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, padding:'0 22px' }}>
        {[{ k:'historico', l:'📋 Histórico' }, { k:'ficha', l:'📄 Ficha do Paciente' }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ border:'none', background:'none', padding:'10px 16px', cursor:'pointer', fontWeight:tab===t.k?700:500, fontSize:'0.84rem', color:tab===t.k?C.primary:C.muted, borderBottom:tab===t.k?`2px solid ${C.primary}`:'2px solid transparent', marginBottom:-1 }}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'historico' ? (
        <div style={{ padding:'16px 22px', maxHeight:260, overflowY:'auto' }}>
          <p style={{ margin:'0 0 10px', fontSize:'0.74rem', color:C.muted }}>Clique numa etapa pra ver ou corrigir os dados que foram preenchidos nela.</p>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {(paciente.historico||[]).slice().reverse().map((h,i)=>{
              const st = STATUSES.find(x=>x.key===h.status);
              const editavel = !!onEditHistorico && h.status !== 'Recebido';
              return (
                <div key={i} onClick={editavel ? () => onEditHistorico(h) : undefined}
                  title={editavel ? 'Clique para ver/editar os dados desta etapa' : undefined}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 12px', background:C.bg, borderRadius:8, border:`1px solid ${C.border}`, cursor:editavel?'pointer':'default', transition:'background 0.15s' }}
                  onMouseEnter={editavel ? e=>{e.currentTarget.style.background='#eef2ff';} : undefined}
                  onMouseLeave={editavel ? e=>{e.currentTarget.style.background=C.bg;} : undefined}>
                  <span>{st?.icon||'•'}</span>
                  <div style={{ flex:1 }}>
                    <span style={{ fontWeight:700, fontSize:'0.82rem', color:st?.color||C.text }}>{h.status}</span>
                    {h.responsavel && <span style={{ fontSize:'0.72rem', color:C.muted, marginLeft:8 }}>por {h.responsavel}</span>}
                    {h.editado && (
                      <span style={{ fontSize:'0.68rem', fontWeight:700, color:'#92400e', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:20, padding:'1px 8px', marginLeft:8 }}>
                        ✏️ editado por {h.editadoPor} em {fmtTs(h.editadoEm)}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize:'0.72rem', color:C.muted, whiteSpace:'nowrap' }}>{fmtTs(h.timestamp)}</span>
                  {editavel && <span style={{ color:C.muted, fontSize:'0.8rem' }}>›</span>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <FichaPaciente paciente={paciente} profissionais={profissionais} />
      )}

      <div style={{ padding:'14px 22px', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onClose} style={{ ...btnS(C.white,C.muted), border:`1px solid ${C.border}` }}>Fechar</button>
          {onEdit && <button onClick={onEdit} style={{ ...btnS('#f59e0b','#fff') }}>✏️ Editar Dados</button>}
          {onCancelar && <button onClick={onCancelar} style={{ ...btnS('#ef4444','#fff') }}>🚫 Cancelar pela Operadora</button>}
        </div>
        <div style={{ display:'flex', gap:10 }}>
            {prev && (
              <button onClick={()=>onAdvance(prev)}
                style={{ ...btnS('#f1f5f9', '#64748b'), display:'flex', alignItems:'center', gap:8, border: `1px solid ${C.border}` }}>
                ⏪ Voltar para {STATUSES.find(s=>s.key===prev)?.short}
              </button>
            )}
            {next && (
              <button onClick={()=>onAdvance(next)}
                style={{ ...btnS(STATUSES.find(s=>s.key===next)?.color||C.primary), display:'flex', alignItems:'center', gap:8 }}>
                {STATUSES.find(s=>s.key===next)?.icon} Avançar para {STATUSES.find(s=>s.key===next)?.short} ➔
              </button>
            )}
        </div>
      </div>
    </Modal>

    {showAgendaModal && (
      <AgendamentoRapidoModal
        paciente={paciente}
        profissionais={profissionais}
        agendamentosExistentes={agPaciente}
        allAgendamentos={agendamentos}
        onClose={() => setShowAgendaModal(false)}
      />
    )}
    </>
  );
}

// ── AgendamentoRapidoModal ──────────────────────────────────────────────────────
function AgendamentoRapidoModal({ paciente, profissionais, agendamentosExistentes = [], allAgendamentos = [], onClose }) {
  const profsNecessariosRoles = PROF_BY_TYPE[getTipoKey(paciente)] || [];
  const profsDisponiveis = profissionais.filter(p => profsNecessariosRoles.includes(p.funcao) && p.status === 'Ativo');

  const initAgend = () => {
    const m = {};
    agendamentosExistentes.forEach(ag => {
      const key = ag.profissionalId === 'curinga' ? 'curinga' : ag.profissionalId;
      m[key] = { data: ag.data || '', hora: ag.hora || '', nomeCustom: ag.profissionalNome || '', docId: ag.id };
    });
    return m;
  };

  const [agendPorProf, setAgendPorProf] = useState(initAgend);
  const [calendarOpenFor, setCalendarOpenFor] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [notificado, setNotificado] = useState({});

  const handleNotificar = async (profId, profEmail, profNome, ag) => {
    if (!profEmail || !ag.data) return;
    const dataFmt = ag.data.split('-').reverse().join('/');
    const corpo = `Você tem uma visita de avaliação agendada para ${paciente.nome} em ${dataFmt}${ag.hora ? ' às ' + ag.hora : ''}.`;
    try {
      await addDoc(collection(db, getCollectionName('notificacoes')), {
        para: profEmail, paraNome: profNome,
        titulo: '📅 Agendamento de Avaliação',
        corpo, pacienteNome: paciente.nome, pacienteId: paciente.id,
        lido: false, criadoEm: serverTimestamp(),
      });
      setNotificado(prev => ({ ...prev, [profId]: true }));
    } catch (e) { console.warn('Notificar:', e.message); }
  };

  const setAg = (profId, field, value) =>
    setAgendPorProf(prev => ({ ...prev, [profId]: { ...(prev[profId] || {}), [field]: value } }));

  const handleSave = async () => {
    setSalvando(true);
    try {
      const dadosPac = {
        pacienteId: paciente.id, pacienteNome: paciente.nome, pacienteNumero: paciente.numero,
        convenio: paciente.convenio || '', hospital: paciente.hospital || '',
        pacienteFilial: paciente.filial || '',
      };
      for (const [profId, ag] of Object.entries(agendPorProf)) {
        if (!ag.data) continue;
        const hora = ag.hora || '';
        if (profId === 'curinga') {
          if (!ag.nomeCustom?.trim()) continue;
          if (ag.docId) {
            await updateDoc(doc(db, getCollectionName('captacao_agendamentos'), ag.docId), { data: ag.data, hora, profissionalNome: ag.nomeCustom.trim(), atualizadoEm: serverTimestamp() });
          } else {
            await addDoc(collection(db, getCollectionName('captacao_agendamentos')), { ...dadosPac, profissionalId: 'curinga', profissionalNome: ag.nomeCustom.trim(), profissionalFuncao: 'Curinga', profissionalEmail: '', isCuringa: true, data: ag.data, hora, status: 'agendado', tipoAgenda: 'avaliacao', criadoEm: serverTimestamp() });
          }
        } else {
          const prof = profissionais.find(p => p.id === profId);
          if (!prof) continue;
          if (ag.docId) {
            await updateDoc(doc(db, getCollectionName('captacao_agendamentos'), ag.docId), { data: ag.data, hora, atualizadoEm: serverTimestamp() });
          } else {
            await addDoc(collection(db, getCollectionName('captacao_agendamentos')), { ...dadosPac, profissionalId: prof.id, profissionalNome: prof.nome, profissionalFuncao: prof.funcao, profissionalEmail: prof.email || '', data: ag.data, hora, status: 'agendado', tipoAgenda: 'avaliacao', criadoEm: serverTimestamp() });
          }
        }
      }
      onClose();
    } catch (e) {
      alert('Erro ao salvar: ' + e.message);
    } finally {
      setSalvando(false);
    }
  };

  const renderProfCard = (profId, nome, funcao, disponibilidade, isCustomName = false) => {
    const ag = agendPorProf[profId] || {};
    const temAg = !!(ag.data);
    const prof = profissionais.find(p => p.id === profId);
    const profEmail = isCustomName ? null : (prof?.email || null);
    const jaNotificou = notificado[profId];
    return (
      <div key={profId} style={{ background: C.bg, borderRadius: 10, padding: 14, border: `1px solid ${temAg ? '#86efac' : C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: temAg ? '#dcfce7' : `${C.primary}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: isCustomName ? '1rem' : '0.8rem', color: temAg ? '#16a34a' : C.primary }}>
            {isCustomName ? '⭐' : nome.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            {isCustomName ? (
              <input value={ag.nomeCustom || ''} onChange={e => setAg(profId, 'nomeCustom', e.target.value)}
                placeholder="Nome do profissional curinga..." style={{ ...inp, margin: 0, width: '100%' }} />
            ) : (
              <>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: C.text }}>{nome}</div>
                <div style={{ fontSize: '0.72rem', color: C.muted }}>{funcao}</div>
              </>
            )}
          </div>
          {temAg && profEmail && (
            <button onClick={() => handleNotificar(profId, profEmail, nome, ag)} disabled={jaNotificou}
              style={{ background: jaNotificou ? '#f0fdf4' : '#dcfce7', color: jaNotificou ? '#16a34a' : '#15803d', border: `1px solid ${jaNotificou ? '#86efac' : '#bbf7d0'}`, borderRadius: 8, padding: '4px 10px', fontSize: '0.72rem', fontWeight: 700, cursor: jaNotificou ? 'default' : 'pointer' }}>
              {jaNotificou ? '✅ Notificado' : '🔔 Notificar'}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: 10, borderRadius: 8, border: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 2 }}>Data / Horário</div>
            {temAg ? (
              <div style={{ fontWeight: 800, color: '#16a34a', fontSize: '0.9rem' }}>
                ✅ {ag.data.split('-').reverse().join('/')} às {ag.hora || '—'}
              </div>
            ) : (
              <div style={{ color: '#94a3b8', fontSize: '0.84rem' }}>Não agendado</div>
            )}
          </div>
          <button onClick={() => setCalendarOpenFor(calendarOpenFor === profId ? null : profId)}
            style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontWeight: 700, color: C.primary, fontSize: '0.8rem' }}>
            📅 {temAg ? 'Reagendar' : 'Agendar'}
          </button>
        </div>
        {calendarOpenFor === profId && (
          <Modal maxWidth={900} onClose={() => setCalendarOpenFor(null)}>
            <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.text }}>📅 {temAg ? 'Reagendar' : 'Agendar'} — {isCustomName ? (ag.nomeCustom || 'Curinga') : nome}</h3>
                <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: C.muted }}>{paciente.nome}</p>
              </div>
              <button onClick={() => setCalendarOpenFor(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: C.muted }}>✕</button>
            </div>
            <div style={{ padding: 20, background: C.bg }}>
              <WeeklyCalendarPicker
                disponibilidade={isCustomName ? CURINGA_DISPONIBILIDADE : (disponibilidade || [])}
                agendamentos={allAgendamentos.filter(a => a.profissionalId === profId && a.status !== 'cancelado' && a.id !== ag.docId)}
                selectedDate={ag.data}
                selectedTime={ag.hora}
                onSelect={(d, h) => { setAg(profId, 'data', d); setAg(profId, 'hora', h); setCalendarOpenFor(null); }}
              />
            </div>
          </Modal>
        )}
      </div>
    );
  };

  return (
    <Modal maxWidth={640} onClose={onClose}>
      <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', fontWeight: 800, color: C.text }}>📅 Agendamento de Avaliação</h3>
          <p style={{ margin: 0, fontSize: '0.83rem', color: C.muted }}>{paciente.nome} — {paciente.numero}</p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: C.muted }}>✕</button>
      </div>
      <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 520, overflowY: 'auto' }}>
        {profsDisponiveis.length === 0 && agendamentosExistentes.length === 0 && (
          <div style={{ background: '#fffbeb', borderRadius: 10, padding: 14, border: '1px solid #fde68a' }}>
            <p style={{ margin: 0, fontWeight: 700, color: '#92400e' }}>⚠️ Nenhum profissional cadastrado para esse tipo de paciente.</p>
            <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#a16207' }}>Use o Profissional Curinga abaixo ou cadastre profissionais em ⚙️ Config.</p>
          </div>
        )}
        {profsDisponiveis.map(p => renderProfCard(p.id, p.nome, p.funcao, p.disponibilidade, false))}
        {renderProfCard('curinga', 'Profissional Curinga', 'Avulso / não cadastrado', CURINGA_DISPONIBILIDADE, true)}
      </div>
      <div style={{ padding: '14px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={onClose} style={{ ...btnS(C.white, C.muted), border: `1px solid ${C.border}` }}>Fechar</button>
        <button onClick={handleSave} disabled={salvando} style={{ ...btnS('#22c55e', '#fff') }}>
          {salvando ? '⏳ Salvando...' : '💾 Salvar Agendamentos'}
        </button>
      </div>
    </Modal>
  );
}

// ── Kanban ─────────────────────────────────────────────────────────────────────
const KANBAN_ROW1 = STATUSES.slice(0, 4);   // Recebido → Avaliação Concluída
const KANBAN_ROW2 = STATUSES.slice(4);      // Ag. Retorno → Implantado

function parseDateAnchor(str) {
  if (!str) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(str);
}

function getImplantAnchor(p) {
  return p.implantadoEm || p.dataImplantacaoPrevista || null;
}

function getDaysElapsed(anchor) {
  const impl = parseDateAnchor(anchor);
  if (!impl || isNaN(impl)) return 0;
  const now    = new Date();
  const implDay = new Date(impl.getFullYear(), impl.getMonth(), impl.getDate());
  const nowDay  = new Date(now.getFullYear(),  now.getMonth(),  now.getDate());
  return Math.round((nowDay - implDay) / 86400000);
}

// Quando o paciente entrou na etapa atual — usa o último evento do histórico
// que bate com o status corrente (se nunca registrado, cai pra dataAtualizacao,
// depois criadoEm como último recurso)
function getEntradaNaEtapa(paciente) {
  const entries = (paciente.historico || []).filter(h => h.status === paciente.status);
  return (entries.length ? entries[entries.length - 1].timestamp : paciente.dataAtualizacao) || paciente.criadoEm || null;
}

const TIMER_STATUSES = ['Aguardando Retorno da Operadora', 'Abordagem à Família'];
function getTempoNaEtapa(paciente) {
  const inicio = getEntradaNaEtapa(paciente);
  if (!inicio) return null;
  const ms = Date.now() - new Date(inicio).getTime();
  if (isNaN(ms) || ms < 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const dias = Math.floor(totalMin / 1440);
  const horas = Math.floor((totalMin % 1440) / 60);
  const min = totalMin % 60;
  if (dias > 0) return `${dias}d ${horas}h`;
  if (horas > 0) return `${horas}h ${min}min`;
  return `${min}min`;
}

// Card em que a operadora pediu para desconsiderar o pedido (robô dos e-mails, 19/10): todo vermelho,
// letras brancas — fica no quadro até a equipe decidir (cancelar, excluir ou remover o aviso).
const CSS_DESCONSIDERADO = `
.cap-desconsiderado { background: #dc2626 !important; border-color: #991b1b !important; }
.cap-desconsiderado, .cap-desconsiderado * { color: #fff !important; }
.cap-desconsiderado span, .cap-desconsiderado div[style*="background"] { background: rgba(255,255,255,.18) !important; }
.cap-proposta { background: #facc15 !important; border-color: #ca8a04 !important; }
.cap-proposta, .cap-proposta * { color: #422006 !important; }
.cap-proposta span, .cap-proposta div[style*="background"] { background: rgba(255,255,255,.4) !important; }
.cap-autorizado { background: #16a34a !important; border-color: #166534 !important; }
.cap-autorizado, .cap-autorizado * { color: #fff !important; }
.cap-autorizado span, .cap-autorizado div[style*="background"] { background: rgba(255,255,255,.18) !important; }
`;

// Cor do card inteiro (20/10): vermelho = operadora pediu para desconsiderar; amarelo = proposta enviada
// à operadora (robô do e-mail); verde = operadora autorizou (robô do e-mail). Só na etapa correspondente.
function corDoCard(p) {
  if (p.desconsideradoPelaOperadora) return 'cap-desconsiderado';
  if (p.status === 'Aguardando Retorno da Operadora' && p.propostaEnviada?.em) return 'cap-proposta';
  if (p.status === 'Abordagem à Família' && p.autorizacaoOperadora?.em) return 'cap-autorizado';
  return undefined;
}
const fmtDiaHora = iso => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
function rotuloAutorizacao(a) {
  if (!a) return '';
  const extra = [a.tipo === 'parcial' && 'PARCIAL', a.dias && `POR ${a.dias} DIAS`, a.ate && `ATÉ ${a.ate}`, !a.dias && !a.ate && a.periodo && 'COM PERÍODO', a.liminar && 'LIMINAR'].filter(Boolean);
  return `AUTORIZADO${extra.length ? ` (${extra.join(' · ')})` : ''} ${fmtDiaHora(a.em)}`;
}

function KanbanColumn({ s, pacientes, onCardClick, isAdmin, onDelete }) {
  let cards = pacientes.filter(p => p.status === s.key);
  if (s.key === 'Implantado') {
    cards = cards.filter(p => getDaysElapsed(getImplantAnchor(p)) < 1);
  }
  if (s.key === 'Em Implantação' || s.key === 'Implantado') {
    cards = cards.slice().sort((a, b) => {
      const da = a.dataImplantacaoPrevista || a.implantadoEm || '';
      const db_ = b.dataImplantacaoPrevista || b.implantadoEm || '';
      return da > db_ ? -1 : da < db_ ? 1 : 0;
    });
  } else {
    // Mais antigo nesta etapa primeiro (topo), mais novo por último (embaixo)
    cards = cards.slice().sort((a, b) => (getEntradaNaEtapa(a) || '') < (getEntradaNaEtapa(b) || '') ? -1 : 1);
  }
  return (
    <div style={{ flex: '1 1 0', minWidth: 170, background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
      <style>{CSS_DESCONSIDERADO}</style>
      <div style={{ padding: '10px 14px', borderBottom: `3px solid ${s.color}`, background: `${s.color}10`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 700, fontSize: '0.82rem', color: s.color }}>{s.icon} {s.short}</span>
        <span style={{ background: s.color, color: '#fff', borderRadius: 20, padding: '1px 9px', fontSize: '0.72rem', fontWeight: 700 }}>{cards.length}</span>
      </div>
      <div style={{ padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {cards.length === 0 && <div style={{ textAlign: 'center', padding: '20px 0', color: '#cbd5e1', fontSize: '0.78rem' }}>Vazio</div>}
        {cards.map(p => {
          const slaActive = isSLAActive(p);
          const sla = slaActive ? getSLAInfo(p.dataSolicitacao || p.dataAbertura, getSLAHours(p)) : null;
          const tempoEtapa = TIMER_STATUSES.includes(s.key) ? getTempoNaEtapa(p) : null;
          return (
            <div key={p.id} onClick={() => onCardClick(p)} className={corDoCard(p)}
              title={p.desconsideradoPelaOperadora ? `A operadora pediu para DESCONSIDERAR este pedido: ${p.desconsideradoMotivo || ''}`
                : corDoCard(p) === 'cap-autorizado' ? `Autorizado pela operadora: ${p.autorizacaoOperadora?.texto || ''}` : undefined}
              style={{ background: C.white, borderRadius: 10, padding: '10px 12px', cursor: 'pointer', border: `1px solid ${C.border}`, borderLeft: `4px solid ${sla ? sla.color : C.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
                <div style={{ fontSize: '0.68rem', color: C.muted }}>{p.numIW || p.numero}</div>
                {isAdmin && <button onClick={(e) => { e.stopPropagation(); onDelete(p.id, p.nome); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }} title="Excluir">🗑️</button>}
              </div>
              {p.desconsideradoPelaOperadora && (
                <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '.03em', marginBottom: 3 }}>⛔ OPERADORA PEDIU PARA DESCONSIDERAR</div>
              )}
              {corDoCard(p) === 'cap-proposta' && (
                <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '.03em', marginBottom: 3 }}>📤 PROPOSTA ENVIADA {fmtDiaHora(p.propostaEnviada.em)}</div>
              )}
              {corDoCard(p) === 'cap-autorizado' && (
                <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '.03em', marginBottom: 3 }}>✅ {rotuloAutorizacao(p.autorizacaoOperadora)}</div>
              )}
              <div style={{ fontWeight: 700, fontSize: '0.98rem', color: C.text, marginBottom: 2, lineHeight: 1.3 }}>{p.nome}</div>
              <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: 2 }}>{p.convenio}</div>
              <InfoCadastroIW paciente={p} />
              {tempoEtapa && (
                <div style={{ fontSize: '0.68rem', color: '#92400e', fontWeight: 700, marginBottom: 2, background: '#fffbeb', borderRadius: 5, padding: '2px 6px', display: 'inline-block' }}>
                  ⏱ {tempoEtapa} nesta etapa
                </div>
              )}
              {(p.regiaoHospital || p.hospital) && (
                <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: p.dataImplantacaoPrevista ? 3 : 6,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={p.regiaoHospital || p.hospital}>
                  📍 {p.regiaoHospital || p.hospital}
                </div>
              )}
              {p.dataImplantacaoPrevista && (
                <div style={{ fontSize: '0.7rem', color: '#0891b2', fontWeight: 700, marginBottom: 5, background: '#ecfeff', borderRadius: 5, padding: '2px 6px', display: 'inline-block' }}>
                  📅 {p.dataImplantacaoPrevista.split('-').reverse().join('/')}{p.horaImplantacaoPrevista ? ` às ${p.horaImplantacaoPrevista}` : ''}
                </div>
              )}
              <TipoBadge paciente={p} />
              <div style={{ marginTop: 8 }}><SLABadge paciente={p} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Monitoramento (Tela 3) ──────────────────────────────────────────────────────
const MON_COLS = Array.from({ length: 10 }, (_, i) => ({
  dia: i + 1,
  label: `Dia ${i + 1}`,
  color: i < 3 ? '#10b981' : i < 7 ? '#f59e0b' : '#ef4444',
}));

function MonitoramentoCard({ p, dia, user }) {
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const diaStatus = p.monitoramentoDias?.[String(dia)] || null;

  const marcar = async (status) => {
    setSalvando(true);
    try {
      await updateDoc(doc(db, getCollectionName('captacao_pacientes'), p.id), {
        [`monitoramentoDias.${dia}`]: { status, por: user?.email || '', em: new Date().toISOString() },
        dataAtualizacao: serverTimestamp(),
      });
      setEditando(false);
    } catch (e) { console.error(e); }
    setSalvando(false);
  };

  const borderColor = diaStatus?.status === 'realizado' ? '#10b981' : diaStatus?.status === 'nao_realizado' ? '#ef4444' : '#94a3b8';

  return (
    <div style={{ background: C.white, borderRadius: 10, padding: '10px 12px', border: `1px solid ${C.border}`,
      borderLeft: `4px solid ${borderColor}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
        <div style={{ fontSize: '0.68rem', color: C.muted }}>{p.numero}</div>
        {diaStatus?.status && !editando && (
          <span style={{ fontSize: '0.65rem', fontWeight: 700,
            color: diaStatus.status === 'realizado' ? '#065f46' : '#991b1b',
            background: diaStatus.status === 'realizado' ? '#d1fae5' : '#fee2e2',
            borderRadius: 4, padding: '1px 6px' }}>
            {diaStatus.status === 'realizado' ? '✓ Realizado' : '✗ Não Realizado'}
          </span>
        )}
      </div>
      <div style={{ fontWeight: 700, fontSize: '0.84rem', color: C.text, marginBottom: 2 }}>{p.nome}</div>
      <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: 4 }}>{p.convenio}</div>
      {getImplantAnchor(p) && (
        <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: 6 }}>
          Implantado: {parseDateAnchor(getImplantAnchor(p))?.toLocaleDateString('pt-BR') || ''}
        </div>
      )}
      {(!diaStatus?.status || editando) ? (
        <div style={{ display: 'flex', gap: 4 }}>
          <button disabled={salvando} onClick={() => marcar('realizado')}
            style={{ flex: 1, background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 6, padding: '4px 0',
              cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, color: '#065f46' }}>
            ✓ Realizado
          </button>
          <button disabled={salvando} onClick={() => marcar('nao_realizado')}
            style={{ flex: 1, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 0',
              cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, color: '#991b1b' }}>
            ✗ Não Real.
          </button>
        </div>
      ) : (
        <button onClick={() => setEditando(true)}
          style={{ width: '100%', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 0',
            cursor: 'pointer', fontSize: '0.68rem', color: C.muted }}>
          Alterar
        </button>
      )}
    </div>
  );
}

function MonitoramentoKanban({ pacientes, user }) {
  const monPacientes = pacientes.filter(p => {
    if (p.status !== 'Implantado') return false;
    const anchor = getImplantAnchor(p);
    if (!anchor) return false;
    const d = getDaysElapsed(anchor);
    return d >= 1 && d <= 10;
  });

  return (
    <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
      <div style={{ display: 'flex', gap: 8, minWidth: MON_COLS.length * 185 }}>
        {MON_COLS.map(col => {
          const cards = monPacientes
            .filter(p => Math.min(Math.max(getDaysElapsed(getImplantAnchor(p)), 1), 10) === col.dia)
            .sort((a, b) => {
              const da = getImplantAnchor(a) || '';
              const db_ = getImplantAnchor(b) || '';
              return da > db_ ? -1 : da < db_ ? 1 : 0;
            });
          return (
            <div key={col.dia} style={{ flex: '1 1 0', minWidth: 175, background: C.bg, borderRadius: 12, border: `1px solid ${C.border}` }}>
              <div style={{ padding: '10px 14px', borderBottom: `3px solid ${col.color}`, background: `${col.color}10`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: '0.78rem', color: col.color }}>📋 {col.label}</span>
                <span style={{ background: col.color, color: '#fff', borderRadius: 20, padding: '1px 9px', fontSize: '0.72rem', fontWeight: 700 }}>{cards.length}</span>
              </div>
              <div style={{ padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {cards.length === 0 && <div style={{ textAlign: 'center', padding: '20px 0', color: '#cbd5e1', fontSize: '0.78rem' }}>Vazio</div>}
                {cards.map(p => <MonitoramentoCard key={p.id} p={p} dia={col.dia} user={user} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tela 4 — Grid Implantados ──────────────────────────────────────────────────
function ImplantadosGridView({ pacientes }) {
  const now = new Date();
  const mesAtual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [filtroMes, setFiltroMes] = useState(mesAtual);
  const [filtroFilial, setFiltroFilial] = useState('');

  const todos = pacientes.filter(p => p.status === 'Implantado');
  const filiais = [...new Set(todos.map(p => p.filial).filter(Boolean))].sort();

  const filtrados = todos.filter(p => {
    const anchor = getImplantAnchor(p);
    if (filtroMes) {
      const d = parseDateAnchor(anchor);
      if (!d || isNaN(d)) return false;
      const mes = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (mes !== filtroMes) return false;
    }
    if (filtroFilial && p.filial !== filtroFilial) return false;
    return true;
  }).sort((a, b) => {
    const da = getImplantAnchor(a) || '';
    const db_ = getImplantAnchor(b) || '';
    return da > db_ ? -1 : da < db_ ? 1 : 0;
  });

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, padding: '10px 14px', background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Filtros</span>
        <div style={{ width: 1, height: 20, background: C.border }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: C.muted }}>Mês</label>
          <input type="month" value={filtroMes} onChange={e => setFiltroMes(e.target.value)}
            style={{ border: `1px solid ${filtroMes !== mesAtual ? C.primary : C.border}`, borderRadius: 7, padding: '5px 8px', fontSize: '0.8rem', outline: 'none' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: C.muted }}>Filial</label>
          <select value={filtroFilial} onChange={e => setFiltroFilial(e.target.value)}
            style={{ border: `1px solid ${filtroFilial ? C.primary : C.border}`, borderRadius: 7, padding: '5px 10px', fontSize: '0.8rem', color: filtroFilial ? C.primary : C.text, background: filtroFilial ? '#eef2ff' : '#f8fafc', fontWeight: filtroFilial ? 700 : 400, outline: 'none' }}>
            <option value="">Todas</option>
            {filiais.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        {(filtroMes !== mesAtual || filtroFilial) && (
          <button onClick={() => { setFiltroMes(mesAtual); setFiltroFilial(''); }}
            style={{ marginLeft: 'auto', background: 'none', border: '1px solid #fca5a5', borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: '0.78rem', color: '#ef4444', fontWeight: 600 }}>
            ✕ Limpar
          </button>
        )}
        <span style={{ marginLeft: filtroMes === mesAtual && !filtroFilial ? 'auto' : 0, fontSize: '0.78rem', color: C.muted, fontWeight: 600 }}>{filtrados.length} paciente{filtrados.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Grid */}
      {filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: C.muted }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>📋</div>
          <p style={{ margin: 0 }}>Nenhum paciente implantado no período selecionado</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
          {filtrados.map(p => {
            const anchor = getImplantAnchor(p);
            const days = getDaysElapsed(anchor);
            const implDate = parseDateAnchor(anchor);
            const monBadges = Array.from({ length: 10 }, (_, i) => {
              const dia = i + 1;
              const s = p.monitoramentoDias?.[String(dia)];
              return { dia, status: s?.status || null };
            });
            return (
              <div key={p.id} style={{ background: C.white, borderRadius: 12, padding: '12px 14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.68rem', color: C.muted, fontWeight: 600 }}>{p.numero}</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, background: '#d1fae5', color: '#065f46', borderRadius: 6, padding: '1px 7px' }}>
                    {days <= 0 ? 'Dia 0' : days <= 10 ? `Dia ${days}` : `${days}d`}
                  </span>
                </div>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: C.text, marginBottom: 2, lineHeight: 1.3 }}>{p.nome}</div>
                <div style={{ fontSize: '0.75rem', color: C.muted, marginBottom: 2 }}>{p.convenio}</div>
                {p.filial && <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 6 }}>{nf(p.filial)}</div>}
                {implDate && (
                  <div style={{ fontSize: '0.72rem', color: '#0891b2', fontWeight: 700, marginBottom: 8 }}>
                    📅 {implDate.toLocaleDateString('pt-BR')}{p.horaImplantacaoPrevista ? ` às ${p.horaImplantacaoPrevista}` : ''}
                  </div>
                )}
                {/* Monitoramento badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {monBadges.map(({ dia, status }) => (
                    <span key={dia} title={`Dia ${dia}${status ? ': ' + (status === 'realizado' ? 'Realizado' : 'Não Realizado') : ': Pendente'}`}
                      style={{ fontSize: '0.62rem', fontWeight: 700, padding: '2px 5px', borderRadius: 4,
                        background: status === 'realizado' ? '#d1fae5' : status === 'nao_realizado' ? '#fee2e2' : '#f1f5f9',
                        color: status === 'realizado' ? '#065f46' : status === 'nao_realizado' ? '#991b1b' : '#94a3b8',
                        border: `1px solid ${status === 'realizado' ? '#6ee7b7' : status === 'nao_realizado' ? '#fca5a5' : '#e2e8f0'}` }}>
                      {dia}{status === 'realizado' ? '✓' : status === 'nao_realizado' ? '✗' : '·'}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KanbanBoard({ pacientes, onCardClick, isAdmin, onDelete, stats, user }) {
  const [tela, setTela] = useState(1);
  const row = tela === 1 ? KANBAN_ROW1 : KANBAN_ROW2;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 16 }}>
      {/* Seletor de tela + KPIs compactos (quando stats é passado = modo painel) */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { n: 1, label: 'Tela 1', color: '#6366f1' },
          { n: 2, label: 'Tela 2', color: '#6366f1' },
          { n: 3, label: 'Tela 3 — Monitoramento', color: '#0891b2' },
          { n: 4, label: 'Tela 4 — Implantados', color: '#22c55e' },
        ].map(({ n, label, color }) => (
          <button key={n} onClick={() => setTela(n)} style={{
            padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontWeight: 700,
            fontSize: '0.8rem', transition: 'all 0.15s',
            background: tela === n ? color : '#e2e8f0',
            color: tela === n ? '#fff' : '#64748b',
            boxShadow: tela === n ? `0 2px 8px ${color}55` : 'none',
          }}>
            {label}
          </button>
        ))}
        <span style={{ fontSize: '0.75rem', color: '#94a3b8', marginLeft: 4 }}>
          {tela === 1 ? 'Recebido → Aval. Concluída' : tela === 2 ? 'Ag. Retorno → Implantado' : tela === 3 ? 'Monitoramento Dias 1–10' : 'Grid implantados por mês'}
        </span>
        {stats && (
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            {[
              { label: 'Total', value: stats.total, color: C.primary },
              { label: 'Em andamento', value: stats.abertos, color: '#0ea5e9' },
              { label: '🔴 Críticos', value: stats.criticos, color: '#ef4444' },
            ].map(kpi => (
              <div key={kpi.label} style={{ display: 'flex', alignItems: 'center', gap: 5, background: `${kpi.color}12`, border: `1px solid ${kpi.color}35`, borderRadius: 20, padding: '4px 14px', fontSize: '0.78rem', fontWeight: 700, color: kpi.color, whiteSpace: 'nowrap' }}>
                <span>{kpi.value}</span>
                <span style={{ fontWeight: 500, color: '#94a3b8', fontSize: '0.7rem' }}>{kpi.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Conteúdo da tela ativa */}
      {tela === 3 ? (
        <MonitoramentoKanban pacientes={pacientes} user={user} />
      ) : tela === 4 ? (
        <ImplantadosGridView pacientes={pacientes} />
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          {row.map(s => <KanbanColumn key={s.key} s={s} pacientes={pacientes} onCardClick={onCardClick} isAdmin={isAdmin} onDelete={onDelete} />)}
        </div>
      )}
    </div>
  );
}

// ── Painel Setor Captação (Prévias + Ocorrências) ─────────────────────────────
const PREVIAS_STATUSES = ['Aguardando Retorno da Operadora','Abordagem à Família'];

function PreviaSetorPanel({ pacientes, userEmail }) {
  const [previas, setPrevias] = useState([]);
  const [selected, setSelected] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [novoComentario, setNovoComentario] = useState('');
  const [confirmacoes, setConfirmacoes] = useState({});

  useEffect(() => {
    const pacs = pacientes.filter(p => PREVIAS_STATUSES.includes(p.status));
    if (!pacs.length) { setPrevias([]); return; }
    Promise.all(pacs.map(async p => {
      try {
        const snap = await getDoc(doc(db, getCollectionName('captacao_previas'), p.id));
        return { paciente: p, previa: snap.exists() ? snap.data() : null };
      } catch { return { paciente: p, previa: null }; }
    })).then(results => setPrevias(results));
  }, [pacientes]);

  const openPrevia = (item) => {
    setSelected(item);
    setNovoComentario('');
    const conf = {};
    Object.entries(item.previa?.confirmacoes || {}).forEach(([k, v]) => { conf[k] = { ...v }; });
    setConfirmacoes(conf);
  };

  const salvarConfirmacao = async (area) => {
    if (!selected?.previa) return;
    setSalvando(true);
    try {
      const novasConf = { ...(selected.previa.confirmacoes || {}), [area]: { ...(confirmacoes[area] || {}), confirmedBy: userEmail, confirmedAt: new Date().toISOString() } };
      await updateDoc(doc(db, getCollectionName('captacao_previas'), selected.paciente.id), { confirmacoes: novasConf, atualizadoEm: serverTimestamp() });
      setSelected(prev => ({ ...prev, previa: { ...prev.previa, confirmacoes: novasConf } }));
      setPrevias(prev => prev.map(p => p.paciente.id === selected.paciente.id ? { ...p, previa: { ...p.previa, confirmacoes: novasConf } } : p));
    } catch (e) { alert('Erro: ' + e.message); }
    setSalvando(false);
  };

  const enviarComentario = async () => {
    const txt = novoComentario.trim();
    if (!txt || !selected?.previa) return;
    setSalvando(true);
    try {
      const comentario = { id: Date.now().toString(), texto: txt, autorEmail: userEmail, criadoEm: new Date().toISOString() };
      const novosComentarios = [...(selected.previa.comentarios || []), comentario];
      await updateDoc(doc(db, getCollectionName('captacao_previas'), selected.paciente.id), { comentarios: novosComentarios, atualizadoEm: serverTimestamp() });
      setSelected(prev => ({ ...prev, previa: { ...prev.previa, comentarios: novosComentarios } }));
      setPrevias(prev => prev.map(p => p.paciente.id === selected.paciente.id ? { ...p, previa: { ...p.previa, comentarios: novosComentarios } } : p));
      setNovoComentario('');
    } catch (e) { alert('Erro: ' + e.message); }
    setSalvando(false);
  };

  const statusColor = { 'Aguardando Retorno da Operadora': '#64748b', 'Abordagem à Família': '#f43f5e' };

  return (
    <div>
      {previas.length === 0 ? (
        <div style={{ textAlign:'center', padding:'48px 0', color:C.muted }}>
          <div style={{ fontSize:'2rem', marginBottom:8 }}>📋</div>
          <p style={{ margin:0, fontWeight:600 }}>Nenhuma prévia ativa no momento</p>
          <p style={{ margin:'4px 0 0', fontSize:'0.82rem' }}>As prévias aparecem quando pacientes estão em Envio, Ag. Retorno ou Abord. Família</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {previas.map(({ paciente: pac, previa }) => {
            const sColor = statusColor[pac.status] || C.muted;
            const areasAtivas = previa ? Object.entries(previa.areas || {}).filter(([,v])=>typeof v==='object'?v.enabled:v).map(([a])=>a) : [];
            const pads = previa ? CAP_PAD_DISCIPLINES.filter(d => previa.pad?.[d.key]?.enabled).map(d => d.label) : [];
            const todasAreas = [...pads.map(p=>`PAD - ${p}`), ...areasAtivas];
            const confirmadas = todasAreas.filter(a => previa?.confirmacoes?.[a]?.dataPrevisata).length;
            return (
              <div key={pac.id} onClick={() => previa && openPrevia({ paciente: pac, previa })}
                style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, borderLeft:`4px solid ${sColor}`, padding:'14px 18px', cursor:previa?'pointer':'default', display:'flex', alignItems:'center', gap:16, transition:'box-shadow 0.15s' }}
                onMouseEnter={e=>{ if(previa) e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                    <span style={{ fontWeight:800, fontSize:'0.92rem', color:C.text }}>{pac.nome}</span>
                    <span style={{ fontSize:'0.7rem', fontWeight:700, color:sColor, background:`${sColor}18`, padding:'2px 8px', borderRadius:20 }}>{pac.status}</span>
                  </div>
                  <div style={{ fontSize:'0.78rem', color:C.muted, marginBottom:6 }}>{pac.convenio} · {pac.numero}</div>
                  {previa ? (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      {todasAreas.slice(0, 5).map(a => {
                        const conf = previa.confirmacoes?.[a];
                        return (
                          <span key={a} style={{ fontSize:'0.68rem', fontWeight:700, padding:'2px 8px', borderRadius:20, background:conf?.dataPrevisata?'#dcfce7':'#f1f5f9', color:conf?.dataPrevisata?'#15803d':'#64748b', border:`1px solid ${conf?.dataPrevisata?'#86efac':'#e2e8f0'}` }}>
                            {conf?.dataPrevisata ? '✅ ' : ''}{a}
                          </span>
                        );
                      })}
                      {todasAreas.length > 5 && <span style={{ fontSize:'0.68rem', color:C.muted }}>+{todasAreas.length - 5}</span>}
                    </div>
                  ) : <span style={{ fontSize:'0.75rem', color:'#f59e0b' }}>⚠️ Sem prévia — não passou pela etapa de Envio</span>}
                </div>
                {previa && todasAreas.length > 0 && (
                  <div style={{ textAlign:'center', minWidth:56 }}>
                    <div style={{ fontWeight:800, fontSize:'1.2rem', color:confirmadas===todasAreas.length?'#15803d':C.primary }}>{confirmadas}/{todasAreas.length}</div>
                    <div style={{ fontSize:'0.65rem', color:C.muted, fontWeight:600 }}>confirmados</div>
                  </div>
                )}
                {previa && <span style={{ color:C.muted, fontSize:'1rem' }}>›</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de detalhe da prévia */}
      {selected && (
        <Modal maxWidth={680} onClose={() => setSelected(null)}>
          <div style={{ padding:'16px 20px 12px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <h3 style={{ margin:'0 0 2px', fontSize:'1.05rem', fontWeight:800, color:C.text }}>{selected.paciente.nome}</h3>
              <div style={{ fontSize:'0.78rem', color:C.muted }}>{selected.paciente.convenio} · {selected.paciente.status}</div>
            </div>
            <button onClick={()=>setSelected(null)} style={{ background:'none', border:'none', fontSize:'1.2rem', cursor:'pointer', color:C.muted }}>✕</button>
          </div>
          <div style={{ padding:'16px 20px', maxHeight:560, overflowY:'auto', display:'flex', flexDirection:'column', gap:16 }}>
            {/* Resumo */}
            {(selected.previa.dataImplantacao) && (
              <div style={{ background:'#f0fdf4', borderRadius:8, padding:'10px 14px', border:'1px solid #bbf7d0', fontSize:'0.82rem', color:'#15803d', fontWeight:700 }}>
                📅 Data prevista: {selected.previa.dataImplantacao.split('-').reverse().join('/')}{selected.previa.horaImplantacao ? ` às ${selected.previa.horaImplantacao}` : ''}
              </div>
            )}

            {/* Áreas / confirmações */}
            {(() => {
              const todasAreas = [
                ...CAP_PAD_DISCIPLINES.filter(d=>selected.previa.pad?.[d.key]?.enabled).map(d=>`PAD - ${d.label}`),
                ...Object.entries(selected.previa.areas||{}).filter(([,v])=>typeof v==='object'?v.enabled:v).map(([a])=>a),
              ];
              if (todasAreas.length === 0) return <p style={{ color:C.muted, fontSize:'0.82rem' }}>Nenhuma área selecionada na prévia.</p>;
              return (
                <div>
                  <p style={{ margin:'0 0 10px', fontWeight:700, fontSize:'0.82rem', color:C.muted, textTransform:'uppercase' }}>Setores / Confirmações</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {todasAreas.map(area => {
                      const conf = confirmacoes[area] || {};
                      const salvo = selected.previa.confirmacoes?.[area];
                      return (
                        <div key={area} style={{ background:C.bg, borderRadius:8, padding:'10px 14px', border:`1px solid ${salvo?.dataPrevisata?'#86efac':C.border}` }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                            <span style={{ fontWeight:700, fontSize:'0.84rem', color:C.text, flex:1 }}>{area}</span>
                            {salvo?.dataPrevisata && <span style={{ fontSize:'0.7rem', color:'#15803d', fontWeight:700 }}>✅ {salvo.dataPrevisata.split('-').reverse().join('/')} · {salvo.confirmedBy}</span>}
                          </div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, alignItems:'end' }}>
                            <div>
                              <label style={{ fontSize:'0.7rem', fontWeight:700, color:C.muted, display:'block', marginBottom:4 }}>Data prevista de chegada</label>
                              <input type="date" value={conf.dataPrevisata||''} onChange={e=>setConfirmacoes(prev=>({...prev,[area]:{...prev[area],dataPrevisata:e.target.value}}))}
                                style={{ ...{ padding:'6px 8px', border:`1px solid ${C.border}`, borderRadius:6, fontSize:'0.82rem', width:'100%', boxSizing:'border-box' } }} />
                            </div>
                            <div>
                              <label style={{ fontSize:'0.7rem', fontWeight:700, color:C.muted, display:'block', marginBottom:4 }}>Observação</label>
                              <input value={conf.obs||''} onChange={e=>setConfirmacoes(prev=>({...prev,[area]:{...prev[area],obs:e.target.value}}))} placeholder="Ex: Cama chega quinta..."
                                style={{ padding:'6px 8px', border:`1px solid ${C.border}`, borderRadius:6, fontSize:'0.82rem', width:'100%', boxSizing:'border-box' }} />
                            </div>
                          </div>
                          <button onClick={()=>salvarConfirmacao(area)} disabled={salvando||!conf.dataPrevisata}
                            style={{ marginTop:8, background:conf.dataPrevisata?'#22c55e':'#e2e8f0', color:conf.dataPrevisata?'#fff':'#94a3b8', border:'none', borderRadius:6, padding:'5px 12px', cursor:conf.dataPrevisata?'pointer':'default', fontWeight:700, fontSize:'0.75rem' }}>
                            {salvando?'Salvando...':'✔ Confirmar'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Comentários */}
            <div>
              <p style={{ margin:'0 0 10px', fontWeight:700, fontSize:'0.82rem', color:C.muted, textTransform:'uppercase' }}>Comentários</p>
              <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:10 }}>
                {(selected.previa.comentarios||[]).length === 0 && <p style={{ fontSize:'0.8rem', color:C.muted, margin:0 }}>Nenhum comentário ainda.</p>}
                {(selected.previa.comentarios||[]).map(c => (
                  <div key={c.id} style={{ background:C.bg, borderRadius:8, padding:'8px 12px', border:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:'0.7rem', color:C.muted, marginBottom:4 }}>{c.autorEmail} · {c.criadoEm ? new Date(c.criadoEm).toLocaleString('pt-BR') : ''}</div>
                    <div style={{ fontSize:'0.84rem', color:C.text }}>{c.texto}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <input value={novoComentario} onChange={e=>setNovoComentario(e.target.value)} placeholder="Adicionar comentário..."
                  onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); enviarComentario(); } }}
                  style={{ flex:1, padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:'0.84rem' }} />
                <button onClick={enviarComentario} disabled={salvando||!novoComentario.trim()}
                  style={{ background:C.primary, color:'#fff', border:'none', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontWeight:700, fontSize:'0.82rem', opacity:novoComentario.trim()?1:0.5 }}>
                  Enviar
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Novo Agendamento Manual ───────────────────────────────────────────────────
function NovoAgendamentoManualModal({ profissionais, pacientes, allAgendamentos, onClose }) {
  const [nomePaciente, setNomePaciente] = useState('');
  const [convenio, setConvenio] = useState('');
  const [searchPac, setSearchPac] = useState('');
  const [profId, setProfId] = useState('');
  const [nomeCustom, setNomeCustom] = useState('');
  const [data, setData] = useState('');
  const [hora, setHora] = useState('');
  const [tipoAgenda, setTipoAgenda] = useState('avaliacao');
  const [showCal, setShowCal] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const profsAtivos = profissionais.filter(p => p.status === 'Ativo');
  const isCuringa = profId === 'curinga';
  const profSel = profsAtivos.find(p => p.id === profId);
  const disponibilidade = isCuringa ? CURINGA_DISPONIBILIDADE : (profSel?.disponibilidade || []);

  const pacientesFiltrados = searchPac.length >= 2
    ? pacientes.filter(p => p.nome?.toLowerCase().includes(searchPac.toLowerCase())).slice(0, 6)
    : [];

  const handleSelectPaciente = (p) => {
    setNomePaciente(p.nome);
    setConvenio(p.convenio || '');
    setSearchPac('');
  };

  const handleSave = async () => {
    const nomeF = nomePaciente.trim();
    if (!nomeF) { alert('Informe o nome do paciente'); return; }
    if (!profId) { alert('Selecione um profissional'); return; }
    if (isCuringa && !nomeCustom.trim()) { alert('Informe o nome do profissional curinga'); return; }
    if (!data) { alert('Selecione a data'); return; }
    setSalvando(true);
    try {
      const dadosPac = { pacienteId: '', pacienteNome: nomeF, pacienteNumero: '', convenio: convenio.trim(), hospital: '', pacienteFilial: '' };
      const agBase = { ...dadosPac, data, hora: hora || 'livre', status: 'agendado', tipoAgenda, criadoEm: serverTimestamp() };
      if (isCuringa) {
        await addDoc(collection(db, getCollectionName('captacao_agendamentos')), { ...agBase, profissionalId: 'curinga', profissionalNome: nomeCustom.trim(), profissionalFuncao: 'Curinga', profissionalEmail: '', isCuringa: true });
      } else {
        await addDoc(collection(db, getCollectionName('captacao_agendamentos')), { ...agBase, profissionalId: profSel.id, profissionalNome: profSel.nome, profissionalFuncao: profSel.funcao, profissionalEmail: profSel.email || '' });
      }
      onClose();
    } catch (e) {
      alert('Erro ao salvar: ' + e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal maxWidth={580} onClose={onClose}>
      <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: C.text }}>📅 Novo Agendamento Manual</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: C.muted }}>✕</button>
      </div>
      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14, maxHeight: 540, overflowY: 'auto' }}>
        {/* Tipo */}
        <div>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Tipo de Agendamento</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ v: 'avaliacao', l: '📋 Avaliação' }, { v: 'implantacao', l: '🔧 Implantação' }].map(t => (
              <button key={t.v} onClick={() => setTipoAgenda(t.v)}
                style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `2px solid ${tipoAgenda === t.v ? C.primary : C.border}`, background: tipoAgenda === t.v ? `${C.primary}12` : C.white, color: tipoAgenda === t.v ? C.primary : C.muted, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.15s' }}>
                {t.l}
              </button>
            ))}
          </div>
        </div>

        {/* Paciente */}
        <div style={{ position: 'relative' }}>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Paciente</label>
          <input value={nomePaciente} onChange={e => { setNomePaciente(e.target.value); setSearchPac(e.target.value); }}
            placeholder="Nome do paciente ou busque pela lista..." style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          {pacientesFiltrados.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 200, overflow: 'hidden', marginTop: 2 }}>
              {pacientesFiltrados.map(p => (
                <div key={p.id} onClick={() => handleSelectPaciente(p)} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, fontSize: '0.84rem', color: C.text, display: 'flex', gap: 8, alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                  onMouseLeave={e => e.currentTarget.style.background = C.white}>
                  <span style={{ fontWeight: 700 }}>{p.nome}</span>
                  {p.convenio && <span style={{ fontSize: '0.72rem', color: C.muted }}>· {p.convenio}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Convênio */}
        <div>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Convênio (opcional)</label>
          <input value={convenio} onChange={e => setConvenio(e.target.value)} placeholder="Ex: Unimed, SulAmérica..." style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
        </div>

        {/* Profissional */}
        <div>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Profissional</label>
          <select value={profId} onChange={e => { setProfId(e.target.value); setData(''); setHora(''); }}
            style={{ ...inp, width: '100%', boxSizing: 'border-box', cursor: 'pointer' }}>
            <option value="">Selecione o profissional...</option>
            {profsAtivos.map(p => <option key={p.id} value={p.id}>{p.nome} — {p.funcao}</option>)}
            <option value="curinga">⭐ Profissional Curinga (avulso)</option>
          </select>
          {isCuringa && (
            <input value={nomeCustom} onChange={e => setNomeCustom(e.target.value)}
              placeholder="Nome do profissional curinga..." style={{ ...inp, width: '100%', boxSizing: 'border-box', marginTop: 8 }} />
          )}
        </div>

        {/* Data/Hora */}
        <div>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Data e Horário</label>
          {data ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px' }}>
              <span style={{ fontWeight: 800, color: '#15803d', fontSize: '0.9rem' }}>✅ {data.split('-').reverse().join('/')} {hora && hora !== 'livre' ? `às ${hora}` : ''}</span>
              <button onClick={() => setShowCal(true)} style={{ marginLeft: 'auto', background: C.white, border: `1px solid ${C.border}`, borderRadius: 7, padding: '4px 10px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: C.primary }}>Alterar</button>
            </div>
          ) : (
            <button onClick={() => { if (!profId) { alert('Selecione um profissional primeiro'); return; } setShowCal(true); }}
              style={{ width: '100%', padding: '10px', borderRadius: 8, border: `2px dashed ${C.border}`, background: C.bg, color: C.muted, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
              📅 Selecionar Data e Hora
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '14px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={onClose} style={{ ...btnS(C.white, C.muted), border: `1px solid ${C.border}` }}>Cancelar</button>
        <button onClick={handleSave} disabled={salvando} style={{ ...btnS('#22c55e', '#fff') }}>
          {salvando ? '⏳ Salvando...' : '💾 Salvar Agendamento'}
        </button>
      </div>

      {showCal && (
        <Modal maxWidth={900} onClose={() => setShowCal(false)}>
          <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800 }}>📅 Selecionar Data</h3>
            <button onClick={() => setShowCal(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: C.muted }}>✕</button>
          </div>
          <div style={{ padding: 20, background: C.bg }}>
            <WeeklyCalendarPicker
              disponibilidade={disponibilidade}
              agendamentos={allAgendamentos.filter(a => a.profissionalId === profId && a.status !== 'cancelado')}
              selectedDate={data}
              selectedTime={hora}
              onSelect={(d, h) => { setData(d); setHora(h); setShowCal(false); }}
            />
          </div>
        </Modal>
      )}
    </Modal>
  );
}

// ── Agendas View (gestão) ─────────────────────────────────────────────────────
function AgendasView({ agendamentos, profissionais, pacientes, onConfirmar, isAdmin, onDelete }) {
  const [filtroPro, setFiltroPro] = useState('');
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' ou 'weekly'
  const [currentDate, setCurrentDate] = useState(new Date());

  const [selectedEvent, setSelectedEvent] = useState(null); // Para o modal de detalhe
  const [confirming, setConfirming] = useState(null);
  const [editingAg, setEditingAg] = useState(null);
  const [showNovoAg, setShowNovoAg] = useState(false);

  const filtered = agendamentos.filter(a => {
    if (filtroPro && a.profissionalId !== filtroPro) return false;
    return true;
  });

  // Funções de Navegação
  const handlePrev = () => {
    if (viewMode === 'monthly') setCurrentDate(subMonths(currentDate, 1));
    else setCurrentDate(subWeeks(currentDate, 1));
  };
  const handleNext = () => {
    if (viewMode === 'monthly') setCurrentDate(addMonths(currentDate, 1));
    else setCurrentDate(addWeeks(currentDate, 1));
  };
  const handleToday = () => setCurrentDate(new Date());

  // Gerar Grid
  const daysInGrid = useMemo(() => {
    if (viewMode === 'monthly') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(monthStart);
      const startDate = startOfWeek(monthStart);
      const endDate = endOfWeek(monthEnd);
      return eachDayOfInterval({ start: startDate, end: endDate });
    } else {
      const startDate = startOfWeek(currentDate);
      const endDate = endOfWeek(currentDate);
      return eachDayOfInterval({ start: startDate, end: endDate });
    }
  }, [currentDate, viewMode]);

  const statusColor = { agendado:'#f59e0b', confirmado:'#22c55e', cancelado:'#ef4444' };
  const statusLabel = { agendado:'⏳ Aguardando', confirmado:'✅ Confirmado', cancelado:'❌ Cancelado' };
  const tipoAgendaColor = { avaliacao:'#3b82f6', implantacao:'#0891b2' };
  const tipoAgendaLabel = { avaliacao:'📋 Avaliação', implantacao:'🔧 Implantação' };

  return (
    <div>
      {/* Barra superior do calendário */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'#fff', padding:'12px 16px', borderRadius:10, border:`1px solid ${C.border}`, marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ display:'flex', background:'#f8fafc', borderRadius:8, overflow:'hidden', border:`1px solid ${C.border}` }}>
            <button onClick={handlePrev} style={{ border:'none', background:'none', padding:'6px 12px', cursor:'pointer', borderRight:`1px solid ${C.border}`, color:C.muted, fontWeight:700 }}>&lt;</button>
            <button onClick={handleToday} style={{ border:'none', background:'none', padding:'6px 14px', cursor:'pointer', borderRight:`1px solid ${C.border}`, color:C.text, fontWeight:700 }}>Hoje</button>
            <button onClick={handleNext} style={{ border:'none', background:'none', padding:'6px 12px', cursor:'pointer', color:C.muted, fontWeight:700 }}>&gt;</button>
          </div>
          <h2 style={{ margin:0, fontSize:'1.2rem', fontWeight:800, color:C.text, textTransform:'capitalize' }}>
            {viewMode === 'monthly' ? format(currentDate, 'MMMM yyyy', { locale: ptBR }) : `Semana de ${format(startOfWeek(currentDate), 'dd/MM')} a ${format(endOfWeek(currentDate), 'dd/MM')}`}
          </h2>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <label style={{ fontSize:'0.78rem', fontWeight:600, color:C.muted }}>👤 Filtro</label>
            <select value={filtroPro} onChange={e=>setFiltroPro(e.target.value)} style={{ border:`1px solid ${filtroPro?C.primary:C.border}`, borderRadius:7, padding:'6px 10px', fontSize:'0.8rem', background:filtroPro?'#eef2ff':'#f8fafc', color:filtroPro?C.primary:C.text, outline:'none', cursor:'pointer' }}>
              <option value="">Todos os Profissionais</option>
              {profissionais.filter(p=>p.status==='Ativo').map(p=><option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <button onClick={() => setShowNovoAg(true)}
            style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            + Novo Agendamento
          </button>
          <div style={{ display:'flex', background:'#f1f5f9', borderRadius:8, padding:3 }}>
            {[{v:'monthly',l:'Mês'},{v:'weekly',l:'Semana'}].map(m=>(
              <button key={m.v} onClick={()=>setViewMode(m.v)}
                style={{ border:'none', borderRadius:6, padding:'4px 12px', cursor:'pointer', fontSize:'0.75rem', fontWeight:700, background:viewMode===m.v?'#fff':'transparent', color:viewMode===m.v?C.primary:'#94a3b8', boxShadow:viewMode===m.v?'0 1px 3px rgba(0,0,0,0.1)':'none', transition:'all 0.15s' }}>
                {m.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Legenda */}
      <div style={{ display:'flex', gap:14, alignItems:'center', marginBottom:10, paddingLeft:4, flexWrap:'wrap' }}>
        {[
          { bg:'#fef9c3', aba:'#f59e0b', label:'Avaliação pendente' },
          { bg:'#dcfce7', aba:'#16a34a', label:'Avaliação concluída' },
          { bg:'#dbeafe', aba:'#22c55e', label:'Implantação' },
          { bg:'#f1f5f9', aba:'#94a3b8', label:'Cancelado' },
        ].map(l => (
          <div key={l.label} style={{ display:'flex', alignItems:'center', gap:5 }}>
            <div style={{ width:14, height:14, borderRadius:3, background:l.bg, borderLeft:`3px solid ${l.aba}` }} />
            <span style={{ fontSize:'0.72rem', color:C.muted, fontWeight:600 }}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Cabeçalho dos dias da semana */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:8, marginBottom:8 }}>
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
          <div key={day} style={{ textAlign:'center', fontWeight:700, fontSize:'0.8rem', color:C.muted, textTransform:'uppercase' }}>{day}</div>
        ))}
      </div>

      {/* Grade do Calendário */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:8, gridAutoRows: viewMode === 'monthly' ? 'minmax(110px, auto)' : 'minmax(200px, auto)' }}>
        {daysInGrid.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const isCurrMonth = isSameMonth(day, currentDate);
          const isTodayDate = isToday(day);
          const dayEvents = filtered.filter(a => a.data === dateStr).sort((a,b) => a.hora.localeCompare(b.hora));

          return (
            <div key={dateStr} style={{ background: isCurrMonth ? '#fff' : '#f8fafc', border:`1px solid ${isTodayDate ? C.primary : C.border}`, borderRadius:10, padding:8, display:'flex', flexDirection:'column', opacity: isCurrMonth ? 1 : 0.6 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <span style={{ fontSize:'0.85rem', fontWeight:800, color: isTodayDate ? '#fff' : (isCurrMonth ? C.text : C.muted), background: isTodayDate ? C.primary : 'transparent', borderRadius:'50%', width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {format(day, 'd')}
                </span>
                {dayEvents.length > 0 && <span style={{ fontSize:'0.65rem', fontWeight:700, color:C.muted, background:'#f1f5f9', padding:'2px 6px', borderRadius:10 }}>{dayEvents.length}</span>}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:4, overflowY:'auto', flex:1, paddingRight:2 }}>
                {dayEvents.map(ag => {
                  const agTipo = ag.tipoAgenda || 'avaliacao';
                  const isImpl = agTipo === 'implantacao';
                  const isConfirmado = ag.status === 'confirmado';
                  const isCancelado = ag.status === 'cancelado';
                  // Cor de fundo: implantação=azul, avaliação confirmada=verde, avaliação pendente=amarelo, cancelado=cinza
                  const bgColor = isCancelado ? '#f1f5f9' : isImpl ? '#dbeafe' : isConfirmado ? '#dcfce7' : '#fef9c3';
                  // Aba lateral (borderLeft): implantação=verde, avaliação confirmada=verde, avaliação pendente=âmbar, cancelado=cinza
                  const abaColor = isCancelado ? '#94a3b8' : isImpl ? '#22c55e' : isConfirmado ? '#16a34a' : '#f59e0b';
                  const textColor = isCancelado ? '#94a3b8' : isImpl ? '#1d4ed8' : isConfirmado ? '#15803d' : '#92400e';
                  const localVal = ag.hospital || ag.pacienteEndereco || '—';
                  return (
                    <div key={ag.id} onClick={() => setSelectedEvent(ag)}
                      style={{ background:bgColor, borderLeft:`3px solid ${abaColor}`, borderRadius:4, padding:'4px 6px', cursor:'pointer', transition:'opacity 0.1s', position:'relative', opacity: isCancelado ? 0.6 : 1 }}>
                      {isAdmin && (
                        <button onClick={e => { e.stopPropagation(); onDelete && onDelete(ag); }}
                          style={{ position:'absolute', top:2, right:2, background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:'0.65rem', lineHeight:1, padding:'1px 3px', opacity:0.7 }}
                          title="Excluir">✕</button>
                      )}
                      <div style={{ fontSize:'0.7rem', fontWeight:700, color:textColor, marginBottom:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', paddingRight: isAdmin ? 14 : 0 }}>
                        {ag.hora === 'livre' ? 'Livre' : ag.hora} · {ag.profissionalNome.split(' ')[0]}
                      </div>
                      <div style={{ fontSize:'0.65rem', color:C.text, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {ag.pacienteNome}
                      </div>
                      {ag.convenio && (
                        <div style={{ fontSize:'0.6rem', color:'#6366f1', fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                          {ag.convenio}
                        </div>
                      )}
                      <div style={{ fontSize:'0.6rem', color:textColor, fontWeight:700, marginTop:1 }}>
                        {tipoAgendaLabel[agTipo] || '📋 Avaliação'}
                      </div>
                      {ag.hospital && (
                        <div style={{ fontSize:'0.6rem', color:C.muted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} title={ag.hospital}>
                          🏥 {ag.hospital}
                        </div>
                      )}
                      {ag.pacienteEndereco && (
                        <div style={{ fontSize:'0.6rem', color:C.muted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} title={ag.pacienteEndereco}>
                          📍 {ag.pacienteEndereco}
                        </div>
                      )}
                      {!ag.hospital && !ag.pacienteEndereco && (
                        <div style={{ fontSize:'0.6rem', color:C.muted }}>📍 —</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal Novo Agendamento Manual */}
      {showNovoAg && (
        <NovoAgendamentoManualModal
          profissionais={profissionais}
          pacientes={pacientes}
          allAgendamentos={agendamentos}
          onClose={() => setShowNovoAg(false)}
        />
      )}

      {/* Modal de Detalhes do Agendamento */}
      {selectedEvent && (
        <Modal maxWidth={500} onClose={() => setSelectedEvent(null)}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: C.text }}>Detalhes do Agendamento</h3>
            <button onClick={() => setSelectedEvent(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: C.muted, lineHeight: 1 }}>✕</button>
          </div>
          <div style={{ padding: 20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <span style={{ background:`${statusColor[selectedEvent.status]||'#94a3b8'}18`, color:statusColor[selectedEvent.status]||'#94a3b8', fontSize:'0.75rem', fontWeight:700, padding:'4px 10px', borderRadius:20, border:`1px solid ${statusColor[selectedEvent.status]||'#94a3b8'}30` }}>
                {statusLabel[selectedEvent.status]||selectedEvent.status}
              </span>
              <span style={{ fontSize:'0.85rem', fontWeight:700, color:C.text }}>
                {format(parseISO(selectedEvent.data), 'dd/MM/yyyy')} às {selectedEvent.hora === 'livre' ? 'Horário Livre' : selectedEvent.hora}
              </span>
            </div>

            <div style={{ background:'#f8fafc', borderRadius:10, padding:14, border:`1px solid ${C.border}`, marginBottom:16 }}>
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:'0.7rem', color:C.muted, fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Paciente</div>
                <div style={{ fontSize:'0.9rem', fontWeight:700, color:C.text }}>{selectedEvent.pacienteNome}</div>
                <div style={{ fontSize:'0.75rem', color:C.muted, marginBottom:8 }}>{selectedEvent.pacienteNumero} · {selectedEvent.convenio}{selectedEvent.pacienteConvenioEspecial && selectedEvent.pacienteConvenioEspecial!=='Nenhum'?` · 🏭 ${selectedEvent.pacienteConvenioEspecial}`:''}</div>
                <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                  {(() => {
                    const selectedPac = pacientes.find(p => p.id === selectedEvent.pacienteId);
                    const selectedTipo = selectedPac ? selectedPac.tipoImplantacao : (selectedEvent.pacienteTipoImplantacao || 'Novo');
                    const selectedTipoLabel = selectedTipo === 'Reimplantação' ? 'Reimplantação' : 'Implantação';
                    const localVal = selectedEvent.hospital || selectedEvent.pacienteEndereco || '—';
                    return (
                      <>
                        <div style={{ fontSize:'0.78rem', color:'#4f46e5', fontWeight:700 }}>🚀 <strong>Tipo:</strong> {selectedTipoLabel}</div>
                        <div style={{ fontSize:'0.78rem', color:C.text }}>📍 <strong>Local da Avaliação:</strong> {localVal}</div>
                      </>
                    );
                  })()}
                  {selectedEvent.hospital && <div style={{ fontSize:'0.78rem', color:C.text }}>🏥 <strong>Hospital:</strong> {selectedEvent.hospital}</div>}
                  {selectedEvent.pacienteEndereco && <div style={{ fontSize:'0.78rem', color:C.text }}>🏠 <strong>Endereço:</strong> {selectedEvent.pacienteEndereco}{selectedEvent.pacienteCep?` (${selectedEvent.pacienteCep})`:''}</div>}
                  {selectedEvent.pacienteDataNascimento && <div style={{ fontSize:'0.78rem', color:C.text }}>🎂 <strong>Nascimento:</strong> {selectedEvent.pacienteDataNascimento.split('-').reverse().join('/')}</div>}
                  <div style={{ fontSize:'0.78rem', color:C.text }}>🏠 <strong>Filial:</strong> {selectedEvent.pacienteFilial}{selectedEvent.pacienteVm?' · ⚠️ VM':''}</div>
                  {selectedEvent.pacienteLiminar && <div style={{ fontSize:'0.78rem', color:'#7c3aed' }}>⚖️ <strong>Possui Liminar</strong></div>}
                  {selectedEvent.pacienteObservacoes && <div style={{ fontSize:'0.78rem', color:C.muted }}>📝 {selectedEvent.pacienteObservacoes}</div>}
                </div>
              </div>
              <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:10 }}>
                <div style={{ fontSize:'0.7rem', color:C.muted, fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Profissional</div>
                <div style={{ fontSize:'0.9rem', fontWeight:700, color:C.text }}>{selectedEvent.profissionalNome}</div>
                <div style={{ fontSize:'0.75rem', color:C.muted }}>{selectedEvent.profissionalFuncao}</div>
              </div>
            </div>

            {selectedEvent.status === 'agendado' && (
              <div style={{ display:'flex', gap:10 }}>
                <button
                  onClick={() => { setEditingAg(selectedEvent); setSelectedEvent(null); }}
                  style={{ flex:1, padding:'10px', background:'#fff', border:`1px solid ${C.border}`, borderRadius:8, cursor:'pointer', color:C.text, fontWeight:700, fontSize:'0.85rem', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }} title="Alterar Horário">
                  ✏️ Editar Horário
                </button>
                <button
                  onClick={() => { setEditingAg({ ...selectedEvent, changeProfissional: true }); setSelectedEvent(null); }}
                  style={{ flex:1, padding:'10px', background:'#fff', border:`1px solid ${C.border}`, borderRadius:8, cursor:'pointer', color:'#6366f1', fontWeight:700, fontSize:'0.85rem', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }} title="Alterar Profissional">
                  👤 Trocar Prof.
                </button>
                <button
                  disabled={confirming===selectedEvent.id}
                  onClick={async()=>{ 
                    setConfirming(selectedEvent.id); 
                    await onConfirmar(selectedEvent); 
                    setConfirming(null);
                    setSelectedEvent(null);
                  }}
                  style={{ flex:2, padding:'10px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, cursor:'pointer', color:'#15803d', fontWeight:700, fontSize:'0.85rem', opacity:confirming===selectedEvent.id?0.6:1 }}>
                  {confirming===selectedEvent.id?'⏳ Confirmando...':'✅ Confirmar Avaliação'}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Modal de Edição (WeeklyCalendarPicker ou Troca de Profissional) */}
      {editingAg && (
        <Modal maxWidth={editingAg.changeProfissional ? 500 : 900} onClose={() => setEditingAg(null)}>
          <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: C.text }}>
                {editingAg.changeProfissional ? '👤 Trocar Profissional' : '✏️ Editar Agendamento'}
              </h3>
              <p style={{ margin: 0, fontSize: '0.82rem', color: C.muted, marginTop: 2 }}>
                Paciente: <strong>{editingAg.pacienteNome}</strong>
              </p>
            </div>
            <button onClick={() => setEditingAg(null)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: C.muted, lineHeight: 1 }}>✕</button>
          </div>
          <div style={{ padding: 20, background: C.bg }}>
            {editingAg.changeProfissional ? (
              /* ─── Seletor de Profissional ─── */
              <div>
                <p style={{ margin: '0 0 14px', fontSize: '0.85rem', color: C.muted }}>
                  Profissional atual: <strong>{editingAg.profissionalNome}</strong> — selecione o novo:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
                  {profissionais.map(prof => (
                    <button
                      key={prof.id}
                      onClick={async () => {
                        await updateDoc(doc(db, getCollectionName('captacao_agendamentos'), editingAg.id), {
                          profissionalId: prof.id,
                          profissionalNome: prof.nome,
                          profissionalFuncao: prof.funcao || '',
                          profissionalEmail: prof.email || '',
                          isCuringa: false,
                        });
                        setEditingAg(null);
                      }}
                      style={{
                        padding: '10px 14px', borderRadius: 8,
                        border: `2px solid ${prof.id === editingAg.profissionalId ? '#6366f1' : C.border}`,
                        background: prof.id === editingAg.profissionalId ? '#eef2ff' : '#fff',
                        color: prof.id === editingAg.profissionalId ? '#4f46e5' : C.text,
                        fontWeight: prof.id === editingAg.profissionalId ? 700 : 500,
                        fontSize: '0.9rem', cursor: 'pointer', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s'
                      }}
                    >
                      <span style={{ fontSize: '1.4rem' }}>
                        {prof.funcao === 'Enfermeiro' ? '👩‍⚕️' : prof.funcao === 'Fisioterapeuta' ? '💪' : '👤'}
                      </span>
                      <div>
                        <div style={{ fontWeight: 700 }}>{prof.nome}</div>
                        <div style={{ fontSize: '0.78rem', color: C.muted }}>{prof.funcao}</div>
                      </div>
                      {prof.id === editingAg.profissionalId && (
                        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#6366f1', fontWeight: 700 }}>atual</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* ─── Edição de Horário ─── */
              <WeeklyCalendarPicker 
                disponibilidade={profissionais.find(p=>p.id===editingAg.profissionalId)?.disponibilidade || []}
                agendamentos={agendamentos.filter(a => a.profissionalId === editingAg.profissionalId && a.status !== 'cancelado')}
                selectedDate={editingAg.data}
                selectedTime={editingAg.hora === 'livre' ? editingAg.horaLivre : editingAg.hora}
                onSelect={async (d, h) => {
                  await updateDoc(doc(db, getCollectionName('captacao_agendamentos'), editingAg.id), {
                    data: d,
                    hora: h
                  });
                  
                  const prof = profissionais.find(p=>p.id===editingAg.profissionalId);
                  const link = waLink(prof?.contato, prof?.nome, editingAg.pacienteNome, d, h, editingAg.hospital);
                  if (link) window.open(link, '_blank');
                  
                  setEditingAg(null);
                }}
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Captacao({ onOpenWizard, kanbanOnly = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const startFullscreen = kanbanOnly || searchParams.get('fullscreen') === '1';
  const [fullscreen, setFullscreen] = useState(startFullscreen);
  const user = auth.currentUser;
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; window.speechSynthesis?.cancel(); };
  }, []);
  const speakSe = (fn, ...args) => { if (isMountedRef.current) fn(...args); };
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSetorCaptacao, setIsSetorCaptacao] = useState(false);
  const [pacientes, setPacientes]         = useState([]);
  const prevPacientesRef = useRef(null); // null = primeira carga (sem áudio)
  const [loading, setLoading]             = useState(true);
  const [convenios, setConvenios]         = useState(CONVENIOS_DEFAULT);
  const [profissionais, setProfissionais] = useState([]);
  const [areaSubitems, setAreaSubitems]   = useState(CAP_AREA_SUBITEMS);
  const [view, setView]                   = useState(startFullscreen ? 'kanban' : 'adm'); // 'adm' | 'kanban' | 'agendas' | 'cancelados'
  const [cancelados, setCancelados]       = useState([]);
  const [agendamentos, setAgendamentos]   = useState([]);
  const [showNovo, setShowNovo]           = useState(false);
  const [selectedPaciente, setSelected]   = useState(null);
  const [editandoPaciente, setEditando]   = useState(null);
  const [transitionTarget, setTransition] = useState(null);
  const [editingHistorico, setEditingHistorico] = useState(null); // { paciente, entry }
  const [filtroConvenio, setFiltroConvenio] = useState('');
  const [filtroTipo, setFiltroTipo]       = useState('');
  const [filtroSLA, setFiltroSLA]         = useState('');
  const [filtroNome, setFiltroNome]       = useState('');
  const [filtroDataDe, setFiltroDataDe]   = useState('');
  const [filtroDataAte, setFiltroDataAte] = useState('');
  const [filtroCancelNome, setFiltroCancelNome]       = useState('');
  const [freqOptions, setFreqOptions]                 = useState(CAP_FREQUENCIA_OPTIONS);
  const [cancelModal, setCancelModal]                 = useState(null); // { paciente }
  const [filtroCancelOperadora, setFiltroCancelOperadora] = useState('');
  const [filtroCancelDe, setFiltroCancelDe]           = useState('');
  const [filtroCancelAte, setFiltroCancelAte]         = useState('');
  const [pageSize, setPageSize]           = useState(25);
  const [paginaAtual, setPaginaAtual]     = useState(1);
  const [tick, setTick]                   = useState(0);
  const slaAlertedRef                     = useRef({});

  useEffect(() => { const t=setInterval(()=>setTick(c=>c+1),60000); return ()=>clearInterval(t); }, []);

  useEffect(() => {
    if (user?.email) {
      getDocs(query(collection(db, 'users'), where('email', '==', user.email))).then(snap => {
        if (!snap.empty) {
          const d = snap.docs[0].data();
          const roles = Array.isArray(d.roles) ? d.roles : (d.role ? [d.role] : []);
          setIsAdmin(roles.includes('admin') || roles.includes('administrador'));
        }
      });
      // Verifica se o usuário pertence ao grupo "Captação"
      getDoc(doc(db, getCollectionName('captacao_config'), 'grupos_implantacao')).then(snap => {
        if (!snap.exists()) return;
        const grupos = snap.data().grupos || [];
        const email = user.email.toLowerCase();
        const emCaptacao = grupos.some(g => g.nome?.toLowerCase() === 'captação' && (g.membros || []).some(m => m.toLowerCase() === email));
        setIsSetorCaptacao(emCaptacao);
      }).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (pacientes.length === 0) return;
    pacientes.forEach(p => {
      // SLA só alarma até a etapa "Envio para Operadora"
      if (!isSLAActive(p)) return;
      const sla = getSLAInfo(p.dataSolicitacao || p.dataAbertura, getSLAHours(p));
      const keyExpired = `${p.id}_expired`;
      const keyWarning = `${p.id}_warning`;
      if (sla.expired) {
        // "SLA do Paciente João vencido há 3 horas" — repete a cada hora
        const lastAlert = slaAlertedRef.current[keyExpired] || 0;
        if (Date.now() - lastAlert > 3600000) {
          slaAlertedRef.current[keyExpired] = Date.now();
          speakSe(speakSLAVencido, p.nome, sla.tempoVoz);
        }
      } else if (sla.status === 'warning') {
        // "Atenção, faltam X horas para vencer o SLA do Paciente João" — repete a cada hora
        const lastAlert = slaAlertedRef.current[keyWarning] || 0;
        if (Date.now() - lastAlert > 3600000) {
          slaAlertedRef.current[keyWarning] = Date.now();
          speakSe(speakSLAAlert, p.nome, sla.tempoVoz);
        }
      } else {
        // Saiu do warning — reseta para poder alarmar novamente se retornar
        delete slaAlertedRef.current[keyWarning];
      }
    });
  }, [tick, pacientes]);

  useEffect(() => {
    // 2026-10-05: a lista inteira (select=*) tinha crescido pra ~142MB — pacientes já
    // "Implantado" acumulam um `historico` que nunca é limpo (achado: 1 registro sozinho
    // com 1,5MB só nesse campo) e ficam sendo baixados de novo a cada carregamento da tela,
    // mesmo sem ninguém olhar pra eles. Isso passava o timeout de 15s do getDocs no servidor
    // self-hosted (o Supabase Cloud escondia o problema com infraestrutura mais robusta) e
    // a tela caía silenciosamente em "nenhum paciente encontrado".
    // Correção: busca separada de (a) quem ainda não foi implantado — ativos e cancelados,
    // de qualquer data, continuam sempre visíveis — e (b) implantados, só dos últimos 7 dias.
    // Reduz o payload de ~142MB pra ~45MB sem esconder nenhum caso em andamento.
    const cutoffIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    let latestAtivos = [];
    let latestImplantadosRecentes = [];
    let ativosCarregou = false;
    let implantadosCarregou = false;

    function mergeESetar() {
      // Só renderiza quando as DUAS buscas já responderam pelo menos uma vez —
      // como são duas consultas separadas, uma delas (a menor) quase sempre volta
      // primeiro; sem essa trava a tela "pisca" com uma lista parcial (achado:
      // Kanban aparecia vazio porque só os implantados recentes tinham chegado
      // ainda, e o Kanban não mostra implantados).
      if (!ativosCarregou || !implantadosCarregou) return;

      const merged = latestAtivos.slice();
      const vistos = new Set(merged.map(p => p.id));
      for (const p of latestImplantadosRecentes) {
        if (!vistos.has(p.id)) merged.push(p);
      }

      const filtered = merged.filter(p => !p.excluido && !p.canceladoPelaOperadora);
      const canceladosList = merged.filter(p => !p.excluido && p.canceladoPelaOperadora);

      // Detecta mudanças de status recebidas de outros dispositivos e fala no lado receptor
      if (prevPacientesRef.current !== null) {
        filtered.forEach(p => {
          const prev = prevPacientesRef.current[p.id];
          if (prev && prev.status !== p.status) {
            if (isSLAActive(p)) {
              const sla = getSLAInfo(p.dataSolicitacao || p.dataAbertura, getSLAHours(p));
              if (sla.expired) {
                speakSe(speakStatusChangeSLAVencido, p.nome, p.status, sla.tempoVoz);
              } else {
                speakSe(speakStatusChangeSLA, p.nome, p.status, sla.tempoVoz);
              }
            } else {
              speakSe(speakStatusChange, p.nome, p.status);
            }
          }
        });
      }
      prevPacientesRef.current = Object.fromEntries(filtered.map(p => [p.id, { status: p.status }]));

      setPacientes(filtered);
      setCancelados(canceladosList);
      setLoading(false);
    }

    const qAtivos = query(
      collection(db, getCollectionName('captacao_pacientes')),
      where('status', '!=', 'Implantado'),
      orderBy('dataAbertura', 'desc'),
      resumo('captacao_resumo') // quadro: sem as cópias do formulário de áreas; o paciente aberto vem completo (abrirPaciente)
    );
    const unsubAtivos = onSnapshot(qAtivos, snap => {
      latestAtivos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      ativosCarregou = true;
      mergeESetar();
    }, () => { ativosCarregou = true; setLoading(false); });

    const qImplantadosRecentes = query(
      collection(db, getCollectionName('captacao_pacientes')),
      where('status', '==', 'Implantado'),
      where('dataAtualizacao', '>=', cutoffIso),
      orderBy('dataAbertura', 'desc'),
      resumo('captacao_resumo')
    );
    const unsubImplantadosRecentes = onSnapshot(qImplantadosRecentes, snap => {
      latestImplantadosRecentes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      implantadosCarregou = true;
      mergeESetar();
    }, () => { implantadosCarregou = true; mergeESetar(); });

    return () => { unsubAtivos(); unsubImplantadosRecentes(); };
  }, []);

  useEffect(() => {
    const q = query(collection(db, getCollectionName('captacao_agendamentos')), orderBy('criadoEm','desc'));
    const unsub = onSnapshot(q, snap=>setAgendamentos(snap.docs.map(d=>({id:d.id,...d.data()}))), ()=>{});
    return ()=>unsub();
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const cSnap = await getDoc(doc(db, getCollectionName('captacao_config'), 'convenios'));
        if (cSnap.exists() && cSnap.data().lista?.length) setConvenios(cSnap.data().lista);
        const pSnap = await getDoc(doc(db, getCollectionName('captacao_config'), 'profissionais'));
        if (pSnap.exists()) setProfissionais(pSnap.data().lista || []);
        const sSnap = await getDoc(doc(db, getCollectionName('captacao_config'), 'implantacao_subitems'));
        if (sSnap.exists()) {
          const stored = sSnap.data() || {};
          setAreaSubitems({ ...CAP_AREA_SUBITEMS, ...stored });
        }
        const fSnap = await getDoc(doc(db, getCollectionName('captacao_config'), 'pad_frequencias'));
        if (fSnap.exists()) {
          const opts = fSnap.data()._opcoes;
          if (Array.isArray(opts) && opts.length > 0) setFreqOptions(opts);
        }
      } catch (e) { console.warn('Config captacao:', e.message); }
    };
    loadConfig();
  }, []);

  // Carrega SLA configurável do Firestore (Configurações → Captação)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, getCollectionName('config_captacao'), 'main'), snap => {
      if (snap.exists()) setSlaConfig(snap.data());
    });
    return () => unsub();
  }, []);

  const saveConvenios = async (lista) => {
    await setDoc(doc(db, getCollectionName('captacao_config'), 'convenios'), { lista });
    setConvenios(lista);
  };
  const saveProfissionais = async (lista) => {
    await setDoc(doc(db, getCollectionName('captacao_config'), 'profissionais'), { lista });
    setProfissionais(lista);
  };

  const handleSaveNovo = async (form) => {
    const n = pacientes.length + 1;
    const numero = `CAP-${String(n).padStart(4,'0')}`;
    const docRef = await addDoc(collection(db, getCollectionName('captacao_pacientes')), {
      numero, nome:form.nome, convenio:form.convenio, liminar:form.liminar,
      hospital:form.hospital, regiaoHospital:form.regiaoHospital,
      dataNascimento:form.dataNascimento||'', cep:form.cep||'',
      dataSolicitacao:form.dataSolicitacao, filial:form.filial, vm:form.vm,
      numIW: form.numIW||'',
      convenioEspecial:form.convenioEspecial, tipoImplantacao:form.tipoImplantacao,
      observacoes:form.observacoes, status:'Recebido',
      historico:[{ status:'Recebido', timestamp:new Date().toISOString(), responsavel:user?.email||'sistema', dados:{} }],
      dataAbertura:serverTimestamp(), dataAtualizacao:serverTimestamp(),
      criadoPorEmail:user?.email||'',
    });
    setShowNovo(false);
    // Calcula tempo restante do SLA a partir da dataSolicitacao para o anúncio de voz
    const slaHorasNovo = form.vm ? _slaConfig.sla_vm : _slaConfig.sla_padrao;
    const elapsedNovo = (Date.now() - new Date(form.dataSolicitacao).getTime()) / 3600000;
    const remainingNovo = Math.max(0, slaHorasNovo - elapsedNovo);
    const rhNovo = Math.floor(remainingNovo), rmNovo = Math.floor((remainingNovo - rhNovo) * 60);
    const partesNovo = [];
    if (rhNovo > 0) partesNovo.push(`${rhNovo} hora${rhNovo !== 1 ? 's' : ''}`);
    if (rmNovo > 0) partesNovo.push(`${rmNovo} minuto${rmNovo !== 1 ? 's' : ''}`);
    const tempoVozNovo = partesNovo.join(' e ') || 'menos de um minuto';
    speakSe(speakNovoPaciente, form.convenio, form.nome, tempoVozNovo);
    // Marca o paciente como já alertado para evitar que o useEffect de SLA fale de novo logo em seguida
    slaAlertedRef.current[`${docRef.id}_warning`] = Date.now();
    slaAlertedRef.current[`${docRef.id}_expired`] = Date.now();
    const novoPaciente = { id:docRef.id, numero, nome:form.nome, convenio:form.convenio, filial:form.filial, vm:form.vm, convenioEspecial:form.convenioEspecial, hospital:form.hospital, status:'Recebido', dataSolicitacao:form.dataSolicitacao, historico:[{ status:'Recebido', timestamp:new Date().toISOString() }], dataAbertura:{ toDate:()=>new Date() } };
    setTransition({ paciente:novoPaciente, targetStatus:'Aguardando Avaliação' });
  };

  const handleUpdatePaciente = async (form) => {
    if (!editandoPaciente?.id) return;
    try {
      await updateDoc(doc(db, getCollectionName('captacao_pacientes'), editandoPaciente.id), {
        nome: form.nome, convenio: form.convenio, liminar: form.liminar,
        hospital: form.hospital, regiaoHospital: form.regiaoHospital,
        dataSolicitacao: form.dataSolicitacao, filial: form.filial, vm: form.vm,
        convenioEspecial: form.convenioEspecial, tipoImplantacao: form.tipoImplantacao,
        observacoes: form.observacoes, dataNascimento: form.dataNascimento||'', cep: form.cep||'',
        numIW: form.numIW||'',
        classificacao: form.classificacao || '',
        categoria: form.categoria || '',
        dataAtualizacao: serverTimestamp(),
      });

      // A ocorrência (prévia/aberta/em andamento) guarda uma cópia dos dados do
      // paciente feita no momento em que foi criada — não se atualiza sozinha.
      // Propaga a edição para não deixá-la com dados desatualizados (ex: Tipo
      // Novo/Reimplantação divergente do cadastro).
      try {
        const ocSnap = await getDocs(query(
          collection(db, getCollectionName('ocorrencias_v2')),
          where('captacaoId', '==', editandoPaciente.id),
          where('status', 'in', ['previa', 'aberto', 'em_andamento'])
        ));
        await Promise.all(ocSnap.docs.map(d => updateDoc(d.ref, {
          'paciente.nomeCompleto': form.nome,
          'paciente.convenio': form.convenio,
          'paciente.filial': form.filial,
          'paciente.numIW': form.numIW || '',
          'paciente.numAtendimento': form.numIW || '',
          'paciente.tipo': form.tipoImplantacao,
          'paciente.possuiLiminar': form.liminar,
          'paciente.observacoes': form.observacoes,
          'paciente.classificacao': form.classificacao || '',
          'paciente.categoria': form.categoria || '',
          dataAtualizacao: serverTimestamp(),
        })));
      } catch (syncErr) {
        console.warn('[Captacao] Erro ao sincronizar ocorrência após editar paciente:', syncErr.message);
      }

      setEditando(null);
    } catch (e) {
      console.error(e);
      alert('Erro ao salvar: ' + e.message);
    }
  };

  const handleCancelarPelaOperadora = (paciente) => {
    setCancelModal({ paciente });
  };

  const handleConfirmarCancelamento = async ({ paciente, justificativa, senha }) => {
    try {
      const email = user?.email || auth.currentUser?.email;
      const { error } = await supabase.auth.signInWithPassword({ email, password: senha });
      if (error) {
        alert('Senha incorreta. Cancelamento não realizado.');
        return;
      }
      await updateDoc(doc(db, getCollectionName('captacao_pacientes'), paciente.id), {
        canceladoPelaOperadora: true,
        canceladoEm: new Date().toISOString(),
        canceladoPor: email || 'sistema',
        statusAntesCancelamento: paciente.status,
        justificativaCancelamento: justificativa,
      });
      // Cancela prévia de implantação se existir
      try {
        const previaSnap = await getDocs(query(
          collection(db, getCollectionName('ocorrencias_v2')),
          where('captacaoId', '==', paciente.id),
          where('status', '==', 'previa')
        ));
        await Promise.all(previaSnap.docs.map(d =>
          updateDoc(d.ref, { status: 'cancelado', dataAtualizacao: serverTimestamp() })
        ));
      } catch (e) { console.warn('[Captacao] Erro ao cancelar prévia:', e.message); }
      setCancelModal(null);
      setSelected(null);
    } catch (e) {
      console.error(e);
      alert('Erro ao cancelar: ' + e.message);
    }
  };

  const handleDeletePaciente = async (id, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir o paciente ${nome}? Você poderá restaurá-lo nas Configurações.`)) return;
    try {
      await updateDoc(doc(db, getCollectionName('captacao_pacientes'), id), {
        excluido: true,
        excluidoEm: new Date().toISOString(),
        excluidoPor: user?.email || 'desconhecido'
      });
    } catch (e) {
      console.error(e);
      alert('Erro ao excluir paciente: ' + e.message);
    }
  };

  // Posta a "Observação extra" da captação como mensagem no chat (💬) de cada setor
  // da ocorrência — mesmo caminho usado pelo ComentariosChat.
  const postarObsNoChatOcorrencia = async (ocorrenciaId, obs) => {
    const texto = (obs || '').trim();
    if (!ocorrenciaId || !texto) return;
    try {
      const tarefasSnap = await getDocs(collection(db, getCollectionName('ocorrencias_v2'), ocorrenciaId, 'tarefas'));
      await Promise.all(tarefasSnap.docs.map(t =>
        addDoc(collection(db, 'ocorrencias', t.id, 'comentarios'), {
          texto: `📝 Observação da Captação: ${texto}`,
          autorNome: user?.displayName || user?.email || 'Captação',
          autorUid: user?.uid || '',
          timestamp: serverTimestamp(),
          tipo: 'mensagem',
          origem: 'captacao',
        })
      ));
    } catch (e) { console.warn('[Captacao] Postar observação no chat da ocorrência:', e.message); }
  };

  // Corrige os dados de uma etapa já concluída (histórico) sem mudar o status atual
  // do paciente nem repetir os efeitos colaterais de avanço (criar ocorrência, remoção
  // etc. — aqueles só devem acontecer uma vez, no avanço original).
  // Abre o paciente na hora com os dados do quadro (resumidos) e busca o completo em seguida —
  // a ficha mostra o formulário de áreas e a edição de etapas só libera com ele (_completo).
  const abrirPaciente = async (p) => {
    setSelected({ ...p, _completo: false });
    const snap = await getDoc(doc(db, getCollectionName('captacao_pacientes'), p.id));
    if (!snap.exists()) return;
    setSelected(atual => (atual?.id === p.id ? { ...atual, ...snap.data(), id: p.id, _completo: true } : atual));
  };

  const handleEditHistorico = async (paciente, entryOriginal, formData) => {
    // Relê do banco: a lista do quadro vem resumida (sem o formulário de áreas) e pode estar desatualizada
    const atualSnap = await getDoc(doc(db, getCollectionName('captacao_pacientes'), paciente.id));
    if (!atualSnap.exists()) return alert('Não foi possível carregar o paciente agora. Tente de novo.');
    const historicoAtual = atualSnap.data().historico || [];
    const idx = historicoAtual.findIndex(h => h.timestamp === entryOriginal.timestamp && h.status === entryOriginal.status);
    if (idx === -1) return alert('Não encontrei esse registro no histórico — pode já ter sido alterado. Recarregue e tente de novo.');

    const novoHistorico = historicoAtual.slice();
    novoHistorico[idx] = {
      ...novoHistorico[idx],
      dados: formData,
      editado: true,
      editadoPor: user?.email || 'sistema',
      editadoEm: new Date().toISOString(),
    };

    const extraUpdates = {};
    if ((entryOriginal.status === 'Relatório' || entryOriginal.status === 'Aguardando Retorno da Operadora') && formData.pad) extraUpdates.dadosPAD = formData.pad;
    // etapa criada pelo robô do e-mail: completada na edição → some o aviso "complete os dados"
    if (entryOriginal.status === 'Aguardando Retorno da Operadora') extraUpdates.envioOperadoraPendente = false;
    if (entryOriginal.status === 'Em Implantação') {
      if (formData.dataImplantacao) extraUpdates.dataImplantacaoPrevista = formData.dataImplantacao;
      if (formData.horaImplantacao) extraUpdates.horaImplantacaoPrevista = formData.horaImplantacao;
    }
    if (formData.classificacao) extraUpdates.classificacao = formData.classificacao;
    if (formData.categoria) extraUpdates.categoria = formData.categoria;

    await updateDoc(doc(db, getCollectionName('captacao_pacientes'), paciente.id), {
      historico: novoHistorico, dataAtualizacao: serverTimestamp(), ...extraUpdates,
    });

    if (entryOriginal.status === 'Em Implantação' && paciente.ocorrenciaId) {
      // Setores ligados na edição que ainda não têm tarefa na ocorrência ganham a tarefa;
      // tarefa ainda pendente tem a lista de itens atualizada. Nada é removido.
      try {
        const tarefasCol = collection(db, getCollectionName('ocorrencias_v2'), paciente.ocorrenciaId, 'tarefas');
        const tarefasSnap = await getDocs(tarefasCol);
        const porArea = new Map(tarefasSnap.docs.map(d => [d.data().area, d]));
        let ordem = tarefasSnap.docs.reduce((m, d) => Math.max(m, d.data().ordem || 0), 0) + 1;
        for (const area of CAP_IMPLANTACAO_AREAS.filter(a => a !== 'Remoções')) {
          const areaState = (formData.areas || {})[area];
          if (!areaState || (typeof areaState === 'object' ? !areaState.enabled : !areaState)) continue;
          const descricao = Object.entries(areaState.items || {}).filter(([, v]) => v === true).map(([name]) => name).join(' | ');
          const existente = porArea.get(area);
          if (existente) {
            if (existente.data().status === 'pendente' && (existente.data().descricao || '') !== descricao) {
              await updateDoc(existente.ref, { descricao });
            }
            continue;
          }
          await addDoc(tarefasCol, {
            area, areaPai: null, status: 'pendente', prioridade: 'Média', descricao,
            dataAbertura: serverTimestamp(), dataConclusao: null, ordem: ordem++,
            responsavelNome: '', responsavelUid: '', criadoPorNome: user?.email || '', criadoPorUid: user?.uid || '',
          });
        }
        await updateDoc(doc(db, getCollectionName('ocorrencias_v2'), paciente.ocorrenciaId), { dataAtualizacao: serverTimestamp() });
      } catch (e) { console.error('[Captacao] Sincronizar setores da ocorrência na edição:', e); }

      // Observação nova/alterada na edição da implantação também vai pro chat da ocorrência
      if ((formData.obs || '').trim() !== (entryOriginal.dados?.obs || '').trim()) {
        await postarObsNoChatOcorrencia(paciente.ocorrenciaId, formData.obs);
      }
    }

    if (selectedPaciente?.id === paciente.id) {
      setSelected(p => ({ ...p, historico: novoHistorico, ...extraUpdates }));
    }
    setEditingHistorico(null);
  };

  const handleAdvanceStatus = async (formData) => {
    const { paciente, targetStatus } = transitionTarget;
    const entry = { status:targetStatus, timestamp:new Date().toISOString(), responsavel:user?.email||'sistema', dados:formData };

    const extraUpdates = {};
    if (targetStatus === 'Aguardando Retorno da Operadora' && formData.pad) {
      extraUpdates.dadosPAD = formData.pad;
    }
    if (targetStatus === 'Em Implantação') {
      extraUpdates.dadosImplantacao = formData;
      if (formData.dataImplantacao) extraUpdates.dataImplantacaoPrevista = formData.dataImplantacao;
      if (formData.horaImplantacao) extraUpdates.horaImplantacaoPrevista = formData.horaImplantacao;
    }
    if (targetStatus === 'Implantado') {
      extraUpdates.implantadoEm = new Date().toISOString();
    }
    if (formData.classificacao) {
      extraUpdates.classificacao = formData.classificacao;
    }
    if (formData.categoria) {
      extraUpdates.categoria = formData.categoria;
    }

    await updateDoc(doc(db, getCollectionName('captacao_pacientes'), paciente.id), {
      status:targetStatus, historico:arrayUnion(entry), dataAtualizacao:serverTimestamp(),
      ...extraUpdates,
    });

    // Auto-confirma agendamentos pendentes quando o status avança além de "Aguardando Avaliação"
    const statusesQueConfirmam = ['Avaliação Concluída','Aguardando Retorno da Operadora','Em Implantação','Implantado'];
    if (statusesQueConfirmam.includes(targetStatus)) {
      try {
        const agSnap = await getDocs(query(
          collection(db, getCollectionName('captacao_agendamentos')),
          where('pacienteId', '==', paciente.id),
          where('status', '==', 'agendado')
        ));
        await Promise.all(agSnap.docs.map(d =>
          updateDoc(d.ref, { status: 'confirmado', dataConfirmacao: serverTimestamp(), confirmadoAutomaticamente: true })
        ));
      } catch (e) { console.warn('[Captacao] Auto-confirmar agendamentos:', e.message); }
    }

    if (targetStatus === 'Aguardando Retorno da Operadora' && formData.pad) {
      try {
        const dadosPAD = formData.pad;
        const pacienteInfo = {
          nomeCompleto: paciente.nome, convenio: paciente.convenio, filial: paciente.filial,
          numIW: paciente.numIW || paciente.numero, numAtendimento: paciente.numIW || paciente.numero,
          tipoOcorrencia: 'Implantação', tipo: paciente.tipoImplantacao || 'Novo',
          possuiLiminar: paciente.liminar || false, observacoes: paciente.observacoes || '',
          classificacao: formData.classificacao || paciente.classificacao || '',
          categoria: formData.categoria || paciente.categoria || '',
        };
        // Se já existe uma prévia, apenas atualiza o PAD com o mais recente
        const previaSnap = await getDocs(query(
          collection(db, getCollectionName('ocorrencias_v2')),
          where('captacaoId', '==', paciente.id),
          where('status', '==', 'previa')
        ));
        if (!previaSnap.empty) {
          await updateDoc(previaSnap.docs[0].ref, { dadosPAD, dataAtualizacao: serverTimestamp() });
        } else {
          const previaRef = await addDoc(collection(db, getCollectionName('ocorrencias_v2')), {
            paciente: pacienteInfo, tipo: 'implantacao', status: 'previa',
            captacaoId: paciente.id, dadosPAD,
            criadoPorNome: user?.displayName || user?.email || 'sistema',
            criadoPorUid: user?.uid || '',
            dataAbertura: serverTimestamp(), dataAtualizacao: serverTimestamp(),
          });
          const tarefasCol = collection(db, getCollectionName('ocorrencias_v2'), previaRef.id, 'tarefas');
          let ordem = 1;
          for (const disc of CAP_PAD_DISCIPLINES) {
            const discData = dadosPAD[disc.key];
            if (!discData?.enabled) continue;
            const discSubs = CAP_PAD_SUBSPECIALTIES[disc.key];
            let descricao = '', frequencia = '', subspecialidades = null;
            if (discSubs) {
              const ativas = {};
              discSubs.forEach(s => { const f = discData.subspecialidades?.[s]; if (f && f !== 'N/A') ativas[s] = f; });
              subspecialidades = discData.subspecialidades || {};
              descricao = Object.entries(ativas).map(([s, f]) => `${s}: ${f}`).join(' | ');
            } else {
              frequencia = discData.frequencia || '';
              descricao = frequencia;
            }
            await addDoc(tarefasCol, {
              area: `PAD - ${disc.label}`, areaPai: 'PAD', status: 'pendente', prioridade: 'Média',
              descricao, frequencia, ...(subspecialidades ? { subspecialidades } : {}),
              dataAbertura: serverTimestamp(), dataConclusao: null, ordem: ordem++,
              responsavelNome: '', responsavelUid: '', criadoPorNome: user?.email || '', criadoPorUid: user?.uid || '',
            });
          }
        }
      } catch (err) { console.error('[Captacao] Erro ao criar prévia:', err); }
    }

    // Salva pedido de remoção quando os campos de remoção foram preenchidos
    if (targetStatus === 'Aguardando Retorno da Operadora') {
      if (formData.remocaoOrigem || formData.remocaoDestino || formData.cepOrigem) {
        try {
          await setDoc(doc(db, getCollectionName('rm_para_agendar'), paciente.id), {
            paciente:          paciente.nome || '',
            plano:             paciente.convenio || '',
            motivo:            'Desospitalização',
            local_origem:      formData.remocaoOrigem || '',
            local_destino:     formData.remocaoDestino || '',
            cep_origem:        formData.cepOrigem || '',
            cep_destino:       formData.cepDestino || '',
            tipo_rm:           formData.tipoRemocao || 'Simples',
            tipo_deslocamento: 'so_ida',
            tem_medico:        false,
            tem_o2:            formData.tipoRemocao === 'UTI com O₂',
            data_agendamento:  '',
            observacao:        '',
            captacao_id:       paciente.id,
            num_iw:            paciente.numIW || paciente.numero || '',
            filial:            paciente.filial || '',
            origem_fluxo:      'captacao_previa',
            criadoEm:          serverTimestamp(),
          });
        } catch (errRem) { console.error('[Captacao] Erro ao criar/atualizar rm_para_agendar:', errRem); }
      }
    }

    if (targetStatus === 'Aguardando Retorno da Operadora') {
      try {
        // 1. Salva snapshot na captacao_previas (para o painel "Prévias")
        await setDoc(doc(db, getCollectionName('captacao_previas'), paciente.id), {
          pacienteId: paciente.id,
          pacienteNome: paciente.nome,
          convenio: paciente.convenio || '',
          filial: paciente.filial || '',
          numero: paciente.numero || '',
          classificacao: formData.classificacao || paciente.classificacao || '',
          status: 'ativa',
          criadoEm: serverTimestamp(),
          atualizadoEm: serverTimestamp(),
          criadoPor: user?.email || '',
          pad: formData.pad || {},
          areas: formData.areas || {},
          remocaoOrigem: formData.remocaoOrigem || '',
          remocaoDestino: formData.remocaoDestino || '',
          cepOrigem: formData.cepOrigem || '',
          cepDestino: formData.cepDestino || '',
          tipoRemocao: formData.tipoRemocao || 'Simples',
          confirmacoes: {},
          comentarios: [],
        });

        // 3. Adiciona tarefas de área na prévia do ocorrencias_v2 (para grupos/setores verem no GestaoOcorrências)
        const previaOcSnap = await getDocs(query(
          collection(db, getCollectionName('ocorrencias_v2')),
          where('captacaoId', '==', paciente.id),
          where('status', '==', 'previa')
        ));
        if (!previaOcSnap.empty) {
          const previaDoc = previaOcSnap.docs[0];
          const tarefasPreviaCol = collection(db, getCollectionName('ocorrencias_v2'), previaDoc.id, 'tarefas');
          const existSnap = await getDocs(tarefasPreviaCol);
          const existAreasPrev = new Set(existSnap.docs.map(d => d.data().area));
          let ordemPrev = existSnap.docs.reduce((m, d) => Math.max(m, d.data().ordem || 0), 0) + 1;

          // Remoção
          if ((formData.remocaoOrigem || formData.remocaoDestino) && !existAreasPrev.has('Remoções')) {
            await addDoc(tarefasPreviaCol, {
              area: 'Remoções', areaPai: null, status: 'pendente', prioridade: 'Alta',
              descricao: [formData.remocaoOrigem ? `Origem: ${formData.remocaoOrigem}` : '', formData.remocaoDestino ? `Destino: ${formData.remocaoDestino}` : '', formData.tipoRemocao ? `Tipo: ${formData.tipoRemocao}` : ''].filter(Boolean).join(' | '),
              dataAbertura: serverTimestamp(), dataConclusao: null, ordem: ordemPrev++,
              responsavelNome: '', responsavelUid: '', criadoPorNome: user?.email || '', criadoPorUid: user?.uid || '',
            });
          }
          // Áreas selecionadas
          for (const area of CAP_IMPLANTACAO_AREAS.filter(a => a !== 'Remoções')) {
            const areaState = (formData.areas || {})[area];
            if (!areaState || (typeof areaState === 'object' ? !areaState.enabled : !areaState)) continue;
            if (existAreasPrev.has(area)) continue;
            const activeItems = CAP_AREA_SUBITEMS[area]
              ? Object.entries(areaState.items || {}).filter(([, v]) => v === true).map(([name]) => name)
              : [];
            await addDoc(tarefasPreviaCol, {
              area, areaPai: null, status: 'pendente', prioridade: 'Média',
              descricao: activeItems.join(' | '),
              dataAbertura: serverTimestamp(), dataConclusao: null, ordem: ordemPrev++,
              responsavelNome: '', responsavelUid: '', criadoPorNome: user?.email || '', criadoPorUid: user?.uid || '',
            });
          }
          await updateDoc(previaDoc.ref, { atualizadoEm: serverTimestamp() });
        }
      } catch (err) { console.error('[Captacao] Erro ao salvar prévia:', err); }
    }

    if (targetStatus === 'Em Implantação') {
      try {
        // Lê prévia existente (criada em "Envio para Operadora") como fonte principal dos dados
        let sourceData = formData;
        const previaRef = doc(db, getCollectionName('captacao_previas'), paciente.id);
        const previaSnap2 = await getDoc(previaRef);
        if (previaSnap2.exists() && previaSnap2.data().status === 'ativa') {
          const pd = previaSnap2.data();
          sourceData = {
            ...formData,
            // 20/10: o popup agora mostra a prévia preenchida para conferir — vale o que foi confirmado na tela
            pad: formData.pad || pd.pad,
            areas: formData.areas || pd.areas,
            remocaoOrigem: formData.remocaoOrigem || pd.remocaoOrigem,
            remocaoDestino: formData.remocaoDestino || pd.remocaoDestino,
            cepOrigem: formData.cepOrigem || pd.cepOrigem || '',
            cepDestino: formData.cepDestino || pd.cepDestino || '',
            tipoRemocao: formData.tipoRemocao || pd.tipoRemocao,
            dataImplantacao: formData.dataImplantacao || pd.dataImplantacao || '',
            horaImplantacao: formData.horaImplantacao || pd.horaImplantacao || '',
            classificacao: formData.classificacao || pd.classificacao || '',
          };
        }

        // formData.pad vem pré-populado pelo TransitionModal com dadosPADSalvo do Firestore
        const dadosPAD = sourceData.pad || {};

        // Garante que a remoção existe em rm_para_agendar (fallback caso não criado em "Envio para Operadora")
        // Usa setDoc com ID = paciente.id para garantir upsert confiável (sem getDocs intermediário)
        if (sourceData.remocaoOrigem || sourceData.remocaoDestino || sourceData.cepOrigem) {
          try {
            await setDoc(doc(db, getCollectionName('rm_para_agendar'), paciente.id), {
              paciente:          paciente.nome || '',
              plano:             paciente.convenio || '',
              motivo:            'Desospitalização',
              local_origem:      sourceData.remocaoOrigem || '',
              local_destino:     sourceData.remocaoDestino || '',
              cep_origem:        sourceData.cepOrigem || '',
              cep_destino:       sourceData.cepDestino || '',
              tipo_rm:           sourceData.tipoRemocao || 'Simples',
              tipo_deslocamento: 'so_ida',
              tem_medico:        false,
              tem_o2:            sourceData.tipoRemocao === 'UTI com O₂',
              data_agendamento:  sourceData.dataImplantacao || '',
              horario:           sourceData.horaImplantacao || '',
              observacao:        '',
              captacao_id:       paciente.id,
              num_iw:            paciente.numIW || paciente.numero || '',
              filial:            paciente.filial || '',
              origem_fluxo:      'captacao_previa',
              criadoEm:          serverTimestamp(),
            }, { merge: true });
          } catch (errRem) { console.error('[Captacao] Erro rm_para_agendar em implantação:', errRem); }
        }

        // Verifica se já existe ocorrência ativa (aberto/em_andamento) para este paciente.
        // Se não existir, verifica se há uma prévia e a promove para 'aberto' em vez de criar nova.
        let existingOcSnap = await getDocs(query(
          collection(db, getCollectionName('ocorrencias_v2')),
          where('captacaoId', '==', paciente.id),
          where('status', 'in', ['aberto', 'em_andamento'])
        ));

        if (existingOcSnap.empty) {
          const previaParaPromoter = await getDocs(query(
            collection(db, getCollectionName('ocorrencias_v2')),
            where('captacaoId', '==', paciente.id),
            where('status', '==', 'previa')
          ));
          if (!previaParaPromoter.empty) {
            await updateDoc(previaParaPromoter.docs[0].ref, {
              status: 'aberto',
              dataAtualizacao: serverTimestamp(),
            });
            existingOcSnap = previaParaPromoter; // reutiliza o doc promovido
          }
        }

        let ocRef;
        const existingAreas = new Set();
        let maxOrdem = 0;

        if (!existingOcSnap.empty) {
          // Ocorrência já existe — só atualiza metadados
          ocRef = existingOcSnap.docs[0].ref;
          await updateDoc(ocRef, {
            dataImplantacaoPrevista: sourceData.dataImplantacao || '',
            horaImplantacaoPrevista: sourceData.horaImplantacao || '',
            dataAtualizacao: serverTimestamp(),
          });
          // Reconcilia qualquer prévia órfã do mesmo paciente (fix de dados existentes)
          try {
            const previasOrfas = await getDocs(query(
              collection(db, getCollectionName('ocorrencias_v2')),
              where('captacaoId', '==', paciente.id),
              where('status', '==', 'previa')
            ));
            await Promise.all(previasOrfas.docs.map(d =>
              updateDoc(d.ref, { status: 'reconciliado', previaSubstituida: true, dataAtualizacao: serverTimestamp() })
            ));
          } catch (e) { console.warn('[Captacao] reconciliar prévia órfã:', e.message); }
          // Carrega tarefas existentes para não duplicar
          const tarefasExistSnap = await getDocs(
            collection(db, getCollectionName('ocorrencias_v2'), existingOcSnap.docs[0].id, 'tarefas')
          );
          tarefasExistSnap.docs.forEach(d => {
            const td = d.data();
            existingAreas.add(td.area);
            if ((td.ordem || 0) > maxOrdem) maxOrdem = td.ordem || 0;
          });
        } else {
          // Cria nova ocorrência
          ocRef = await addDoc(collection(db, getCollectionName('ocorrencias_v2')), {
            paciente: {
              nomeCompleto: paciente.nome,
              convenio: paciente.convenio,
              filial: paciente.filial,
              numIW: paciente.numIW || paciente.numero,
              numAtendimento: paciente.numIW || paciente.numero,
              tipoOcorrencia: 'Implantação',
              tipo: paciente.tipoImplantacao || 'Novo',
              possuiLiminar: paciente.liminar || false,
              observacoes: paciente.observacoes || '',
              classificacao: sourceData.classificacao || paciente.classificacao || '',
              categoria: paciente.categoria || '',
            },
            tipo: 'implantacao',
            status: 'aberto',
            captacaoId: paciente.id,
            dataImplantacaoPrevista: sourceData.dataImplantacao || '',
            horaImplantacaoPrevista: sourceData.horaImplantacao || '',
            criadoPorNome: user?.displayName || user?.email || 'sistema',
            criadoPorUid: user?.uid || '',
            dataAbertura: serverTimestamp(),
            dataAtualizacao: serverTimestamp(),
          });
        }

        const tarefasCol = collection(db, getCollectionName('ocorrencias_v2'), ocRef.id, 'tarefas');
        let ordem = maxOrdem + 1;

        // Tarefas do PAD (disciplinas selecionadas — pula se área já existe na ocorrência)
        for (const disc of CAP_PAD_DISCIPLINES) {
          const discData = dadosPAD[disc.key];
          if (!discData?.enabled) continue;
          const areaDisc = `PAD - ${disc.label}`;
          if (existingAreas.has(areaDisc)) continue; // evita duplicidade
          const discSubs = CAP_PAD_SUBSPECIALTIES[disc.key];
          let descricao = '';
          let subspecialidades = null;
          let frequencia = '';
          if (discSubs) {
            const ativas = {};
            discSubs.forEach(s => {
              const f = discData.subspecialidades?.[s];
              if (f && f !== 'N/A') ativas[s] = f;
            });
            subspecialidades = discData.subspecialidades || {};
            descricao = Object.entries(ativas).map(([s, f]) => `${s}: ${f}`).join(' | ');
          } else {
            frequencia = discData.frequencia || '';
            descricao = frequencia;
          }
          await addDoc(tarefasCol, {
            area: areaDisc,
            areaPai: 'PAD',
            status: 'pendente',
            prioridade: 'Média',
            descricao,
            frequencia,
            ...(subspecialidades ? { subspecialidades } : {}),
            dataAbertura: serverTimestamp(),
            dataConclusao: null,
            ordem: ordem++,
            responsavelNome: '',
            responsavelUid: '',
            criadoPorNome: user?.email || '',
            criadoPorUid: user?.uid || '',
          });
        }

        // Tarefa de Remoção — pula se já existe
        if ((sourceData.remocaoOrigem || sourceData.remocaoDestino) && !existingAreas.has('Remoções')) {
          await addDoc(tarefasCol, {
            area: 'Remoções',
            areaPai: null,
            status: 'pendente',
            prioridade: 'Alta',
            descricao: [
              sourceData.remocaoOrigem ? `Origem: ${sourceData.remocaoOrigem}` : '',
              sourceData.remocaoDestino ? `Destino: ${sourceData.remocaoDestino}` : '',
              sourceData.tipoRemocao ? `Tipo: ${sourceData.tipoRemocao}` : '',
            ].filter(Boolean).join(' | '),
            dataAbertura: serverTimestamp(),
            dataConclusao: null,
            ordem: ordem++,
            responsavelNome: '',
            responsavelUid: '',
            criadoPorNome: user?.email || '',
            criadoPorUid: user?.uid || '',
          });
        }

        // Tarefas dos setores selecionados — pula áreas que já existem
        const selectedAreas = sourceData.areas || {};
        for (const area of CAP_IMPLANTACAO_AREAS.filter(a => a !== 'Remoções')) {
          const areaState = selectedAreas[area];
          if (!areaState || (typeof areaState === 'object' ? !areaState.enabled : !areaState)) continue;
          if (existingAreas.has(area)) continue; // evita duplicidade

          if (area === 'Prontuário') {
            const activeItems = Object.entries(areaState.items || {})
              .filter(([_, enabled]) => enabled === true)
              .map(([name]) => name);
            await addDoc(tarefasCol, {
              area: 'Prontuário',
              areaPai: null,
              status: 'pendente',
              prioridade: 'Média',
              descricao: activeItems.join(' | '),
              dataAbertura: serverTimestamp(),
              dataConclusao: null,
              ordem: ordem++,
              responsavelNome: '',
              responsavelUid: '',
              criadoPorNome: user?.email || '',
              criadoPorUid: user?.uid || '',
            });
            continue;
          }

          const activeItems = CAP_AREA_SUBITEMS[area]
            ? Object.entries(areaState.items || {}).filter(([, v]) => v === true).map(([name]) => name)
            : [];
          await addDoc(tarefasCol, {
            area,
            areaPai: null,
            status: 'pendente',
            prioridade: 'Média',
            descricao: activeItems.join(' | '),
            dataAbertura: serverTimestamp(),
            dataConclusao: null,
            ordem: ordem++,
            responsavelNome: '',
            responsavelUid: '',
            criadoPorNome: user?.email || '',
            criadoPorUid: user?.uid || '',
          });
        }

        // Tarefa automática para Educação Continuada quando VM + classificação ID
        const classificacaoFinal = sourceData.classificacao || paciente.classificacao;
        if (paciente.vm && classificacaoFinal === 'ID' && !existingAreas.has('Educação Continuada')) {
          const dataImpl = sourceData.dataImplantacao
            ? `${sourceData.dataImplantacao}${sourceData.horaImplantacao ? ' às ' + sourceData.horaImplantacao : ''}`
            : 'A confirmar';
          await addDoc(tarefasCol, {
            area: 'Educação Continuada',
            areaPai: null,
            status: 'pendente',
            prioridade: 'Alta',
            descricao: [
              `Paciente VM classificado como ID`,
              `Data de implantação: ${dataImpl}`,
              `Convênio: ${paciente.convenio || '-'}`,
              paciente.categoria ? `Categoria: ${paciente.categoria}` : '',
              paciente.observacoes ? `Obs: ${paciente.observacoes}` : '',
            ].filter(Boolean).join(' | '),
            dataAbertura: serverTimestamp(),
            dataConclusao: null,
            ordem: ordem++,
            responsavelNome: '',
            responsavelUid: '',
            criadoPorNome: user?.email || '',
            criadoPorUid: user?.uid || '',
          });
        }

        // Reconcilia com prévia — sinaliza se o PAD mudou desde o Relatório
        const previaSnap = await getDocs(query(
          collection(db, getCollectionName('ocorrencias_v2')),
          where('captacaoId', '==', paciente.id),
          where('status', '==', 'previa')
        ));
        if (!previaSnap.empty) {
          const previaDoc = previaSnap.docs[0];
          const previaPAD = previaDoc.data().dadosPAD || {};
          const houveMudanca = JSON.stringify(previaPAD) !== JSON.stringify(dadosPAD);
          await updateDoc(ocRef, {
            previaId: previaDoc.id,
            previaHouveMudanca: houveMudanca,
            previaResumo: houveMudanca ? 'PAD foi alterado após a prévia' : 'PAD idêntico à prévia',
          });
          await updateDoc(previaDoc.ref, {
            status: 'reconciliado',
            ocorrenciaFinalId: ocRef.id,
            previaHouveMudanca: houveMudanca,
            dataReconciliacao: serverTimestamp(),
          });
        }

        await updateDoc(doc(db, getCollectionName('captacao_pacientes'), paciente.id), { ocorrenciaId: ocRef.id });

        await postarObsNoChatOcorrencia(ocRef.id, formData.obs);

        // Marca prévia como migrada
        if (previaSnap2.exists() && previaSnap2.data().status === 'ativa') {
          await updateDoc(previaRef, { status: 'migrada', ocorrenciaId: ocRef.id, migradoEm: serverTimestamp() });
        }
      } catch (err) { console.error('[Captacao] Erro ao criar ocorrência:', err); }
    }

    if ((targetStatus==='Aguardando Avaliação' || targetStatus==='Em Implantação') && formData.agendamentosPorProf) {
      const tipoAgenda = targetStatus === 'Em Implantação' ? 'implantacao' : 'avaliacao';
      const dadosPaciente = {
        pacienteId: paciente.id, pacienteNome: paciente.nome, pacienteNumero: paciente.numero,
        convenio: paciente.convenio || '', hospital: paciente.hospital || '',
        pacienteFilial: paciente.filial || '',
        pacienteEndereco: paciente.regiaoHospital || '',
        pacienteCep: paciente.cep || '',
        pacienteDataNascimento: paciente.dataNascimento || '',
        pacienteVm: paciente.vm || false,
        pacienteLiminar: paciente.liminar || false,
        pacienteConvenioEspecial: paciente.convenioEspecial || '',
        pacienteObservacoes: paciente.observacoes || '',
        pacienteTipoImplantacao: paciente.tipoImplantacao || 'Novo',
      };
      // Fetch existing appointments for dedup — update instead of duplicating on reschedule
      const existingSnap = await getDocs(query(
        collection(db, getCollectionName('captacao_agendamentos')),
        where('pacienteId', '==', paciente.id)
      ));
      const existingByKey = {};
      existingSnap.docs.forEach(d => {
        const dd = d.data();
        const kt = dd.tipoAgenda || 'avaliacao';
        existingByKey[`${dd.profissionalId}_${kt}`] = d;
      });
      for (const [profId, ag] of Object.entries(formData.agendamentosPorProf)) {
        if (!ag.data) continue;
        const hora = ag.hora==='livre' ? (ag.horaLivre||'') : (ag.hora||'');
        if (profId === 'curinga' || profId === 'impl_curinga') {
          if (!ag.nomeCustom?.trim()) continue;
          const existing = existingByKey[`curinga_${tipoAgenda}`];
          if (existing && existing.data().status === 'agendado') {
            await updateDoc(existing.ref, { data: ag.data, hora, profissionalNome: ag.nomeCustom.trim(), atualizadoEm: serverTimestamp() });
          } else {
            await addDoc(collection(db, getCollectionName('captacao_agendamentos')), {
              ...dadosPaciente,
              profissionalId: 'curinga', profissionalNome: ag.nomeCustom.trim(), profissionalFuncao: 'Curinga',
              profissionalEmail: '', isCuringa: true,
              data: ag.data, hora, status:'agendado', tipoAgenda, criadoEm:serverTimestamp(),
            });
          }
        } else {
          const prof = profissionais.find(p=>p.id===profId);
          if (!prof) continue;
          const existing = existingByKey[`${profId}_${tipoAgenda}`];
          if (existing && existing.data().status === 'agendado') {
            await updateDoc(existing.ref, { data: ag.data, hora, atualizadoEm: serverTimestamp() });
          } else {
            await addDoc(collection(db, getCollectionName('captacao_agendamentos')), {
              ...dadosPaciente,
              profissionalId: prof.id, profissionalNome: prof.nome, profissionalFuncao: prof.funcao,
              profissionalEmail: prof.email || '',
              data: ag.data, hora, status:'agendado', tipoAgenda, criadoEm:serverTimestamp(),
            });
          }
        }
      }
    }
    const _speakComSLA = (nome, status) => {
      if (isSLAActive({ status })) {
        const sla = getSLAInfo(paciente.dataSolicitacao || paciente.dataAbertura, getSLAHours(paciente));
        speakSe(speakStatusChangeSLA, nome, status, sla.tempoVoz);
      } else {
        speakSe(speakStatusChange, nome, status);
      }
    };
    if (targetStatus === 'Aguardando Avaliação') {
      const profsAgendados = Object.entries(formData.agendamentosPorProf||{})
        .map(([id]) => profissionais.find(p=>p.id===id)?.nome)
        .filter(Boolean);
      if (profsAgendados.length > 0) speakSe(speakVisitaAgendada, paciente.nome, profsAgendados.join(' e '));
      else if (paciente.status !== 'Recebido') _speakComSLA(paciente.nome, targetStatus);
    } else if (targetStatus === 'Implantado') {
      speakSe(speakImplantado, paciente.nome);
    } else {
      _speakComSLA(paciente.nome, targetStatus);
    }
    // Atualiza prevPacientesRef imediatamente para o onSnapshot não duplicar o aviso de voz
    if (prevPacientesRef.current[paciente.id]) {
      prevPacientesRef.current[paciente.id] = { status: targetStatus };
    }

    setTransition(null);
    if (selectedPaciente?.id===paciente.id) setSelected(p=>({...p,status:targetStatus,classificacao:formData.classificacao || p.classificacao || '',categoria:formData.categoria || p.categoria || '',historico:[...(p.historico||[]),entry]}));
    if (targetStatus==='Implantado'&&onOpenWizard) onOpenWizard(paciente);
  };

  const filtered = useMemo(()=>{
    const getTs = p => { const d = new Date(p.dataSolicitacao); return !isNaN(d) ? d : (p.dataAbertura?.toDate ? p.dataAbertura.toDate() : new Date(0)); };
    return pacientes
      .filter(p=>{
        if (filtroNome && !(p.nome||'').toLowerCase().includes(filtroNome.toLowerCase())) return false;
        if (filtroConvenio && p.convenio!==filtroConvenio) return false;
        if (filtroTipo==='Homebaby'   && p.filial!=='Homebaby') return false;
        if (filtroTipo==='Vitalcare' && nf(p.filial)!=='Vitalcare') return false;
        if (filtroTipo==='VM'         && !p.vm) return false;
        if (filtroTipo==='Petrobras'  && p.convenioEspecial!=='Petrobras') return false;
        if (filtroSLA) { const sla=getSLAInfo(p.dataSolicitacao||p.dataAbertura,getSLAHours(p)); if (sla.status!==filtroSLA) return false; }
        if (filtroDataDe)  { const ts=getTs(p); if (ts < new Date(filtroDataDe+'T00:00')) return false; }
        if (filtroDataAte) { const ts=getTs(p); if (ts > new Date(filtroDataAte+'T23:59:59')) return false; }
        return true;
      })
      .sort((a,b) => getTs(b) - getTs(a));
  }, [pacientes, filtroNome, filtroConvenio, filtroTipo, filtroSLA, filtroDataDe, filtroDataAte, tick]);

  // Volta pra página 1 sempre que algum filtro ou o tamanho da página muda
  // (senão o usuário pode ficar "preso" numa página que não existe mais)
  useEffect(() => { setPaginaAtual(1); }, [filtroNome, filtroConvenio, filtroTipo, filtroSLA, filtroDataDe, filtroDataAte, pageSize]);

  // Busca de nome com debounce — digitar letra por letra não deve disparar
  // uma consulta nova no servidor a cada tecla
  const [filtroNomeBusca, setFiltroNomeBusca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setFiltroNomeBusca(filtroNome.trim()), 400);
    return () => clearTimeout(t);
  }, [filtroNome]);

  // 2026-10-05: paginação de verdade — busca só a página atual direto no
  // servidor (não busca mais tudo pra depois fatiar no navegador). Resolve o
  // carregamento inicial também, não só a exibição. Nome/convênio/filial/data
  // filtram no banco; SLA continua calculado no navegador (regra de prazo por
  // convênio, não dá pra traduzir pra SQL sem duplicar essa lógica), então ele
  // só filtra dentro da página já carregada — combinado com o Alexandre.
  const [listaRows, setListaRows] = useState([]);
  const [listaTotal, setListaTotal] = useState(0);
  const [listaCarregando, setListaCarregando] = useState(true);

  useEffect(() => {
    if (view !== 'adm') return;
    let cancelado = false;
    setListaCarregando(true);

    async function buscar() {
      let q = supabase
        .from(getCollectionName('captacao_pacientes'))
        .select('*', { count: 'exact' })
        .or('data->>excluido.eq.false,data->>excluido.is.null')
        .or('data->>canceladoPelaOperadora.eq.false,data->>canceladoPelaOperadora.is.null');

      if (filtroNomeBusca) q = q.ilike('data->>nome', `%${filtroNomeBusca}%`);
      if (filtroConvenio) q = q.eq('data->>convenio', filtroConvenio);
      if (filtroTipo === 'Homebaby')   q = q.eq('data->>filial', 'Homebaby');
      if (filtroTipo === 'Vitalcare')  q = q.in('data->>filial', ['Vitalcare', 'Homesenior']);
      if (filtroTipo === 'VM')         q = q.eq('data->>vm', 'true');
      if (filtroTipo === 'Petrobras')  q = q.eq('data->>convenioEspecial', 'Petrobras');
      if (filtroDataDe)  q = q.gte('data->>dataAbertura', filtroDataDe + 'T00:00:00.000Z');
      if (filtroDataAte) q = q.lte('data->>dataAbertura', filtroDataAte + 'T23:59:59.999Z');

      const from = (paginaAtual - 1) * pageSize;
      q = q.order('data->>dataAbertura', { ascending: false }).range(from, from + pageSize - 1);

      const { data, count, error } = await q;
      if (cancelado) return;
      if (error) {
        console.warn('[Captacao] lista paginada:', error.message);
        setListaRows([]);
        setListaTotal(0);
      } else {
        setListaRows((data || []).map(r => ({ id: r.id, ...r.data })));
        setListaTotal(count || 0);
      }
      setListaCarregando(false);
    }
    buscar();
    return () => { cancelado = true; };
  }, [view, paginaAtual, pageSize, filtroNomeBusca, filtroConvenio, filtroTipo, filtroDataDe, filtroDataAte]);

  // SLA é calculado no navegador — filtra só dentro da página já carregada
  const pacientesPagina = filtroSLA
    ? listaRows.filter(p => getSLAInfo(p.dataSolicitacao || p.dataAbertura, getSLAHours(p)).status === filtroSLA)
    : listaRows;
  const totalPaginas = Math.max(1, Math.ceil(listaTotal / pageSize));
  const paginaSegura = Math.min(paginaAtual, totalPaginas);
  const inicioPagina = (paginaSegura - 1) * pageSize;

  const stats = useMemo(()=>({
    total:    pacientes.length,
    abertos:  pacientes.filter(p=>p.status!=='Implantado').length,
    criticos: pacientes.filter(p=>{ const s=getSLAInfo(p.dataSolicitacao||p.dataAbertura,getSLAHours(p)); return s.status==='critical'&&p.status!=='Implantado'; }).length,
  }), [pacientes, tick]);

  const exportarExcel = () => {
    const ETAPAS = [
      'Recebido','Aguardando Avaliação','Em Avaliação','Avaliação Concluída',
      'Sessão Clínica','Relatório','Orçamento','Aguardando Aprovação da Direção',
      'Envio para Operadora','Aguardando Retorno da Operadora','Abordagem à Família',
      'Em Implantação','Implantado',
    ];
    const fmtDt = (ts) => {
      if (!ts) return '';
      const d = new Date(ts);
      return isNaN(d) ? '' : d.toLocaleDateString('pt-BR');
    };
    const fmtHr = (ts) => {
      if (!ts) return '';
      const d = new Date(ts);
      return isNaN(d) ? '' : d.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });
    };
    const getHist = (p, status) => {
      const h = (p.historico || []).find(h => h.status === status);
      return h || null;
    };
    const esc = (v) => {
      const s = String(v ?? '').replace(/"/g, '""');
      return `"${s}"`;
    };

    const etapaCols = ETAPAS.flatMap(e => [
      `Data - ${e}`, `Hora - ${e}`, `Responsável - ${e}`, `Observação - ${e}`,
    ]);

    const headers = [
      'Nº IW','Nº Interno (CAP)','Nome','Hospital','Convênio','Filial','Tipo','VM',
      'Data/Hora Solicitação','Status Atual','Cancelado','Data Cancelamento','Motivo Cancelamento',
      ...etapaCols,
    ];

    const getTs = p => { const d = new Date(p.dataSolicitacao); return !isNaN(d) ? d : (p.dataAbertura?.toDate ? p.dataAbertura.toDate() : new Date(0)); };
    const todos = [...filtered, ...cancelados].sort((a,b) => getTs(b) - getTs(a));
    const rows = todos.map(p => {
      const etapaVals = ETAPAS.flatMap(e => {
        const h = getHist(p, e);
        return [
          fmtDt(h?.timestamp),
          fmtHr(h?.timestamp),
          h?.responsavel || '',
          h?.dados?.observacao || h?.dados?.obs || '',
        ];
      });
      return [
        p.numIW || p.numero || '',
        p.numero || '',
        p.nome || '',
        p.hospital || '',
        p.convenio || '',
        nf(p.filial) || '',
        [p.filial?.includes('baby') || p.filial==='Homebaby' ? 'Homebaby' : 'Vitalcare', p.programa || ''].filter(Boolean).join(' - '),
        p.vm ? 'Sim' : 'Não',
        p.dataSolicitacao ? (() => { const d = new Date(p.dataSolicitacao); return isNaN(d) ? p.dataSolicitacao : d.toLocaleString('pt-BR'); })() : '',
        p.status || (p.canceladoPelaOperadora ? 'Cancelado' : ''),
        p.canceladoPelaOperadora ? 'Sim' : 'Não',
        p.canceladoEm ? fmtDt(p.canceladoEm) : '',
        p.justificativaCancelamento || '',
        ...etapaVals,
      ].map(esc).join(';');
    });

    const csv = '﻿' + [headers.map(esc).join(';'), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `captacao_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:C.muted }}>Carregando captações...</div>;

  return (
    <div style={{ padding: fullscreen ? '10px 12px' : '16px 0' }}>
      {/* Barra de saida — so aparece em modo tela cheia, ja que o header normal fica oculto */}
      {fullscreen && (
        <button onClick={() => { setFullscreen(false); setSearchParams({}); }}
          style={{ position:'fixed', bottom:20, right:20, zIndex:950, background:'#1e293b', color:'#fff', border:'none', borderRadius:24, padding:'10px 18px', cursor:'pointer', fontSize:'0.8rem', fontWeight:700, boxShadow:'0 4px 16px rgba(0,0,0,0.35)', display:'flex', alignItems:'center', gap:6 }}>
          ✕ Sair da tela cheia
        </button>
      )}
      {/* Header — oculto no modo tela cheia */}
      {!fullscreen && (
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
          <div style={{ flex:1 }}>
            <h3 style={{ margin:'0 0 3px', fontWeight:800, fontSize:'1.05rem', color:C.text }}>Captação de Pacientes</h3>
            <p style={{ margin:0, fontSize:'0.78rem', color:C.muted }}>Da solicitação à implantação</p>
          </div>
          {/* KPIs */}
          {[{label:'Total',value:stats.total,color:C.primary},{label:'Em andamento',value:stats.abertos,color:'#0ea5e9'},{label:'🔴 Críticos',value:stats.criticos,color:'#ef4444'}].map(kpi=>(
            <div key={kpi.label} style={{ background:`${kpi.color}10`, border:`1px solid ${kpi.color}30`, borderRadius:10, padding:'8px 14px', textAlign:'center', minWidth:70 }}>
              <div style={{ fontWeight:800, fontSize:'1.2rem', color:kpi.color }}>{kpi.value}</div>
              <div style={{ fontSize:'0.65rem', color:C.muted, fontWeight:600, whiteSpace:'nowrap' }}>{kpi.label}</div>
            </div>
          ))}
          <div style={{ display:'flex', background:'#f1f5f9', borderRadius:10, padding:3, gap:2 }}>
            {[
              {k:'adm',l:'📋 Lista'},
              {k:'kanban',l:'🗂 Kanban'},
              {k:'agendas',l:'📅 Agendas'},
              {k:'cancelados',l:'🚫 Cancelados'},
              ...(isAdmin || isSetorCaptacao ? [{k:'previas',l:'📦 Prévias'}] : []),
            ].map(v=>(
              <button key={v.k} onClick={()=>setView(v.k)}
                style={{ border:'none', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:'0.8rem', fontWeight:700, background:view===v.k?'#fff':'transparent', color:view===v.k?C.primary:'#94a3b8', boxShadow:view===v.k?'0 1px 4px rgba(0,0,0,0.10)':'none', transition:'all 0.15s' }}>
                {v.l}
              </button>
            ))}
          </div>
          <button onClick={()=>{ setView('kanban'); setFullscreen(true); setSearchParams({ fullscreen: '1' }); }} title="Abrir Kanban em tela cheia"
            style={{ border:`1px solid ${C.border}`, background:'#fff', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:'0.8rem', fontWeight:700, color:C.muted, display:'flex', alignItems:'center', gap:6 }}>
            ⛶ Tela cheia
          </button>
          <BotaoImportacaoIW podeCriar />
          <button onClick={()=>setShowNovo(true)}
            style={{ ...btnS(C.primary), display:'flex', alignItems:'center', gap:6, boxShadow:'0 2px 8px rgba(99,102,241,0.3)' }}>
            + Novo Paciente
          </button>
        </div>
      )}

      {/* Filters — oculto no modo painel */}
      {!fullscreen && <div style={{ display:'flex', gap:10, padding:'10px 14px', background:'#fff', borderRadius:10, border:`1px solid ${C.border}`, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:'0.7rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>Filtros</span>
        <div style={{ width:1, height:20, background:C.border }} />
        {/* Busca por nome */}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:'0.78rem', fontWeight:600, color:C.muted, whiteSpace:'nowrap' }}>🔍 Paciente</span>
          <input
            value={filtroNome}
            onChange={e=>setFiltroNome(e.target.value)}
            placeholder="Buscar por nome..."
            style={{ border:`1px solid ${filtroNome?C.primary:C.border}`, borderRadius:7, padding:'5px 10px', fontSize:'0.8rem', outline:'none', minWidth:180, color:filtroNome?C.primary:C.text, background:filtroNome?'#eef2ff':'#f8fafc', fontWeight:filtroNome?700:400 }}
          />
        </div>
        <div style={{ width:1, height:20, background:C.border }} />
        {[
          {label:'🏥 Convênio', value:filtroConvenio, onChange:setFiltroConvenio, options:[{v:'',l:'Todos'},...convenios.map(c=>({v:c,l:c}))]},
          {label:'🏠 Filial',   value:filtroTipo,     onChange:setFiltroTipo,     options:[{v:'',l:'Todos'},{v:'Homebaby',l:'Homebaby'},{v:'Vitalcare',l:'Vitalcare'},{v:'VM',l:'VM'},{v:'Petrobras',l:'Petrobras'}]},
          {label:'⏱ SLA',      value:filtroSLA,      onChange:setFiltroSLA,      options:[{v:'',l:'Todos'},{v:'ok',l:'🟢 Normal'},{v:'warning',l:'🟡 Atenção'},{v:'critical',l:'🔴 Crítico'}]},
        ].map(f=>(
          <div key={f.label} style={{ display:'flex', alignItems:'center', gap:6 }}>
            <label style={{ fontSize:'0.78rem', fontWeight:600, color:C.muted, whiteSpace:'nowrap' }}>{f.label}</label>
            <select value={f.value} onChange={e=>f.onChange(e.target.value)}
              style={{ border:`1px solid ${f.value?C.primary:C.border}`, borderRadius:7, padding:'5px 10px', fontSize:'0.8rem', color:f.value?C.primary:C.text, background:f.value?'#eef2ff':'#f8fafc', fontWeight:f.value?700:400, cursor:'pointer', outline:'none' }}>
              {f.options.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
        ))}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <label style={{ fontSize:'0.78rem', fontWeight:600, color:C.muted, whiteSpace:'nowrap' }}>📅 De</label>
          <input type="date" value={filtroDataDe} onChange={e=>setFiltroDataDe(e.target.value)}
            style={{ border:`1px solid ${filtroDataDe?C.primary:C.border}`, borderRadius:7, padding:'5px 8px', fontSize:'0.8rem', outline:'none', background:filtroDataDe?'#eef2ff':'#f8fafc', color:filtroDataDe?C.primary:C.text }} />
          <label style={{ fontSize:'0.78rem', fontWeight:600, color:C.muted, whiteSpace:'nowrap' }}>até</label>
          <input type="date" value={filtroDataAte} onChange={e=>setFiltroDataAte(e.target.value)}
            style={{ border:`1px solid ${filtroDataAte?C.primary:C.border}`, borderRadius:7, padding:'5px 8px', fontSize:'0.8rem', outline:'none', background:filtroDataAte?'#eef2ff':'#f8fafc', color:filtroDataAte?C.primary:C.text }} />
        </div>
        <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
          {(filtroNome||filtroConvenio||filtroTipo||filtroSLA||filtroDataDe||filtroDataAte) && (
            <button onClick={()=>{setFiltroNome('');setFiltroConvenio('');setFiltroTipo('');setFiltroSLA('');setFiltroDataDe('');setFiltroDataAte('');}}
              style={{ background:'none', border:'1px solid #fca5a5', borderRadius:7, padding:'5px 12px', cursor:'pointer', fontSize:'0.78rem', color:'#ef4444', fontWeight:600 }}>
              ✕ Limpar
            </button>
          )}
          <button onClick={exportarExcel}
            style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:7, padding:'5px 14px', cursor:'pointer', fontSize:'0.78rem', color:'#16a34a', fontWeight:700, display:'flex', alignItems:'center', gap:5 }}>
            📥 Exportar Excel ({filtered.length + cancelados.length})
          </button>
        </div>
      </div>}

      {/* Content */}
      {view==='previas' && (
        <PreviaSetorPanel pacientes={pacientes} userEmail={user?.email || ''} />
      )}
      {view==='agendas' && (
        <AgendasView agendamentos={agendamentos} profissionais={profissionais} pacientes={pacientes}
          isAdmin={isAdmin}
          onDelete={async (ag) => {
            if (!window.confirm(`Excluir agendamento de ${ag.pacienteNome} em ${ag.data}?`)) return;
            await deleteDoc(doc(db, getCollectionName('captacao_agendamentos'), ag.id));
          }}
          onConfirmar={async (ag) => {
            await updateDoc(doc(db, getCollectionName('captacao_agendamentos'), ag.id), { status:'confirmado', dataConfirmacao:serverTimestamp() });
            speakSe(speakAvaliacaoConfirmada, ag.pacienteNome, ag.profissionalNome);
            if (ag.pacienteId) {
              const entry = { status:'Avaliação Concluída', timestamp:new Date().toISOString(), responsavel:ag.profissionalNome, dados:{ confirmadoPor:ag.profissionalNome } };
              await updateDoc(doc(db, getCollectionName('captacao_pacientes'), ag.pacienteId), { status:'Avaliação Concluída', historico:arrayUnion(entry), dataAtualizacao:serverTimestamp() });
              speakSe(speakStatusChange, ag.pacienteNome, 'Avaliação Concluída');
            }
          }}
        />
      )}
      {view==='cancelados' ? (() => {
        const operadorasCancel = [...new Set(cancelados.map(p => p.convenio || p.operadora || '').filter(Boolean))].sort();
        const filtrados = cancelados.filter(p => {
          const nomeOk = !filtroCancelNome || (p.nome || '').toLowerCase().includes(filtroCancelNome.toLowerCase());
          const opOk   = !filtroCancelOperadora || (p.convenio || p.operadora || '') === filtroCancelOperadora;
          const em     = p.canceladoEm ? p.canceladoEm.slice(0, 10) : '';
          const deOk   = !filtroCancelDe  || em >= filtroCancelDe;
          const ateOk  = !filtroCancelAte || em <= filtroCancelAte;
          return nomeOk && opOk && deOk && ateOk;
        });
        return (
          <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden' }}>
            {/* Filtros cancelados */}
            <div style={{ display:'flex', gap:10, padding:'12px 16px', background:'#fef2f2', borderBottom:`1px solid #fecaca`, flexWrap:'wrap', alignItems:'center' }}>
              <span style={{ fontSize:'0.7rem', fontWeight:700, color:'#dc2626', textTransform:'uppercase', letterSpacing:'0.06em' }}>🚫 Cancelados pela Operadora</span>
              <div style={{ width:1, height:20, background:'#fecaca' }} />
              <input placeholder="Buscar paciente..." value={filtroCancelNome} onChange={e=>setFiltroCancelNome(e.target.value)}
                style={{ border:'1px solid #fecaca', borderRadius:7, padding:'5px 10px', fontSize:'0.8rem', outline:'none', minWidth:180 }} />
              <select value={filtroCancelOperadora} onChange={e=>setFiltroCancelOperadora(e.target.value)}
                style={{ border:'1px solid #fecaca', borderRadius:7, padding:'5px 10px', fontSize:'0.8rem', outline:'none' }}>
                <option value="">Todas operadoras</option>
                {operadorasCancel.map(op=><option key={op} value={op}>{op}</option>)}
              </select>
              <input type="date" value={filtroCancelDe} onChange={e=>setFiltroCancelDe(e.target.value)}
                style={{ border:'1px solid #fecaca', borderRadius:7, padding:'5px 8px', fontSize:'0.8rem', outline:'none' }}
                title="De (data cancelamento)" />
              <span style={{ fontSize:'0.75rem', color:'#ef4444' }}>até</span>
              <input type="date" value={filtroCancelAte} onChange={e=>setFiltroCancelAte(e.target.value)}
                style={{ border:'1px solid #fecaca', borderRadius:7, padding:'5px 8px', fontSize:'0.8rem', outline:'none' }}
                title="Até (data cancelamento)" />
              {(filtroCancelNome||filtroCancelOperadora||filtroCancelDe||filtroCancelAte) && (
                <button onClick={()=>{setFiltroCancelNome('');setFiltroCancelOperadora('');setFiltroCancelDe('');setFiltroCancelAte('');}}
                  style={{ marginLeft:'auto', background:'none', border:'1px solid #fca5a5', borderRadius:7, padding:'5px 12px', cursor:'pointer', fontSize:'0.78rem', color:'#ef4444', fontWeight:600 }}>
                  ✕ Limpar
                </button>
              )}
            </div>
            {/* Header */}
            <div style={{ display:'grid', gridTemplateColumns:'80px 1fr 160px 150px 140px 1fr', background:'#fafbfc', borderBottom:`1px solid ${C.border}`, padding:'8px 16px' }}>
              {['Nº','Paciente','Convênio','Status Anterior','Data Cancelamento','Cancelado por'].map(h=>(
                <div key={h} style={{ fontSize:'0.68rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', padding:'0 6px' }}>{h}</div>
              ))}
            </div>
            {filtrados.length===0 && (
              <div style={{ padding:'48px', textAlign:'center', color:C.muted }}>
                <div style={{ fontSize:'2.5rem', marginBottom:12 }}>🚫</div>
                <p style={{ margin:0, fontWeight:600 }}>Nenhum cancelamento encontrado</p>
              </div>
            )}
            {filtrados.map((p,idx)=>{
              const dataCancel = p.canceladoEm
                ? new Date(p.canceladoEm).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })
                : '—';
              const motivo = p.justificativaCancelamento?.trim();
              return (
                <div key={p.id} style={{ borderBottom:'1px solid #f4f6f8', background:idx%2===0?'#fff':'#fff7f7' }}>
                  <div style={{ display:'grid', gridTemplateColumns:'80px 1fr 160px 150px 140px 1fr', padding:'12px 16px', alignItems:'center' }}>
                    <div style={{ padding:'0 6px', fontSize:'0.75rem', fontWeight:700, color:'#ef4444' }}>{p.numero || '—'}</div>
                    <div style={{ padding:'0 6px' }}>
                      <div style={{ fontWeight:600, fontSize:'0.85rem' }}>{p.nome}</div>
                      <div style={{ fontSize:'0.72rem', color:C.muted }}>{p.telefone || ''}</div>
                    </div>
                    <div style={{ padding:'0 6px', fontSize:'0.8rem' }}>{p.convenio || p.operadora || '—'}</div>
                    <div style={{ padding:'0 6px' }}>
                      <span style={{ fontSize:'0.72rem', fontWeight:600, background:'#fee2e2', color:'#dc2626', borderRadius:6, padding:'2px 8px' }}>{p.statusAntesCancelamento || '—'}</span>
                    </div>
                    <div style={{ padding:'0 6px', fontSize:'0.78rem', color:C.muted }}>{dataCancel}</div>
                    <div style={{ padding:'0 6px', fontSize:'0.78rem', color:C.muted }}>{p.canceladoPor || '—'}</div>
                  </div>
                  {motivo && (
                    <div style={{ margin:'0 16px 10px 16px', padding:'7px 12px', background:'#fef2f2', borderRadius:7, border:'1px solid #fecaca', display:'flex', gap:8, alignItems:'flex-start' }}>
                      <span style={{ fontSize:'0.67rem', fontWeight:700, color:'#dc2626', textTransform:'uppercase', whiteSpace:'nowrap', marginTop:1 }}>Motivo:</span>
                      <span style={{ fontSize:'0.8rem', color:'#374151', lineHeight:1.5 }}>{motivo}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })() : view==='kanban' ? (
        <KanbanBoard pacientes={filtered} onCardClick={abrirPaciente} isAdmin={isAdmin} onDelete={handleDeletePaciente} stats={fullscreen ? stats : null} user={user} />
      ) : view==='adm' && (
        <div style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'80px 1fr 160px 130px 150px 170px 110px', background:'#fafbfc', borderBottom:`1px solid ${C.border}`, padding:'8px 16px' }}>
            {['Nº IW','Paciente','Convênio','Filial / Tipo','Status','SLA','Ações'].map(h=>(
              <div key={h} style={{ fontSize:'0.68rem', fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', padding:'0 6px' }}>{h}</div>
            ))}
          </div>

          {listaCarregando && (
            <div style={{ padding:'48px', textAlign:'center', color:C.muted }}>Carregando página...</div>
          )}

          {!listaCarregando && pacientesPagina.length===0 && (
            <div style={{ padding:'48px', textAlign:'center', color:C.muted }}>
              <div style={{ fontSize:'2.5rem', marginBottom:12 }}>📋</div>
              <p style={{ margin:0, fontWeight:600 }}>Nenhum paciente encontrado</p>
              <p style={{ margin:'4px 0 0', fontSize:'0.82rem' }}>Clique em "+ Novo Paciente" para começar</p>
            </div>
          )}

          {!listaCarregando && pacientesPagina.map((p,idx)=>{
            const sla  = getSLAInfo(p.dataSolicitacao || p.dataAbertura, getSLAHours(p));
            const next = nextStatus(p.status);
            const prev = prevStatus(p.status);
            const nCfg = STATUSES.find(s=>s.key===next);
            return (
              <div key={p.id}
                style={{ display:'grid', gridTemplateColumns:'80px 1fr 160px 130px 150px 170px 110px', padding:'12px 16px', borderBottom:'1px solid #f4f6f8', background:idx%2===0?'#fff':'#fafbfc', alignItems:'center', transition:'background 0.1s' }}
                onMouseEnter={e=>e.currentTarget.style.background='#f0f4ff'}
                onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?'#fff':'#fafbfc'}>
                <div style={{ padding:'0 6px' }}>
                  <div style={{ fontSize:'0.8rem', fontWeight:800, color:C.primary }}>{p.numIW || '—'}</div>
                  <div style={{ fontSize:'0.62rem', color:C.muted, marginTop:1 }}>{p.numero}</div>
                </div>
                <div style={{ padding:'0 6px' }}>
                  <div style={{ fontWeight:600, fontSize:'0.88rem', color:C.text, marginBottom:2 }}>{p.nome}</div>
                  <div style={{ fontSize:'0.72rem', color:C.muted }}>{p.hospital}{p.regiaoHospital?` — ${p.regiaoHospital}`:''}</div>
                  {p.dataImplantacaoPrevista && (
                    <div style={{ fontSize:'0.7rem', color:'#0891b2', fontWeight:700, marginTop:3 }}>
                      📅 {p.dataImplantacaoPrevista.split('-').reverse().join('/')}{p.horaImplantacaoPrevista ? ` às ${p.horaImplantacaoPrevista}` : ''}
                    </div>
                  )}
                </div>
                <div style={{ padding:'0 6px', fontSize:'0.8rem', color:C.muted }}>{p.convenio}</div>
                <div style={{ padding:'0 6px' }}><TipoBadge paciente={p} /></div>
                <div style={{ padding:'0 6px' }}><StatusBadge status={p.status} /></div>
                <div style={{ padding:'0 6px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ height:5, borderRadius:3, background:'#e2e8f0', marginBottom:3 }}>
                        <div style={{ height:'100%', width:`${sla.pct}%`, borderRadius:3, background:sla.color }} />
                      </div>
                      <span style={{ fontSize:'0.65rem', color:sla.color, fontWeight:600 }}>{sla.label}</span>
                    </div>
                    <span style={{ fontSize:'0.75rem' }}>{sla.status==='ok'?'🟢':sla.status==='warning'?'🟡':'🔴'}</span>
                  </div>
                </div>
                <div style={{ padding:'0 6px', display:'flex', flexDirection:'column', gap:4 }}>
                  <button onClick={()=>abrirPaciente(p)}
                    style={{ background:'#f1f5f9', border:'none', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'0.72rem', fontWeight:600, color:C.muted }}>📋 Ver</button>
                  <div style={{ display:'flex', gap:6 }}>
                    {prev && (
                      <button onClick={()=>setTransition({paciente:p,targetStatus:prev})}
                        style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:6, padding:'4px 6px', cursor:'pointer', color:C.muted, fontSize:'0.75rem', fontWeight:700 }} title={`Voltar para ${STATUSES.find(s=>s.key===prev)?.short}`}>
                        ⏪
                      </button>
                    )}
                    {next && (
                      <button onClick={()=>setTransition({paciente:p,targetStatus:next})}
                        style={{ background:`${nCfg?.color||C.primary}15`, border:`1px solid ${nCfg?.color||C.primary}40`, borderRadius:6, padding:'4px 8px', cursor:'pointer', fontSize:'0.68rem', fontWeight:700, color:nCfg?.color||C.primary, whiteSpace:'nowrap', flex:1 }}>
                        {nCfg?.icon} Avançar
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={()=>handleDeletePaciente(p.id, p.nome)}
                        style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:6, padding:'4px 8px', cursor:'pointer', fontSize:'0.68rem', fontWeight:700, color:'#ef4444', whiteSpace:'nowrap' }} title="Excluir">
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {listaTotal > 0 && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:16, padding:'10px 16px', borderTop:`1px solid ${C.border}`, background:'#fafbfc' }}>
              {filtroSLA && (
                <span style={{ fontSize:'0.72rem', color:'#d97706', fontWeight:600 }}>⚠️ Filtro de SLA aplicado só nesta página</span>
              )}
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:'0.78rem', color:C.muted, fontWeight:600 }}>Linhas por página:</span>
                <select value={pageSize} onChange={e=>setPageSize(Number(e.target.value))}
                  style={{ border:`1px solid ${C.border}`, borderRadius:6, padding:'4px 8px', fontSize:'0.8rem', outline:'none', cursor:'pointer', fontWeight:700, color:C.text }}>
                  {[25,50,100].map(n=><option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <span style={{ fontSize:'0.78rem', color:C.muted }}>
                {inicioPagina+1}–{Math.min(inicioPagina+pageSize, listaTotal)} de {listaTotal}
              </span>
              <div style={{ display:'flex', gap:4 }}>
                <button onClick={()=>setPaginaAtual(p=>Math.max(1,p-1))} disabled={paginaSegura<=1}
                  style={{ border:`1px solid ${C.border}`, background:'#fff', borderRadius:6, width:28, height:28, cursor:paginaSegura<=1?'default':'pointer', color:paginaSegura<=1?'#cbd5e1':C.text, fontSize:'0.9rem' }}>
                  ‹
                </button>
                <button onClick={()=>setPaginaAtual(p=>Math.min(totalPaginas,p+1))} disabled={paginaSegura>=totalPaginas}
                  style={{ border:`1px solid ${C.border}`, background:'#fff', borderRadius:6, width:28, height:28, cursor:paginaSegura>=totalPaginas?'default':'pointer', color:paginaSegura>=totalPaginas?'#cbd5e1':C.text, fontSize:'0.9rem' }}>
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showNovo && <NovoPacienteModal onClose={()=>setShowNovo(false)} onSave={handleSaveNovo} convenios={convenios} />}
      {selectedPaciente && <PatientTimeline paciente={selectedPaciente} profissionais={profissionais} agendamentos={agendamentos} onClose={()=>setSelected(null)} onAdvance={ts=>{setTransition({paciente:selectedPaciente,targetStatus:ts});}} onEdit={()=>{ setEditando(selectedPaciente); setSelected(null); }} onEditHistorico={selectedPaciente._completo ? entry=>setEditingHistorico({paciente:selectedPaciente,entry}) : undefined} onCancelar={()=>handleCancelarPelaOperadora(selectedPaciente)} />}
      {editandoPaciente && <NovoPacienteModal onClose={()=>setEditando(null)} onSave={handleSaveNovo} onUpdate={handleUpdatePaciente} convenios={convenios} initialData={editandoPaciente} />}
      {cancelModal && <CancelModal paciente={cancelModal.paciente} onClose={() => setCancelModal(null)} onConfirm={handleConfirmarCancelamento} />}
      {transitionTarget && <TransitionModal
        paciente={transitionTarget.paciente}
        targetStatus={transitionTarget.targetStatus}
        profissionais={profissionais}
        agendamentos={agendamentos}
        dadosPADSalvo={pacientes.find(p => p.id === transitionTarget.paciente?.id)?.dadosPAD}
        areaSubitems={areaSubitems}
        freqOptions={freqOptions}
        onClose={()=>setTransition(null)}
        onConfirm={handleAdvanceStatus}
      />}
      {editingHistorico && <TransitionModal
        paciente={editingHistorico.paciente}
        targetStatus={editingHistorico.entry.status}
        initialData={editingHistorico.entry.dados}
        profissionais={profissionais}
        agendamentos={agendamentos}
        areaSubitems={areaSubitems}
        freqOptions={freqOptions}
        onClose={()=>setEditingHistorico(null)}
        onConfirm={formData => handleEditHistorico(editingHistorico.paciente, editingHistorico.entry, formData)}
      />}
    </div>
  );
}
